/**
 * Interactive mode for the coding agent.
 * Handles TUI rendering and user interaction, delegating business logic to AgentSession.
 */

import * as crypto from "node:crypto";
import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import type { AgentMessage } from "@ashx-j/lunr-agent";
import type { AuthEvent, AuthPrompt, Credential } from "@ashx-j/lunr-ai";
import {
	type AssistantMessage,
	getSupportedThinkingLevels,
	type ImageContent,
	type Message,
	type Model,
} from "@ashx-j/lunr-ai/compat";
import type {
	AutocompleteItem,
	AutocompleteProvider,
	EditorComponent,
	Keybinding,
	KeyId,
	MarkdownTheme,
	OverlayHandle,
	OverlayOptions,
	SlashCommand,
} from "@ashx-j/lunr-tui";
import {
	collectImageMarkerIds,
	CombinedAutocompleteProvider,
	type Component,
	Container,
	type EditorImageAttachment,
	formatImageMarker,
	fuzzyFilter,
	isFocusable,
	Markdown,
	matchesKey,
	ProcessTerminal,
	Spacer,
	setKeybindings,
	Text,
	TruncatedText,
	TUI,
	visibleWidth,
} from "@ashx-j/lunr-tui";
import chalk from "chalk";
import { spawn, spawnSync } from "child_process";
import {
	APP_NAME,
	APP_TITLE,
	appendDebugLog,
	CONFIG_DIR_NAME,
	getAgentDir,
	getAuthPath,
	getDebugLogPath,
	getDocsPath,
	getShareViewerUrl,
	VERSION,
} from "../../config.ts";
import { type AgentSession, type AgentSessionEvent, parseSkillBlock } from "../../core/agent-session.ts";
import { type AgentSessionRuntime, SessionImportFileNotFoundError } from "../../core/agent-session-runtime.ts";
import {
	CACHE_TTL_MS,
	type CacheMiss,
	collectCacheMisses,
	computeCacheWaste,
	detectCacheMiss,
} from "../../core/cache-stats.ts";
import { formatCatalogRefreshSummary } from "../../core/catalog-merge.ts";
import { computeContextBreakdown } from "../../core/context-breakdown.ts";
import type {
	AutocompleteProviderFactory,
	EditorFactory,
	ExtensionCommandContext,
	ExtensionContext,
	ExtensionRunner,
	ExtensionUIContext,
	ExtensionUIDialogOptions,
	ExtensionWidgetOptions,
	InlineExtension,
	ProjectTrustContext,
	WorkingIndicatorOptions,
} from "../../core/extensions/index.ts";
import { FooterDataProvider, type ReadonlyFooterDataProvider } from "../../core/footer-data-provider.ts";
import { hasGrokCliXaiAuth } from "../../core/grok-cli-auth.ts";
import { configureHttpDispatcher, formatHttpIdleTimeoutMs } from "../../core/http-dispatcher.ts";
import { detectInjectedPrompt } from "../../core/injected-prompt.ts";
import { type AppKeybinding, KeybindingsManager } from "../../core/keybindings.ts";
import {
	applyIncompleteAnswers,
	describeIncomplete,
	INCOMPLETE_PROMPT_CAP,
	type IncompleteLiveModel,
	parsePositiveInt,
} from "../../core/live-catalog.ts";
import { createCompactionSummaryMessage } from "../../core/messages.ts";
import {
	isValidProviderBaseUrl,
	parseModelIds,
	slugifyProviderId,
	upsertCustomProvider,
} from "../../core/model-config-writer.ts";
import { defaultModelPerProvider, findExactModelReferenceMatch, resolveModelScope } from "../../core/model-resolver.ts";
import { refreshModelCandidatesForInit } from "../../core/model-runtime.ts";
import { getModelTiersBridge } from "../../core/model-tiers.ts";
import { registerPermissionModeBridge } from "../../core/permission-mode.ts";
import { nextModeForGoal, registerPermissionModeControlBridge } from "../../core/permission-mode-control.ts";
import {
	type ApprovalResponse,
	AUTO_MODE_ADDENDUM,
	getPermissionMode,
	nextPermissionMode,
	type PermissionMode,
	registerApprovalHandler,
	resetPermissions,
	restorePermissionModeAfterPlan,
	setPermissionMode,
} from "../../core/permissions.ts";
import { PLAN_MODE_ADDENDUM } from "../../core/plan-mode.ts";
import * as processRegistry from "../../core/process-registry.ts";
import type { ResourceDiagnostic } from "../../core/resource-loader.ts";
import {
	captureTreeChanges,
	clearRollback,
	disableRollbackForSession,
	enableRollbackForSession,
	initRollback,
	isRollbackEnabled,
	isRollbackSessionForceEnabled,
	migrateRollbackSession,
	peekRollbackTurnsConsumed,
	type RollbackResult,
	beginTurn as rollbackBeginTurn,
	rollbackLastTurn,
	setRollbackWarningHandler,
} from "../../core/rollback.ts";
import { getSearchCuratorSetting, setSearchCuratorSetting } from "../../core/search-curator.ts";
import { formatMissingSessionCwdPrompt, MissingSessionCwdError } from "../../core/session-cwd.ts";
import { type SessionEntry, SessionManager, sessionEntryToContextMessages } from "../../core/session-manager.ts";
import { BUILTIN_SLASH_COMMANDS } from "../../core/slash-commands.ts";
import type { SourceInfo } from "../../core/source-info.ts";
// lunr: multi-subscription API-key pools (stage 3 UI).
import type { SubEntry } from "../../core/subscriptions.ts";
// lunr: moved to core/swarm.ts so the gateway can reuse it.
import { buildSwarmPrompt } from "../../core/swarm.ts";
import { time } from "../../core/timings.ts";
import type { TruncationResult } from "../../core/tools/truncate.ts";
import { hasTrustRequiringProjectResources, ProjectTrustStore } from "../../core/trust-manager.ts";
import { checkForUpdate, markUpdateNotified } from "../../core/update-check.ts";
import { getAllPlanUsageResults, getUsageServiceBridge } from "../../core/usage-service.ts";
import { modelToUserEntry } from "../../core/user-models.ts";
import { copyToClipboard, readClipboardText } from "../../utils/clipboard.ts";
import { extensionForImageMimeType, readClipboardImage } from "../../utils/clipboard-image.ts";
import { parseGitUrl } from "../../utils/git.ts";
import { processImage } from "../../utils/image-process.ts";
import { getCwdRelativePath } from "../../utils/paths.ts";
import { killTrackedDetachedChildren } from "../../utils/shell.ts";
import { ensureTool, getToolPath } from "../../utils/tools-manager.ts";
import { ArminComponent } from "./components/armin.ts";
import { AssistantMessageComponent } from "./components/assistant-message.ts";
import { BashExecutionComponent } from "./components/bash-execution.ts";
import { BootScreenComponent, type BootScreenRow } from "./components/boot-screen.ts";
import { BorderedLoader } from "./components/bordered-loader.ts";
import { BranchSummaryMessageComponent } from "./components/branch-summary-message.ts";
import { CompactionSummaryMessageComponent } from "./components/compaction-summary-message.ts";
import { renderContextBox } from "./components/context-view.ts";
import { CustomEditor } from "./components/custom-editor.ts";
import { CustomEntryComponent } from "./components/custom-entry.ts";
import { CustomMessageComponent } from "./components/custom-message.ts";
import { DaxnutsComponent } from "./components/daxnuts.ts";
import { DynamicBorder } from "./components/dynamic-border.ts";
import { buildExitCard, computeExitCardStats } from "./components/exit-card.ts";
import { ExtensionEditorComponent } from "./components/extension-editor.ts";
import { ExtensionInputComponent } from "./components/extension-input.ts";
import { ExtensionSelectorComponent } from "./components/extension-selector.ts";
import { FooterComponent, formatTokens } from "./components/footer.ts";
import { InjectedPromptMessageComponent } from "./components/injected-prompt-message.ts";
import { formatKeyText, keyDisplayText, keyText } from "./components/keybinding-hints.ts";
import { LoginDialogComponent } from "./components/login-dialog.ts";
import { ModelSelectorComponent } from "./components/model-selector.ts";
import {
	type AuthSelectorProvider,
	formatAuthSelectorProviderType,
	OAuthSelectorComponent,
} from "./components/oauth-selector.ts";
import { PlanMessageComponent } from "./components/plan-message.ts";
import { ProcessesSelectorComponent } from "./components/processes-selector.ts";
import { ScopedModelsSelectorComponent } from "./components/scoped-models-selector.ts";
import { SessionSelectorComponent } from "./components/session-selector.ts";
import {
	formatSubscriptionExhaustedUntil,
	SettingsSelectorComponent,
	type SubscriptionRowInfo,
} from "./components/settings-selector.ts";
import { SkillInvocationMessageComponent } from "./components/skill-invocation-message.ts";
import {
	BranchSummaryStatusIndicator,
	CompactionStatusIndicator,
	IdleStatus,
	RetryStatusIndicator,
	type StatusIndicator,
	WorkingStatusIndicator,
} from "./components/status-indicator.ts";
import { type ThinkingRunTiming, updateThinkingRunTimings } from "./components/thinking-summary.ts";
import { applySameToolGrouping, ToolExecutionComponent } from "./components/tool-execution.ts";
import { TreeSelectorComponent } from "./components/tree-selector.ts";
import { TrustSelectorComponent } from "./components/trust-selector.ts";
import { renderUsageBox } from "./components/usage-view.ts";
import { UserMessageComponent } from "./components/user-message.ts";
import { UserMessageSelectorComponent } from "./components/user-message-selector.ts";
import { getModelSearchText } from "./model-search.ts";
import {
	computeSmoothRevealStep,
	countGraphemesBeforeToolCall,
	countMessageGraphemes,
	type SmoothStreamingOptions,
	sliceMessageContent,
} from "./smooth-streaming.ts";
import {
	getAvailableThemes,
	getAvailableThemesWithPaths,
	getEditorTheme,
	getMarkdownTheme,
	getThemeByName,
	getThemeName,
	onThemeChange,
	setRegisteredThemes,
	stopThemeWatcher,
	Theme,
	type ThemeColor,
	theme,
} from "./theme/theme.ts";
import { InteractiveThemeController } from "./theme/theme-controller.ts";

/** Interface for components that can be expanded/collapsed */
interface Expandable {
	setExpanded(expanded: boolean): void;
}

function isExpandable(obj: unknown): obj is Expandable {
	return typeof obj === "object" && obj !== null && "setExpanded" in obj && typeof obj.setExpanded === "function";
}

class ExpandableText extends Text implements Expandable {
	private readonly getCollapsedText: () => string;
	private readonly getExpandedText: () => string;

	constructor(
		getCollapsedText: () => string,
		getExpandedText: () => string,
		expanded = false,
		paddingX = 0,
		paddingY = 0,
	) {
		super(expanded ? getExpandedText() : getCollapsedText(), paddingX, paddingY);
		this.getCollapsedText = getCollapsedText;
		this.getExpandedText = getExpandedText;
	}

	setExpanded(expanded: boolean): void {
		this.setText(expanded ? this.getExpandedText() : this.getCollapsedText());
	}
}

type CompactionQueuedMessage = {
	text: string;
	mode: "steer" | "followUp";
	images?: ImageContent[];
};

type QueuedUserInput = {
	text: string;
	images?: ImageContent[];
};

type RenderSessionItem = AgentMessage | Extract<SessionEntry, { type: "custom" }>;

function isCustomSessionEntry(item: RenderSessionItem): item is Extract<SessionEntry, { type: "custom" }> {
	return "type" in item && item.type === "custom";
}

const DEAD_TERMINAL_ERROR_CODES = new Set(["EIO", "EPIPE", "ENOTCONN"]);

function isDeadTerminalError(error: unknown): boolean {
	if (!error || typeof error !== "object" || !("code" in error)) {
		return false;
	}
	const code = (error as NodeJS.ErrnoException).code;
	return code !== undefined && DEAD_TERMINAL_ERROR_CODES.has(code);
}

const ANTHROPIC_SUBSCRIPTION_AUTH_WARNING =
	"Anthropic subscription auth is active. Third-party harness usage draws from extra usage and is billed per token, not your Claude plan limits. Manage extra usage at https://claude.ai/settings/usage.";

// lunr: pre-connection ToS disclaimer for Anthropic subscription (OAuth) accounts.
const ANTHROPIC_TOS_DISCLAIMER =
	"Connecting an Anthropic subscription (Claude Pro/Max) account to lunR may violate Anthropic's Terms of Service (https://www.anthropic.com/legal/consumer-terms). Third-party harness usage also draws from paid extra usage, not your plan limits. Continue?";

const INIT_PROMPT = `Analyze this codebase and write a starter AGENTS.md in the project root.
Scan: package manifests, directory layout, build/test/lint scripts, CI config,
existing README/docs. Include only: project purpose (one paragraph), build &
test commands, code style/conventions found, directory map, and agent rules
(safety: no secrets in commits, no destructive commands without confirmation).
Keep it under 150 lines. Facts only — if something is unknown, omit it. After
writing the file, reply with a 5-line summary of what you detected.`;

const INIT_EXISTING_FILE_INSTRUCTIONS = {
	overwrite: "An AGENTS.md already exists — replace it entirely with the new content.",
	append:
		"An AGENTS.md already exists — keep the existing content and only add missing sections; do not rewrite what is already there.",
} as const;

function isAnthropicSubscriptionAuthKey(apiKey: string | undefined): boolean {
	return typeof apiKey === "string" && apiKey.startsWith("sk-ant-oat");
}

function isUnknownModel(model: Model<any> | undefined): boolean {
	return !!model && model.provider === "unknown" && model.id === "unknown" && model.api === "unknown";
}

function quoteIfNeeded(value: string): string {
	if (value.length > 0 && !/[^a-zA-Z0-9_\-./~:@]/.test(value)) {
		return value;
	}
	return `'${value.replace(/'/g, `'\\''`)}'`;
}

export function formatResumeCommand(sessionManager: SessionManager): string | undefined {
	if (!process.stdout.isTTY) return undefined;
	if (!sessionManager.isPersisted()) return undefined;

	const sessionFile = sessionManager.getSessionFile();
	if (!sessionFile || !fs.existsSync(sessionFile)) return undefined;

	const args = [APP_NAME];
	if (!sessionManager.usesDefaultSessionDir()) {
		args.push("--session-dir", quoteIfNeeded(sessionManager.getSessionDir()));
	}
	args.push("--session", sessionManager.getSessionId());
	return args.join(" ");
}

function hasDefaultModelProvider(providerId: string): providerId is keyof typeof defaultModelPerProvider {
	return providerId in defaultModelPerProvider;
}

type LoginProviderCompletionOption = {
	id: string;
	name: string;
	authTypes: AuthSelectorProvider["authType"][];
};

const AUTH_TYPE_ORDER = { oauth: 0, api_key: 1 } satisfies Record<AuthSelectorProvider["authType"], number>;

function createFuzzyAutocompleteItems<T>(
	items: T[],
	prefix: string,
	getSearchText: (item: T) => string,
	toAutocompleteItem: (item: T) => AutocompleteItem,
): AutocompleteItem[] | null {
	const filtered = fuzzyFilter(items, prefix, getSearchText);
	if (filtered.length === 0) return null;
	return filtered.map(toAutocompleteItem);
}

function getLoginProviderCompletionOptions(
	providerOptions: readonly AuthSelectorProvider[],
): LoginProviderCompletionOption[] {
	const byId = new Map<string, LoginProviderCompletionOption>();
	for (const provider of providerOptions) {
		const existing = byId.get(provider.id);
		if (existing) {
			if (!existing.authTypes.includes(provider.authType)) {
				existing.authTypes.push(provider.authType);
				existing.authTypes.sort((a, b) => AUTH_TYPE_ORDER[a] - AUTH_TYPE_ORDER[b]);
			}
			continue;
		}
		byId.set(provider.id, {
			id: provider.id,
			name: provider.name,
			authTypes: [provider.authType],
		});
	}
	return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
}

function getLoginProviderSearchText(provider: LoginProviderCompletionOption): string {
	const authTypes = provider.authTypes
		.map((authType) => `${authType} ${formatAuthSelectorProviderType(authType)}`)
		.join(" ");
	return `${provider.id} ${provider.name} ${authTypes}`;
}

function formatLoginProviderCompletionDescription(provider: LoginProviderCompletionOption): string {
	const authTypes = provider.authTypes.map(formatAuthSelectorProviderType).join("/");
	return provider.name === provider.id ? authTypes : `${provider.name} · ${authTypes}`;
}

/**
 * Options for InteractiveMode initialization.
 */
export interface InteractiveModeOptions {
	/** Providers that were migrated to auth.json (shows warning) */
	migratedProviders?: string[];
	/** Warning message if session model couldn't be restored */
	modelFallbackMessage?: string;
	/** Cwd to trust after reload if it gained a .pi directory during this implicitly trusted session. */
	autoTrustOnReloadCwd?: string;
	/** Initial message to send on startup (can include @file content) */
	initialMessage?: string;
	/** Images to attach to the initial message */
	initialImages?: ImageContent[];
	/** Additional messages to send after the initial message */
	initialMessages?: string[];
	/** Force verbose startup (overrides quietStartup setting) */
	verbose?: boolean;
	/** Extra inline factories to attach after first paint (MCP / LSP / web-access / intercom / subagents). */
	deferredBuiltinFactories?: () => Promise<InlineExtension[]>;
	/** Persist attached factories so /new and /resume recreate the full roster. */
	onDeferredBuiltinsAttached?: (factories: InlineExtension[]) => void;
}

export class InteractiveMode {
	private runtimeHost: AgentSessionRuntime;
	private ui: TUI;
	private loadedResourcesContainer: Container;
	private chatContainer: Container;
	private pendingMessagesContainer: Container;
	private statusContainer: Container;
	private defaultEditor: CustomEditor;
	private editor: EditorComponent;
	private editorComponentFactory: EditorFactory | undefined;
	private autocompleteProvider: AutocompleteProvider | undefined;
	private autocompleteProviderWrappers: AutocompleteProviderFactory[] = [];
	private fdPath: string | undefined;
	private editorContainer: Container;
	private footer: FooterComponent;
	private footerDataProvider: FooterDataProvider;
	// Stored so the same manager can be injected into custom editors, selectors, and extension UI.
	private keybindings: KeybindingsManager;
	private version: string;
	private isInitialized = false;
	private onInputCallback?: (input: QueuedUserInput) => void;
	private pendingUserInputs: QueuedUserInput[] = [];
	private stagedSubmitImages: ImageContent[] | undefined;
	private fallbackImageAttachments = new Map<number, EditorImageAttachment>();
	private activeStatusIndicator: StatusIndicator | undefined = undefined;
	private readonly idleStatus = new IdleStatus();
	private workingMessage: string | undefined = undefined;
	private workingVisible = true;
	private workingIndicatorOptions: WorkingIndicatorOptions | undefined = undefined;
	private readonly defaultWorkingMessage = "Working...";
	private readonly defaultHiddenThinkingLabel = "Thinking...";
	private hiddenThinkingLabel = this.defaultHiddenThinkingLabel;

	private lastSigintTime = 0;
	private lastEscapeTime = 0;
	private anthropicSubscriptionWarningShown = false;

	// Status line tracking (for mutating immediately-sequential status updates)
	private lastStatusSpacer: Spacer | undefined = undefined;
	private lastStatusText: Text | undefined = undefined;

	// Streaming message tracking
	private streamingComponent: AssistantMessageComponent | undefined = undefined;
	private streamingMessage: AssistantMessage | undefined = undefined;

	// Smooth streaming state (smoothStreaming setting): the streaming component
	// reveals the target message grapheme by grapheme on a timer instead of
	// flashing whole chunks.
	private streamingTargetMessage: AssistantMessage | undefined = undefined;
	private streamingDisplayedLength = 0;
	private smoothStreamingTimer: NodeJS.Timeout | undefined = undefined;

	// Tool execution tracking: toolCallId -> component
	private pendingTools = new Map<string, ToolExecutionComponent>();

	// Tool output expansion state
	private toolOutputExpanded = false;

	// Thinking block visibility state
	private hideThinkingBlock = false;
	// lunr: collapsible reasoning — collapse completed thinking runs to a summary.
	private thinkingCollapse = true;
	// lunr: live timings for the streaming message's thinking runs; kept per
	// message so rebuildChatFromMessages can re-attach them.
	private thinkingRunTimings: ThinkingRunTiming[] = [];
	private thinkingTimingsByMessage = new WeakMap<AssistantMessage, ThinkingRunTiming[]>();
	private outputPad = 1;

	// Skill commands: command name -> skill file path
	private skillCommands = new Map<string, string>();

	// Agent subscription unsubscribe function
	private unsubscribe?: () => void;
	private signalCleanupHandlers: Array<() => void> = [];

	// Track if editor is in bash mode (text starts with !)
	private isBashMode = false;

	// Track whether a /swarm orchestration turn is in flight (footer status)
	private swarmMode = false;

	// lunr: mode in effect before the current plan stretch. Used by approve / `/plan off`
	// / `/plan <text>` to leave plan. Shift+Tab and `/mode` pick the destination themselves.
	private previousPermissionMode: PermissionMode | undefined;

	// lunr: mode in effect before `/goal` forced session auto. Dedicated so it cannot
	// collide with plan restore.
	private preGoalPermissionMode: PermissionMode | undefined;

	// lunr: in-memory redo stack for /undo and /redo (leaf entry ids; cleared on any
	// new user message, never persisted — undone turns reappear after restart)
	private redoStack: string[] = [];

	// lunr: one-shot guard for the session auto-name LLM call (first assistant response)
	private autoNameTriggered = false;

	// Track current bash execution component
	private bashComponent: BashExecutionComponent | undefined = undefined;

	// Track pending bash components (shown in pending area, moved to chat on submit)
	private pendingBashComponents: BashExecutionComponent[] = [];

	// Auto-compaction state
	private autoCompactionEscapeHandler?: () => void;

	// Auto-retry state
	private retryEscapeHandler?: () => void;

	// Messages queued while compaction is running
	private compactionQueuedMessages: CompactionQueuedMessage[] = [];

	// Shutdown state
	private shutdownRequested = false;

	// Extension UI state
	private extensionSelector: ExtensionSelectorComponent | undefined = undefined;
	private extensionInput: ExtensionInputComponent | undefined = undefined;
	private extensionEditor: ExtensionEditorComponent | undefined = undefined;
	private extensionTerminalInputUnsubscribers = new Set<() => void>();

	// Extension widgets (components rendered above/below the editor)
	private extensionWidgetsAbove = new Map<string, Component & { dispose?(): void }>();
	private extensionWidgetsBelow = new Map<string, Component & { dispose?(): void }>();
	private widgetContainerAbove!: Container;
	private widgetContainerBelow!: Container;

	// Custom footer from extension (undefined = use built-in footer)
	private customFooter: (Component & { dispose?(): void }) | undefined = undefined;

	// Header container that holds the built-in or custom header
	private headerContainer: Container;

	// Built-in header (logo + keybinding hints)
	private builtInHeader: Component | undefined = undefined;

	// Custom header from extension (undefined = use built-in header)
	private customHeader: (Component & { dispose?(): void }) | undefined = undefined;

	private options: InteractiveModeOptions;
	private autoTrustOnReloadCwd: string | undefined;
	private themeController: InteractiveThemeController;
	private deferredBuiltinsAttached = false;
	private deferredBuiltinAttachPromise: Promise<void> | undefined;

	// Convenience accessors
	private get session(): AgentSession {
		return this.runtimeHost.session;
	}
	private get agent() {
		return this.session.agent;
	}
	private get sessionManager() {
		return this.session.sessionManager;
	}
	private get settingsManager() {
		return this.session.settingsManager;
	}

	constructor(runtimeHost: AgentSessionRuntime, options: InteractiveModeOptions = {}) {
		this.runtimeHost = runtimeHost;
		this.options = options;
		this.autoTrustOnReloadCwd = options.autoTrustOnReloadCwd;
		this.runtimeHost.setBeforeSessionInvalidate((reason) => {
			this.resetExtensionUI();
			// lunr: reset permission mode to configured default + clear session approvals
			this.previousPermissionMode = undefined;
			this.preGoalPermissionMode = undefined;
			resetPermissions(this.settingsManager.getDefaultPermissionMode());
			// lunr: clear process registry and rollback state
			processRegistry.clearRegistry();
			// lunr: on fork (/rollback, tree navigation) the snapshots SURVIVE —
			// the caller migrates them to the forked session id (migrateRollbackSession).
			// Any other replacement wipes only the session being torn down.
			if (reason !== "fork") {
				clearRollback(this.sessionManager.getSessionId());
			}
		});
		this.runtimeHost.setRebindSession(async () => {
			await this.rebindCurrentSession({ renderBeforeBind: true });
			// lunr: re-init rollback for the new session id + re-apply auto force-enable.
			initRollback(this.settingsManager, this.sessionManager.getSessionId());
			this.syncPermissionModeEffects(this.settingsManager.getDefaultPermissionMode());
		});
		this.version = VERSION;
		this.ui = new TUI(new ProcessTerminal(), this.settingsManager.getShowHardwareCursor());
		this.ui.setClearOnShrink(this.settingsManager.getClearOnShrink());

		// lunr: register the permission-mode footer bridge (provider-side: InteractiveMode owns the state).
		registerPermissionModeBridge(() => getPermissionMode());
		registerPermissionModeControlBridge({
			enterGoalAuto: () => this.enterGoalAuto(),
			leaveGoalAuto: () => this.leaveGoalAuto(),
		});
		// lunr: reset permission mode to configured default on startup.
		resetPermissions(this.settingsManager.getDefaultPermissionMode());
		this.headerContainer = new Container();
		this.loadedResourcesContainer = new Container();
		this.chatContainer = new Container();
		this.pendingMessagesContainer = new Container();
		this.statusContainer = new Container();
		this.widgetContainerAbove = new Container();
		this.widgetContainerBelow = new Container();
		this.keybindings = KeybindingsManager.create();
		setKeybindings(this.keybindings);
		const editorPaddingX = this.settingsManager.getEditorPaddingX();
		const autocompleteMaxVisible = this.settingsManager.getAutocompleteMaxVisible();
		this.defaultEditor = new CustomEditor(this.ui, getEditorTheme(), this.keybindings, {
			paddingX: editorPaddingX,
			autocompleteMaxVisible,
		});
		this.editor = this.defaultEditor;
		this.editorContainer = new Container();
		this.editorContainer.addChild(this.editor as Component);
		this.footerDataProvider = new FooterDataProvider(this.sessionManager.getCwd());
		this.footer = new FooterComponent(this.session, this.footerDataProvider);
		this.footer.setAutoCompactEnabled(this.session.autoCompactionEnabled);

		// Load hide thinking block setting
		this.hideThinkingBlock = this.settingsManager.getHideThinkingBlock();
		this.thinkingCollapse = this.settingsManager.getThinkingCollapse();
		this.outputPad = this.settingsManager.getOutputPad();

		// Register themes from resource loader and initialize
		setRegisteredThemes(this.session.resourceLoader.getThemes().themes);
		this.themeController = new InteractiveThemeController(
			this.ui,
			this.settingsManager,
			(message) => this.showError(message),
			() => this.updateEditorBorderColor(),
		);
	}

	private getAutocompleteSourceTag(sourceInfo?: SourceInfo): string | undefined {
		if (!sourceInfo) {
			return undefined;
		}

		// lunr: temporary scope covers baked-in extensions and ad-hoc (cli/local)
		// loads — these are part of the product, so show no provenance tag.
		if (sourceInfo.scope === "temporary") {
			return undefined;
		}

		const scopePrefix = sourceInfo.scope === "user" ? "u" : "p";
		const source = sourceInfo.source.trim();

		if (source === "auto" || source === "local" || source === "cli") {
			return scopePrefix;
		}

		if (source.startsWith("npm:")) {
			return `${scopePrefix}:${source}`;
		}

		const gitSource = parseGitUrl(source);
		if (gitSource) {
			const ref = gitSource.ref ? `@${gitSource.ref}` : "";
			return `${scopePrefix}:git:${gitSource.host}/${gitSource.path}${ref}`;
		}

		return scopePrefix;
	}

	private prefixAutocompleteDescription(description: string | undefined, sourceInfo?: SourceInfo): string | undefined {
		const sourceTag = this.getAutocompleteSourceTag(sourceInfo);
		if (!sourceTag) {
			return description;
		}
		return description ? `[${sourceTag}] ${description}` : `[${sourceTag}]`;
	}

	private getBuiltInCommandConflictDiagnostics(extensionRunner: ExtensionRunner): ResourceDiagnostic[] {
		const builtinNames = new Set(BUILTIN_SLASH_COMMANDS.map((command) => command.name));
		return extensionRunner
			.getRegisteredCommands()
			.filter((command) => builtinNames.has(command.name))
			.map((command) => ({
				type: "warning" as const,
				message:
					command.invocationName === command.name
						? `Extension command '/${command.name}' conflicts with built-in interactive command. Skipping in autocomplete.`
						: `Extension command '/${command.name}' conflicts with built-in interactive command. Available as '/${command.invocationName}'.`,
				path: command.sourceInfo.path,
			}));
	}

	private createBaseAutocompleteProvider(): AutocompleteProvider {
		// Define commands for autocomplete
		const slashCommands: SlashCommand[] = BUILTIN_SLASH_COMMANDS.map((command) => ({
			name: command.name,
			description: command.description,
			...(command.argumentHint && { argumentHint: command.argumentHint }),
		}));

		const modelCommand = slashCommands.find((command) => command.name === "model");
		if (modelCommand) {
			modelCommand.getArgumentCompletions = async (prefix: string): Promise<AutocompleteItem[] | null> => {
				// Get available models (scoped or from registry)
				const models =
					this.session.scopedModels.length > 0
						? this.session.scopedModels.map((s) => s.model)
						: await this.session.modelRuntime.getAvailable();

				if (models.length === 0) return null;

				// Create items with provider/id format
				const items = models.map((m) => ({
					id: m.id,
					provider: m.provider,
					name: m.name,
					label: `${m.provider}/${m.id}`,
				}));

				return createFuzzyAutocompleteItems(items, prefix, getModelSearchText, (item) => ({
					value: item.label,
					label: item.id,
					description: item.provider,
				}));
			};
		}

		const loginArgCompletions = (prefix: string): AutocompleteItem[] | null => {
			const providers = getLoginProviderCompletionOptions(this.getLoginProviderOptions());
			return createFuzzyAutocompleteItems(providers, prefix, getLoginProviderSearchText, (provider) => ({
				value: provider.id,
				label: provider.id,
				description: formatLoginProviderCompletionDescription(provider),
			}));
		};
		const loginCommand = slashCommands.find((command) => command.name === "login");
		if (loginCommand) {
			loginCommand.getArgumentCompletions = loginArgCompletions;
		}
		// lunr: /auth shares /login's provider argument completions.
		const authCommand = slashCommands.find((command) => command.name === "auth");
		if (authCommand) {
			authCommand.getArgumentCompletions = loginArgCompletions;
		}

		// Convert prompt templates to SlashCommand format for autocomplete
		const templateCommands: SlashCommand[] = this.session.promptTemplates.map((cmd) => ({
			name: cmd.name,
			description: this.prefixAutocompleteDescription(cmd.description, cmd.sourceInfo),
			...(cmd.argumentHint && { argumentHint: cmd.argumentHint }),
		}));

		// Convert extension commands to SlashCommand format
		const builtinCommandNames = new Set(slashCommands.map((c) => c.name));
		const extensionCommands: SlashCommand[] = this.session.extensionRunner
			.getRegisteredCommands()
			.filter((cmd) => !builtinCommandNames.has(cmd.name))
			.map((cmd) => ({
				name: cmd.invocationName,
				description: this.prefixAutocompleteDescription(cmd.description, cmd.sourceInfo),
				getArgumentCompletions: cmd.getArgumentCompletions,
			}));

		// Build skill commands from session.skills (if enabled)
		this.skillCommands.clear();
		const skillCommandList: SlashCommand[] = [];
		if (this.settingsManager.getEnableSkillCommands()) {
			for (const skill of this.session.resourceLoader.getSkills().skills) {
				const commandName = `skill:${skill.name}`;
				this.skillCommands.set(commandName, skill.filePath);
				skillCommandList.push({
					name: commandName,
					description: this.prefixAutocompleteDescription(skill.description, skill.sourceInfo),
				});
			}
		}

		return new CombinedAutocompleteProvider(
			[...slashCommands, ...templateCommands, ...extensionCommands, ...skillCommandList],
			this.sessionManager.getCwd(),
			this.fdPath,
		);
	}

	private setupAutocompleteProvider(): void {
		let provider = this.createBaseAutocompleteProvider();
		const triggerCharacters: string[] = [];
		for (const wrapProvider of this.autocompleteProviderWrappers) {
			provider = wrapProvider(provider);
			triggerCharacters.push(...(provider.triggerCharacters ?? []));
		}
		if (triggerCharacters.length > 0) {
			provider.triggerCharacters = [...new Set(triggerCharacters)];
		}

		this.autocompleteProvider = provider;
		this.defaultEditor.setAutocompleteProvider(provider);
		if (this.editor !== this.defaultEditor) {
			this.editor.setAutocompleteProvider?.(provider);
		}
	}

	async init(): Promise<void> {
		if (this.isInitialized) return;

		this.registerSignalHandlers();

		// Use already-installed fd immediately. Download in the background so a
		// missing binary or GitHub outage cannot block first paint.
		this.fdPath = getToolPath("fd") ?? undefined;
		void Promise.all([ensureTool("fd"), ensureTool("rg")]).then(([fdPath]) => {
			if (fdPath && fdPath !== this.fdPath) {
				this.fdPath = fdPath;
				this.setupAutocompleteProvider();
			}
		});
		time("ensureTools");

		if (this.session.scopedModels.length > 0 && (this.options.verbose || !this.settingsManager.getQuietStartup())) {
			const modelList = this.session.scopedModels
				.map((sm) => {
					const thinkingStr = sm.thinkingLevel ? `:${sm.thinkingLevel}` : "";
					return `${sm.model.id}${thinkingStr}`;
				})
				.join(", ");
			const cycleKeys = this.keybindings.getKeys("app.model.cycleForward");
			const cycleHint =
				cycleKeys.length > 0
					? theme.fg("muted", ` (${formatKeyText(cycleKeys.join("/"), { capitalize: true })} to cycle)`)
					: "";
			console.log(theme.fg("dim", `Model scope: ${modelList}${cycleHint}`));
		}

		// Add header container as first child. Populate it after applying theme settings.
		// Keep loaded resources before chat so restored session messages never precede them.
		this.ui.addChild(this.headerContainer);
		this.ui.addChild(this.loadedResourcesContainer);

		this.ui.addChild(this.chatContainer);
		this.ui.addChild(this.pendingMessagesContainer);
		this.ui.addChild(this.statusContainer);
		this.renderWidgets(); // Initialize with default spacer
		this.ui.addChild(this.widgetContainerAbove);
		this.ui.addChild(this.editorContainer);
		this.ui.addChild(this.widgetContainerBelow);
		this.ui.addChild(this.footer);
		this.ui.setFocus(this.editor);

		this.setupKeyHandlers();
		this.setupEditorSubmitHandler();

		this.ui.pinFrom(this.widgetContainerAbove);
		this.ui.setAlternateScreen(true);

		// Start the UI before initializing extensions so session_start handlers can use interactive dialogs
		this.ui.start();
		this.isInitialized = true;
		time("ui.start");
		void this.maybeNotifyCliUpdate();
		this.startPlanUsagePolling();

		// lunr: register the permission approval dialog handler so manual mode can prompt.
		registerApprovalHandler(async (req) => {
			// lunr: auto-activated agent swarms get their own dialog; on approval the
			// footer shows the swarm status for the rest of the turn (cleared with
			// this.swarmMode at turn end, same as an explicit /swarm).
			if (req.kind === "swarm") {
				const resp = await this.showSwarmApprovalDialog(req);
				const decision = typeof resp === "string" ? resp : resp.decision;
				if (decision !== "reject") {
					this.swarmMode = true;
					this.setExtensionStatus("swarm", "swarm");
				}
				return resp;
			}
			// lunr: plan approval (present_plan tool). Any approve leaves plan mode —
			// restore runs BEFORE resolving so the model's next tool call already
			// has full access. Decline keeps plan mode active.
			if (req.kind === "plan") {
				const resp = await this.showPlanApprovalDialog(req.detail);
				const decision = typeof resp === "string" ? resp : resp.decision;
				if (decision !== "reject") {
					this.applyPermissionMode(this.restoreTargetAfterPlan());
				}
				return resp;
			}
			return this.showApprovalDialog(req);
		});

		// lunr: initialize rollback service for this session.
		initRollback(this.settingsManager, this.sessionManager.getSessionId());
		setRollbackWarningHandler((msg) => this.showStatus(msg));
		this.syncPermissionModeEffects(this.settingsManager.getDefaultPermissionMode());

		await this.themeController.applyFromSettings();

		// Add boot screen header (unless silenced)
		if (this.options.verbose || !this.settingsManager.getQuietStartup()) {
			const header = theme.bold(theme.fg("accent", APP_NAME)) + theme.fg("dim", ` v${this.version}`);

			this.builtInHeader = new BootScreenComponent(header, this.buildBootRows());

			// Setup UI layout
			this.headerContainer.addChild(new Spacer(1));
			this.headerContainer.addChild(this.builtInHeader);
		} else {
			// Minimal header when silenced
			this.builtInHeader = new Text("", 0, 0);
			this.headerContainer.addChild(this.builtInHeader);
		}
		this.ui.requestRender();

		// Light extensions first so resources are shown before messages. Deferred
		// MCP/LSP/web/intercom attach in the background so the editor is usable.
		await this.rebindCurrentSession();
		time("rebindCurrentSession");
		this.renderInitialMessages();
		this.deferredBuiltinAttachPromise = this.attachDeferredBuiltinExtensions();
		void this.deferredBuiltinAttachPromise.then(() => {
			time("attachDeferredBuiltins");
		});

		// Set up theme file watcher
		onThemeChange(() => {
			this.ui.invalidate();
			this.updateEditorBorderColor();
			this.ui.requestRender();
		});

		// Set up git branch watcher (uses provider instead of footer)
		this.footerDataProvider.onBranchChange(() => {
			this.ui.requestRender();
		});

		// Cache-only footer snapshot. Do not hold time-to-type on it.
		void this.updateAvailableProviderCount().then(() => {
			time("updateAvailableProviderCount");
		});
	}

	/** Wait until deferred builtins have attached (or were skipped). */
	async waitForDeferredBuiltins(): Promise<void> {
		if (this.deferredBuiltinAttachPromise) {
			await this.deferredBuiltinAttachPromise;
		}
	}

	private planUsageTimer: ReturnType<typeof setInterval> | undefined;

	private startPlanUsagePolling(): void {
		const bridge = getUsageServiceBridge();
		if (!bridge) return;
		bridge.setOnUpdate(() => this.ui.requestRender());
		const tick = () => {
			const provider = this.session.model?.provider;
			if (provider) bridge.prefetch(provider);
		};
		tick();
		this.planUsageTimer = setInterval(tick, 60_000);
		this.planUsageTimer.unref?.();
	}

	/** Background npm version check. Never blocks first paint. Workspace installs skip. */
	private async maybeNotifyCliUpdate(): Promise<void> {
		try {
			const result = await checkForUpdate({
				currentVersion: VERSION,
				agentDir: getAgentDir(),
				offline: process.env.PI_OFFLINE === "1",
			});
			if (!result?.notice) return;
			this.showStatus(result.notice);
			markUpdateNotified(getAgentDir(), result.latest);
		} catch {
			// Version check is best-effort.
		}
	}

	/**
	 * Update terminal title with session name and cwd.
	 */
	private updateTerminalTitle(): void {
		const cwdBasename = path.basename(this.sessionManager.getCwd());
		const sessionName = this.sessionManager.getSessionName();
		if (sessionName) {
			this.ui.terminal.setTitle(`${APP_TITLE} - ${sessionName} - ${cwdBasename}`);
		} else {
			this.ui.terminal.setTitle(`${APP_TITLE} - ${cwdBasename}`);
		}
	}

	/**
	 * Run the interactive mode. This is the main entry point.
	 * Initializes the UI, shows warnings, processes initial messages, and starts the interactive loop.
	 */
	async run(): Promise<void> {
		if (!this.isInitialized) {
			await this.init();
		}

		// Check tmux keyboard setup asynchronously
		this.checkTmuxKeyboardSetup().then((warning) => {
			if (warning) {
				this.showWarning(warning);
			}
		});

		// Show startup warnings
		const { migratedProviders, modelFallbackMessage, initialMessage, initialImages, initialMessages } = this.options;

		if (migratedProviders && migratedProviders.length > 0) {
			this.showWarning(`Migrated credentials to auth.json: ${migratedProviders.join(", ")}`);
		}

		const modelsJsonError = this.session.modelRuntime.getError();
		if (modelsJsonError) {
			this.showError(`models.json error: ${modelsJsonError}`);
		}

		if (modelFallbackMessage) {
			this.showWarning(modelFallbackMessage);
		}

		void this.maybeWarnAboutAnthropicSubscriptionAuth();

		// Process initial messages
		if (initialMessage) {
			try {
				await this.promptAfterDeferredBuiltins(initialMessage, { images: initialImages });
			} catch (error: unknown) {
				const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
				this.showError(errorMessage);
			}
		}

		if (initialMessages) {
			for (const message of initialMessages) {
				try {
					await this.promptAfterDeferredBuiltins(message);
				} catch (error: unknown) {
					const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
					this.showError(errorMessage);
				}
			}
		}

		// Main interactive loop
		while (true) {
			const userInput = await this.getUserInput();
			try {
				await this.promptAfterDeferredBuiltins(userInput.text, { images: userInput.images });
			} catch (error: unknown) {
				const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
				this.showError(errorMessage);
			}
		}
	}

	private async checkTmuxKeyboardSetup(): Promise<string | undefined> {
		if (!process.env.TMUX) return undefined;

		const runTmuxShow = (option: string): Promise<string | undefined> => {
			return new Promise((resolve) => {
				const proc = spawn("tmux", ["show", "-gv", option], {
					stdio: ["ignore", "pipe", "ignore"],
				});
				let stdout = "";
				const timer = setTimeout(() => {
					proc.kill();
					resolve(undefined);
				}, 2000);

				proc.stdout?.on("data", (data) => {
					stdout += data.toString();
				});
				proc.on("error", () => {
					clearTimeout(timer);
					resolve(undefined);
				});
				proc.on("close", (code) => {
					clearTimeout(timer);
					resolve(code === 0 ? stdout.trim() : undefined);
				});
			});
		};

		const [extendedKeys, extendedKeysFormat] = await Promise.all([
			runTmuxShow("extended-keys"),
			runTmuxShow("extended-keys-format"),
		]);

		// If we couldn't query tmux (timeout, sandbox, etc.), don't warn
		if (extendedKeys === undefined) return undefined;

		if (extendedKeys !== "on" && extendedKeys !== "always") {
			return "tmux extended-keys is off. Modified Enter keys may not work. Add `set -g extended-keys on` to ~/.tmux.conf and restart tmux.";
		}

		if (extendedKeysFormat === "xterm") {
			return "tmux extended-keys-format is xterm. Pi works best with csi-u. Add `set -g extended-keys-format csi-u` to ~/.tmux.conf and restart tmux.";
		}

		return undefined;
	}

	private getMarkdownThemeWithSettings(): MarkdownTheme {
		return {
			...getMarkdownTheme(),
			codeBlockIndent: this.settingsManager.getCodeBlockIndent(),
		};
	}

	// =========================================================================
	// Extension System
	// =========================================================================

	private formatDisplayPath(p: string): string {
		const home = os.homedir();
		let result = p;

		// Replace home directory with ~
		if (result.startsWith(home)) {
			result = `~${result.slice(home.length)}`;
		}

		return result;
	}

	private formatExtensionDisplayPath(path: string): string {
		let result = this.formatDisplayPath(path);
		result = result.replace(/\/index\.ts$/, "").replace(/\/index\.js$/, "");
		return result;
	}

	private formatContextPath(p: string): string {
		const cwd = path.resolve(this.sessionManager.getCwd());
		const absolutePath = path.isAbsolute(p) ? path.resolve(p) : path.resolve(cwd, p);
		const relativePath = getCwdRelativePath(absolutePath, cwd);
		if (relativePath !== undefined) {
			return relativePath;
		}

		return this.formatDisplayPath(absolutePath);
	}

	private getStartupExpansionState(): boolean {
		return this.options.verbose || this.toolOutputExpanded;
	}

	/**
	 * Get a short path relative to the package root for display.
	 */
	private getShortPath(fullPath: string, sourceInfo?: SourceInfo): string {
		const baseDir = sourceInfo?.baseDir;
		if (baseDir && this.isPackageSource(sourceInfo)) {
			const relativePath = path.relative(path.resolve(baseDir), path.resolve(fullPath));
			if (
				relativePath &&
				relativePath !== "." &&
				!relativePath.startsWith("..") &&
				!relativePath.startsWith(`..${path.sep}`) &&
				!path.isAbsolute(relativePath)
			) {
				return relativePath.replace(/\\/g, "/");
			}
		}

		const source = sourceInfo?.source ?? "";
		const npmMatch = fullPath.match(/node_modules\/(@?[^/]+(?:\/[^/]+)?)\/(.*)/);
		if (npmMatch && source.startsWith("npm:")) {
			return npmMatch[2];
		}

		const gitMatch = fullPath.match(/git\/[^/]+\/[^/]+\/(.*)/);
		if (gitMatch && source.startsWith("git:")) {
			return gitMatch[1];
		}

		return this.formatDisplayPath(fullPath);
	}

	private getCompactPathLabel(resourcePath: string, sourceInfo?: SourceInfo): string {
		const shortPath = this.getShortPath(resourcePath, sourceInfo);
		const normalizedPath = shortPath.replace(/\\/g, "/");
		const segments = normalizedPath.split("/").filter((segment) => segment.length > 0 && segment !== "~");
		if (segments.length > 0) {
			return segments[segments.length - 1]!;
		}
		return shortPath;
	}

	private getCompactPackageSourceLabel(sourceInfo?: SourceInfo): string {
		const source = sourceInfo?.source ?? "";
		if (source.startsWith("npm:")) {
			return source.slice("npm:".length) || source;
		}

		const gitSource = parseGitUrl(source);
		if (gitSource) {
			return gitSource.path || source;
		}

		return source;
	}

	private getCompactExtensionLabel(resourcePath: string, sourceInfo?: SourceInfo): string {
		if (!this.isPackageSource(sourceInfo)) {
			return this.getCompactPathLabel(resourcePath, sourceInfo);
		}

		const sourceLabel = this.getCompactPackageSourceLabel(sourceInfo);
		if (!sourceLabel) {
			return this.getCompactPathLabel(resourcePath, sourceInfo);
		}

		const shortPath = this.getShortPath(resourcePath, sourceInfo).replace(/\\/g, "/");
		const packagePath = shortPath.startsWith("extensions/") ? shortPath.slice("extensions/".length) : shortPath;
		const parsedPath = path.posix.parse(packagePath);

		if (parsedPath.name === "index") {
			return !parsedPath.dir || parsedPath.dir === "." ? sourceLabel : `${sourceLabel}:${parsedPath.dir}`;
		}

		return `${sourceLabel}:${packagePath}`;
	}

	private getCompactDisplayPathSegments(resourcePath: string): string[] {
		return this.formatDisplayPath(resourcePath)
			.replace(/\\/g, "/")
			.split("/")
			.filter((segment) => segment.length > 0 && segment !== "~");
	}

	private getCompactNonPackageExtensionLabel(
		resourcePath: string,
		index: number,
		allPaths: Array<{ path: string; segments: string[] }>,
	): string {
		const segments = allPaths[index]?.segments;
		if (!segments || segments.length === 0) {
			return this.getCompactPathLabel(resourcePath);
		}

		for (let segmentCount = 1; segmentCount <= segments.length; segmentCount += 1) {
			const candidate = segments.slice(-segmentCount).join("/");
			const isUnique = allPaths.every((item, itemIndex) => {
				if (itemIndex === index) {
					return true;
				}
				return item.segments.slice(-segmentCount).join("/") !== candidate;
			});

			if (isUnique) {
				return candidate;
			}
		}

		return segments.join("/");
	}

	private getCompactExtensionLabels(extensions: Array<{ path: string; sourceInfo?: SourceInfo }>): string[] {
		const nonPackageExtensions = extensions
			.map((extension) => {
				const segments = this.getCompactDisplayPathSegments(extension.path);
				const lastSegment = segments[segments.length - 1];
				if (segments.length > 1 && (lastSegment === "index.ts" || lastSegment === "index.js")) {
					segments.pop();
				}
				return {
					path: extension.path,
					sourceInfo: extension.sourceInfo,
					segments,
				};
			})
			.filter((extension) => !this.isPackageSource(extension.sourceInfo));

		return extensions.map((extension) => {
			if (this.isPackageSource(extension.sourceInfo)) {
				return this.getCompactExtensionLabel(extension.path, extension.sourceInfo);
			}

			const nonPackageIndex = nonPackageExtensions.findIndex((item) => item.path === extension.path);
			if (nonPackageIndex === -1) {
				return this.getCompactPathLabel(extension.path, extension.sourceInfo);
			}

			return this.getCompactNonPackageExtensionLabel(extension.path, nonPackageIndex, nonPackageExtensions);
		});
	}

	private getDisplaySourceInfo(sourceInfo?: SourceInfo): {
		label: string;
		scopeLabel?: string;
		color: "accent" | "muted";
	} {
		const source = sourceInfo?.source ?? "local";
		const scope = sourceInfo?.scope ?? "project";
		if (source === "local") {
			if (scope === "user") {
				return { label: "user", color: "muted" };
			}
			if (scope === "project") {
				return { label: "project", color: "muted" };
			}
			if (scope === "temporary") {
				return { label: "path", scopeLabel: "temp", color: "muted" };
			}
			return { label: "path", color: "muted" };
		}

		if (source === "cli") {
			return { label: "path", scopeLabel: scope === "temporary" ? "temp" : undefined, color: "muted" };
		}

		const scopeLabel =
			scope === "user" ? "user" : scope === "project" ? "project" : scope === "temporary" ? "temp" : undefined;
		return { label: source, scopeLabel, color: "accent" };
	}

	private getScopeGroup(sourceInfo?: SourceInfo): "user" | "project" | "path" {
		const source = sourceInfo?.source ?? "local";
		const scope = sourceInfo?.scope ?? "project";
		if (source === "cli" || scope === "temporary") return "path";
		if (scope === "user") return "user";
		if (scope === "project") return "project";
		return "path";
	}

	private isPackageSource(sourceInfo?: SourceInfo): boolean {
		const source = sourceInfo?.source ?? "";
		return source.startsWith("npm:") || source.startsWith("git:");
	}

	private buildScopeGroups(items: Array<{ path: string; sourceInfo?: SourceInfo }>): Array<{
		scope: "user" | "project" | "path";
		paths: Array<{ path: string; sourceInfo?: SourceInfo }>;
		packages: Map<string, Array<{ path: string; sourceInfo?: SourceInfo }>>;
	}> {
		const groups: Record<
			"user" | "project" | "path",
			{
				scope: "user" | "project" | "path";
				paths: Array<{ path: string; sourceInfo?: SourceInfo }>;
				packages: Map<string, Array<{ path: string; sourceInfo?: SourceInfo }>>;
			}
		> = {
			user: { scope: "user", paths: [], packages: new Map() },
			project: { scope: "project", paths: [], packages: new Map() },
			path: { scope: "path", paths: [], packages: new Map() },
		};

		for (const item of items) {
			const groupKey = this.getScopeGroup(item.sourceInfo);
			const group = groups[groupKey];
			const source = item.sourceInfo?.source ?? "local";

			if (this.isPackageSource(item.sourceInfo)) {
				const list = group.packages.get(source) ?? [];
				list.push(item);
				group.packages.set(source, list);
			} else {
				group.paths.push(item);
			}
		}

		return [groups.project, groups.user, groups.path].filter(
			(group) => group.paths.length > 0 || group.packages.size > 0,
		);
	}

	private formatScopeGroups(
		groups: Array<{
			scope: "user" | "project" | "path";
			paths: Array<{ path: string; sourceInfo?: SourceInfo }>;
			packages: Map<string, Array<{ path: string; sourceInfo?: SourceInfo }>>;
		}>,
		options: {
			formatPath: (item: { path: string; sourceInfo?: SourceInfo }) => string;
			formatPackagePath: (item: { path: string; sourceInfo?: SourceInfo }, source: string) => string;
		},
	): string {
		const lines: string[] = [];

		for (const group of groups) {
			lines.push(`  ${theme.fg("accent", group.scope)}`);

			const sortedPaths = [...group.paths].sort((a, b) => a.path.localeCompare(b.path));
			for (const item of sortedPaths) {
				lines.push(theme.fg("dim", `    ${options.formatPath(item)}`));
			}

			const sortedPackages = Array.from(group.packages.entries()).sort(([a], [b]) => a.localeCompare(b));
			for (const [source, items] of sortedPackages) {
				lines.push(`    ${theme.fg("mdLink", source)}`);
				const sortedPackagePaths = [...items].sort((a, b) => a.path.localeCompare(b.path));
				for (const item of sortedPackagePaths) {
					lines.push(theme.fg("dim", `      ${options.formatPackagePath(item, source)}`));
				}
			}
		}

		return lines.join("\n");
	}

	private findSourceInfoForPath(p: string, sourceInfos: Map<string, SourceInfo>): SourceInfo | undefined {
		const exact = sourceInfos.get(p);
		if (exact) return exact;

		let current = p;
		while (current.includes("/")) {
			current = current.substring(0, current.lastIndexOf("/"));
			const parent = sourceInfos.get(current);
			if (parent) return parent;
		}

		return undefined;
	}

	private formatPathWithSource(p: string, sourceInfo?: SourceInfo): string {
		if (sourceInfo) {
			const shortPath = this.getShortPath(p, sourceInfo);
			const { label, scopeLabel } = this.getDisplaySourceInfo(sourceInfo);
			const labelText = scopeLabel ? `${label} (${scopeLabel})` : label;
			return `${labelText} ${shortPath}`;
		}
		return this.formatDisplayPath(p);
	}

	private formatDiagnostics(diagnostics: readonly ResourceDiagnostic[], sourceInfos: Map<string, SourceInfo>): string {
		const lines: string[] = [];

		// Group collision diagnostics by name
		const collisions = new Map<string, ResourceDiagnostic[]>();
		const otherDiagnostics: ResourceDiagnostic[] = [];

		for (const d of diagnostics) {
			if (d.type === "collision" && d.collision) {
				const list = collisions.get(d.collision.name) ?? [];
				list.push(d);
				collisions.set(d.collision.name, list);
			} else {
				otherDiagnostics.push(d);
			}
		}

		// Format collision diagnostics grouped by name
		for (const [name, collisionList] of collisions) {
			const first = collisionList[0]?.collision;
			if (!first) continue;
			lines.push(theme.fg("warning", `  "${name}" collision:`));
			lines.push(
				theme.fg(
					"dim",
					`    ${theme.fg("success", "✓")} ${this.formatPathWithSource(first.winnerPath, this.findSourceInfoForPath(first.winnerPath, sourceInfos))}`,
				),
			);
			for (const d of collisionList) {
				if (d.collision) {
					lines.push(
						theme.fg(
							"dim",
							`    ${theme.fg("warning", "✗")} ${this.formatPathWithSource(d.collision.loserPath, this.findSourceInfoForPath(d.collision.loserPath, sourceInfos))} (skipped)`,
						),
					);
				}
			}
		}

		for (const d of otherDiagnostics) {
			if (d.path) {
				const formattedPath = this.formatPathWithSource(d.path, this.findSourceInfoForPath(d.path, sourceInfos));
				lines.push(theme.fg(d.type === "error" ? "error" : "warning", `  ${formattedPath}`));
				lines.push(theme.fg(d.type === "error" ? "error" : "warning", `    ${d.message}`));
			} else {
				lines.push(theme.fg(d.type === "error" ? "error" : "warning", `  ${d.message}`));
			}
		}

		return lines.join("\n");
	}

	private showLoadedResources(options?: {
		extensions?: Array<{ path: string; sourceInfo?: SourceInfo }>;
		force?: boolean;
		showDiagnosticsWhenQuiet?: boolean;
	}): void {
		// Resource rendering is idempotent; chat clears no longer clear this separate container.
		this.loadedResourcesContainer.clear();

		// lunr: startup/reload render nothing by default; the [Context]/[Skills]/etc. listing
		// only appears with --verbose (or an explicit force). Diagnostics still surface below.
		const showListing = options?.force || this.options.verbose;
		const showDiagnostics = showListing || options?.showDiagnosticsWhenQuiet === true;
		if (!showListing && !showDiagnostics) {
			return;
		}

		const sectionHeader = (name: string, color: ThemeColor = "mdHeading") => theme.fg(color, `[${name}]`);
		const formatCompactList = (items: string[], options?: { sort?: boolean }): string => {
			const labels = items.map((item) => item.trim()).filter((item) => item.length > 0);
			if (options?.sort !== false) {
				labels.sort((a, b) => a.localeCompare(b));
			}
			return theme.fg("dim", `  ${labels.join(", ")}`);
		};
		const addLoadedSection = (
			name: string,
			collapsedBody: string,
			expandedBody = collapsedBody,
			color: ThemeColor = "mdHeading",
		): void => {
			const section = new ExpandableText(
				() => `${sectionHeader(name, color)}\n${collapsedBody}`,
				() => `${sectionHeader(name, color)}\n${expandedBody}`,
				this.getStartupExpansionState(),
				0,
				0,
			);
			this.loadedResourcesContainer.addChild(section);
			this.loadedResourcesContainer.addChild(new Spacer(1));
		};

		const skillsResult = this.session.resourceLoader.getSkills();
		const promptsResult = this.session.resourceLoader.getPrompts();
		const themesResult = this.session.resourceLoader.getThemes();
		const extensions =
			options?.extensions ??
			this.session.resourceLoader
				.getExtensions()
				.extensions.filter((extension) => !extension.path.startsWith("<inline:"))
				.map((extension) => ({
					path: extension.path,
					sourceInfo: extension.sourceInfo,
				}));
		const sourceInfos = new Map<string, SourceInfo>();
		for (const extension of extensions) {
			if (extension.sourceInfo) {
				sourceInfos.set(extension.path, extension.sourceInfo);
			}
		}
		for (const skill of skillsResult.skills) {
			if (skill.sourceInfo) {
				sourceInfos.set(skill.filePath, skill.sourceInfo);
			}
		}
		for (const prompt of promptsResult.prompts) {
			if (prompt.sourceInfo) {
				sourceInfos.set(prompt.filePath, prompt.sourceInfo);
			}
		}
		for (const loadedTheme of themesResult.themes) {
			if (loadedTheme.sourcePath && loadedTheme.sourceInfo) {
				sourceInfos.set(loadedTheme.sourcePath, loadedTheme.sourceInfo);
			}
		}

		if (showListing) {
			const contextFiles = this.session.resourceLoader.getAgentsFiles().agentsFiles;
			if (contextFiles.length > 0) {
				this.loadedResourcesContainer.addChild(new Spacer(1));
				const contextList = contextFiles
					.map((f) => theme.fg("dim", `  ${this.formatDisplayPath(f.path)}`))
					.join("\n");
				const contextCompactList = formatCompactList(
					contextFiles.map((contextFile) => this.formatContextPath(contextFile.path)),
					{ sort: false },
				);
				addLoadedSection("Context", contextCompactList, contextList);
			}

			const skills = skillsResult.skills;
			if (skills.length > 0) {
				const groups = this.buildScopeGroups(
					skills.map((skill) => ({ path: skill.filePath, sourceInfo: skill.sourceInfo })),
				);
				const skillList = this.formatScopeGroups(groups, {
					formatPath: (item) => this.formatDisplayPath(item.path),
					formatPackagePath: (item) => this.getShortPath(item.path, item.sourceInfo),
				});
				const skillCompactList = formatCompactList(skills.map((skill) => skill.name));
				addLoadedSection("Skills", skillCompactList, skillList);
			}

			const templates = this.session.promptTemplates;
			if (templates.length > 0) {
				const groups = this.buildScopeGroups(
					templates.map((template) => ({ path: template.filePath, sourceInfo: template.sourceInfo })),
				);
				const templateByPath = new Map(templates.map((t) => [t.filePath, t]));
				const templateList = this.formatScopeGroups(groups, {
					formatPath: (item) => {
						const template = templateByPath.get(item.path);
						return template ? `/${template.name}` : this.formatDisplayPath(item.path);
					},
					formatPackagePath: (item) => {
						const template = templateByPath.get(item.path);
						return template ? `/${template.name}` : this.formatDisplayPath(item.path);
					},
				});
				const promptCompactList = formatCompactList(templates.map((template) => `/${template.name}`));
				addLoadedSection("Prompts", promptCompactList, templateList);
			}

			if (extensions.length > 0) {
				const groups = this.buildScopeGroups(extensions);
				const extList = this.formatScopeGroups(groups, {
					formatPath: (item) => this.formatExtensionDisplayPath(item.path),
					formatPackagePath: (item) =>
						this.formatExtensionDisplayPath(this.getShortPath(item.path, item.sourceInfo)),
				});
				const extensionCompactList = formatCompactList(this.getCompactExtensionLabels(extensions));
				addLoadedSection("Extensions", extensionCompactList, extList, "mdHeading");
			}

			// Show loaded themes (excluding built-in)
			const loadedThemes = themesResult.themes;
			const customThemes = loadedThemes.filter((t) => t.sourcePath);
			if (customThemes.length > 0) {
				const groups = this.buildScopeGroups(
					customThemes.map((loadedTheme) => ({
						path: loadedTheme.sourcePath!,
						sourceInfo: loadedTheme.sourceInfo,
					})),
				);
				const themeList = this.formatScopeGroups(groups, {
					formatPath: (item) => this.formatDisplayPath(item.path),
					formatPackagePath: (item) => this.getShortPath(item.path, item.sourceInfo),
				});
				const themeCompactList = formatCompactList(
					customThemes.map(
						(loadedTheme) =>
							loadedTheme.name ?? this.getCompactPathLabel(loadedTheme.sourcePath!, loadedTheme.sourceInfo),
					),
				);
				addLoadedSection("Themes", themeCompactList, themeList);
			}
		}

		if (showDiagnostics) {
			const skillDiagnostics = skillsResult.diagnostics;
			if (skillDiagnostics.length > 0) {
				const warningLines = this.formatDiagnostics(skillDiagnostics, sourceInfos);
				this.loadedResourcesContainer.addChild(
					new Text(`${theme.fg("warning", "[Skill conflicts]")}\n${warningLines}`, 0, 0),
				);
				this.loadedResourcesContainer.addChild(new Spacer(1));
			}

			const promptDiagnostics = promptsResult.diagnostics;
			if (promptDiagnostics.length > 0) {
				const warningLines = this.formatDiagnostics(promptDiagnostics, sourceInfos);
				this.loadedResourcesContainer.addChild(
					new Text(`${theme.fg("warning", "[Prompt conflicts]")}\n${warningLines}`, 0, 0),
				);
				this.loadedResourcesContainer.addChild(new Spacer(1));
			}

			const extensionDiagnostics: ResourceDiagnostic[] = [];
			const extensionErrors = this.session.resourceLoader.getExtensions().errors;
			if (extensionErrors.length > 0) {
				for (const error of extensionErrors) {
					extensionDiagnostics.push({ type: "error", message: error.error, path: error.path });
				}
			}

			const commandDiagnostics = this.session.extensionRunner.getCommandDiagnostics();
			extensionDiagnostics.push(...commandDiagnostics);
			extensionDiagnostics.push(...this.getBuiltInCommandConflictDiagnostics(this.session.extensionRunner));

			const shortcutDiagnostics = this.session.extensionRunner.getShortcutDiagnostics();
			extensionDiagnostics.push(...shortcutDiagnostics);

			if (extensionDiagnostics.length > 0) {
				const warningLines = this.formatDiagnostics(extensionDiagnostics, sourceInfos);
				this.loadedResourcesContainer.addChild(
					new Text(`${theme.fg("warning", "[Extension issues]")}\n${warningLines}`, 0, 0),
				);
				this.loadedResourcesContainer.addChild(new Spacer(1));
			}

			const themeDiagnostics = themesResult.diagnostics;
			if (themeDiagnostics.length > 0) {
				const warningLines = this.formatDiagnostics(themeDiagnostics, sourceInfos);
				this.loadedResourcesContainer.addChild(
					new Text(`${theme.fg("warning", "[Theme conflicts]")}\n${warningLines}`, 0, 0),
				);
				this.loadedResourcesContainer.addChild(new Spacer(1));
			}
		}
	}

	/**
	 * Initialize the extension system with TUI-based UI context.
	 */
	private async awaitDeferredBuiltinsForPrompt(): Promise<void> {
		await this.waitForDeferredBuiltins();
	}

	private async promptAfterDeferredBuiltins(
		text: string,
		options?: Parameters<AgentSession["prompt"]>[1],
	): Promise<void> {
		await this.awaitDeferredBuiltinsForPrompt();
		await this.session.prompt(text, options);
	}

	private async sendUserMessageAfterDeferredBuiltins(text: string): Promise<void> {
		await this.awaitDeferredBuiltinsForPrompt();
		await this.session.sendUserMessage(text);
	}

	private async attachDeferredBuiltinExtensions(): Promise<void> {
		if (this.deferredBuiltinsAttached || !this.options.deferredBuiltinFactories) {
			return;
		}
		this.setExtensionStatus("deferred-builtins", "loading tools…");
		try {
			const factories = await this.options.deferredBuiltinFactories();
			this.deferredBuiltinsAttached = true;
			if (factories.length === 0) return;
			this.options.onDeferredBuiltinsAttached?.(factories);
			await this.session.attachInlineExtensions(factories);
			this.setupAutocompleteProvider();
			this.setupExtensionShortcuts(this.session.extensionRunner);
			this.showLoadedResources({ force: false, showDiagnosticsWhenQuiet: true });
		} catch (error) {
			this.deferredBuiltinsAttached = false;
			const message = error instanceof Error ? error.message : String(error);
			this.showError(`Failed to load deferred extensions: ${message}`);
		} finally {
			this.setExtensionStatus("deferred-builtins", undefined);
		}
	}

	private async bindCurrentSessionExtensions(): Promise<void> {
		const uiContext = this.createExtensionUIContext();
		await this.session.bindExtensions({
			uiContext,
			mode: "tui",
			abortHandler: () => {
				this.restoreQueuedMessagesToEditor({ abort: true });
			},
			commandContextActions: {
				waitForIdle: () => this.session.waitForIdle(),
				newSession: async (options) => {
					this.clearStatusIndicator();
					try {
						return await this.runtimeHost.newSession(options);
					} catch (error: unknown) {
						return this.handleFatalRuntimeError("Failed to create session", error);
					}
				},
				fork: async (entryId, options) => {
					try {
						const result = await this.runtimeHost.fork(entryId, options);
						if (!result.cancelled) {
							this.editor.setText(result.selectedText ?? "");
							this.showStatus("Forked to new session");
						}
						return { cancelled: result.cancelled };
					} catch (error: unknown) {
						return this.handleFatalRuntimeError("Failed to fork session", error);
					}
				},
				navigateTree: async (targetId, options) => {
					const result = await this.session.navigateTree(targetId, {
						summarize: options?.summarize,
						customInstructions: options?.customInstructions,
						replaceInstructions: options?.replaceInstructions,
						label: options?.label,
					});
					if (result.cancelled) {
						return { cancelled: true };
					}

					this.chatContainer.clear();
					this.renderInitialMessages();
					if (result.editorText && !this.editor.getText().trim()) {
						this.restoreEditorFromTreeResult(result);
					}
					this.showStatus("Navigated to selected point");
					void this.flushCompactionQueue({ willRetry: false });
					return { cancelled: false };
				},
				switchSession: async (sessionPath, options) => {
					return this.handleResumeSession(sessionPath, options);
				},
				reload: async () => {
					await this.handleReloadCommand();
				},
			},
			shutdownHandler: () => {
				this.shutdownRequested = true;
				if (this.session.isIdle) {
					void this.shutdown();
				}
			},
			onError: (error) => {
				this.showExtensionError(error.extensionPath, error.error, error.stack);
			},
		});

		setRegisteredThemes(this.session.resourceLoader.getThemes().themes);
		this.setupAutocompleteProvider();

		const extensionRunner = this.session.extensionRunner;
		this.setupExtensionShortcuts(extensionRunner);
		this.showLoadedResources({ force: false, showDiagnosticsWhenQuiet: true });
	}

	private applyRuntimeSettings(): void {
		configureHttpDispatcher(this.settingsManager.getHttpIdleTimeoutMs());
		this.footer.setSession(this.session);
		this.footer.setAutoCompactEnabled(this.session.autoCompactionEnabled);
		this.footerDataProvider.setCwd(this.sessionManager.getCwd());
		this.hideThinkingBlock = this.settingsManager.getHideThinkingBlock();
		this.thinkingCollapse = this.settingsManager.getThinkingCollapse();
		this.outputPad = this.settingsManager.getOutputPad();
		this.ui.setShowHardwareCursor(this.settingsManager.getShowHardwareCursor());
		const clearOnShrink = this.settingsManager.getClearOnShrink();
		this.ui.setClearOnShrink(clearOnShrink);
		if (!clearOnShrink && !this.activeStatusIndicator) {
			this.statusContainer.clear();
		}
		const editorPaddingX = this.settingsManager.getEditorPaddingX();
		const autocompleteMaxVisible = this.settingsManager.getAutocompleteMaxVisible();
		this.defaultEditor.setPaddingX(editorPaddingX);
		this.defaultEditor.setAutocompleteMaxVisible(autocompleteMaxVisible);
		if (this.editor !== this.defaultEditor) {
			this.editor.setPaddingX?.(editorPaddingX);
			this.editor.setAutocompleteMaxVisible?.(autocompleteMaxVisible);
		}
	}

	private buildBootRows(): BootScreenRow[] {
		// lunr: no model row — the footer (ashxj-tui) already shows the current model.
		const bootRows: BootScreenRow[] = [
			{ label: "directory", value: this.sessionManager.getCwd() },
			{ label: "session", value: this.sessionManager.getSessionName() ?? this.sessionManager.getSessionId() },
			{ label: "config", value: getAgentDir() },
			{ label: "theme", value: getThemeName() },
		];
		const skillsCount = this.session.resourceLoader.getSkills().skills.length;
		if (skillsCount > 0) {
			bootRows.push({ label: "skills", value: String(skillsCount) });
		}
		return bootRows;
	}

	private async rebindCurrentSession(options: { renderBeforeBind?: boolean } = {}): Promise<void> {
		await this.waitForDeferredBuiltins();
		this.unsubscribe?.();
		this.unsubscribe = undefined;
		this.applyRuntimeSettings();
		// lunr: refresh boot-screen rows on session replacement so the session row stays current.
		if (this.builtInHeader instanceof BootScreenComponent) {
			(this.builtInHeader as BootScreenComponent).updateRows(this.buildBootRows());
		}
		if (options.renderBeforeBind) {
			this.renderCurrentSessionState();
			this.subscribeToAgent();
			await this.bindCurrentSessionExtensions();
		} else {
			await this.bindCurrentSessionExtensions();
			this.subscribeToAgent();
		}
		void this.updateAvailableProviderCount();
		this.updateEditorBorderColor();
		this.updateTerminalTitle();
	}

	private async handleFatalRuntimeError(prefix: string, error: unknown): Promise<never> {
		const message = error instanceof Error ? error.message : String(error);
		this.showError(`${prefix}: ${message}`);
		stopThemeWatcher();
		this.stop();
		process.exit(1);
	}

	private renderCurrentSessionState(): void {
		this.ui.setChatScroll(0);
		this.loadedResourcesContainer.clear();
		this.chatContainer.clear();
		this.pendingMessagesContainer.clear();
		this.compactionQueuedMessages = [];
		this.stopSmoothStreaming();
		this.streamingComponent = undefined;
		this.streamingMessage = undefined;
		this.pendingTools.clear();
		this.renderInitialMessages();
	}

	/**
	 * Get a registered tool definition by name (for custom rendering).
	 */
	private getRegisteredToolDefinition(toolName: string) {
		return this.session.getToolDefinition(toolName);
	}

	/**
	 * Set up keyboard shortcuts registered by extensions.
	 */
	private setupExtensionShortcuts(extensionRunner: ExtensionRunner): void {
		const shortcuts = extensionRunner.getShortcuts(this.keybindings.getEffectiveConfig());
		if (shortcuts.size === 0) return;

		// Create a context for shortcut handlers
		const createContext = (): ExtensionContext => ({
			ui: this.createExtensionUIContext(),
			mode: "tui",
			hasUI: true,
			cwd: this.sessionManager.getCwd(),
			sessionManager: this.sessionManager,
			modelRegistry: extensionRunner.getModelRegistry(),
			model: this.session.model,
			isIdle: () => this.session.isIdle,
			isProjectTrusted: () => this.settingsManager.isProjectTrusted(),
			signal: this.session.agent.signal,
			abort: () => {
				this.restoreQueuedMessagesToEditor({ abort: true });
			},
			hasPendingMessages: () => this.session.pendingMessageCount > 0,
			shutdown: () => {
				this.shutdownRequested = true;
			},
			getContextUsage: () => this.session.getContextUsage(),
			compact: (options) => {
				void (async () => {
					try {
						const result = await this.session.compact(options?.customInstructions);
						options?.onComplete?.(result);
					} catch (error) {
						const err = error instanceof Error ? error : new Error(String(error));
						options?.onError?.(err);
					}
				})();
			},
			getSystemPrompt: () => this.session.systemPrompt,
		});

		// Set up the extension shortcut handler on the default editor
		this.defaultEditor.onExtensionShortcut = (data: string) => {
			for (const [shortcutStr, shortcut] of shortcuts) {
				// Cast to KeyId - extension shortcuts use the same format
				if (matchesKey(data, shortcutStr as KeyId)) {
					// Run handler async, don't block input
					Promise.resolve(shortcut.handler(createContext())).catch((err) => {
						this.showError(`Shortcut handler error: ${err instanceof Error ? err.message : String(err)}`);
					});
					return true;
				}
			}
			return false;
		};
	}

	/**
	 * Set extension status text in the footer.
	 */
	private setExtensionStatus(key: string, text: string | undefined): void {
		this.footerDataProvider.setExtensionStatus(key, text);
		this.ui.requestRender();
	}

	private showStatusIndicator(indicator: StatusIndicator): void {
		this.activeStatusIndicator?.dispose();
		this.activeStatusIndicator = indicator;
		this.statusContainer.clear();
		this.statusContainer.addChild(indicator);
	}

	private clearStatusIndicator(kind?: StatusIndicator["kind"]): void {
		if (kind && this.activeStatusIndicator?.kind !== kind) {
			return;
		}
		const hadActiveStatusIndicator = this.activeStatusIndicator !== undefined;
		this.activeStatusIndicator?.dispose();
		this.activeStatusIndicator = undefined;
		this.statusContainer.clear();
		if (hadActiveStatusIndicator && this.ui.getClearOnShrink()) {
			this.statusContainer.addChild(this.idleStatus);
		}
	}

	private setWorkingVisible(visible: boolean): void {
		this.workingVisible = visible;
		if (!visible) {
			this.clearStatusIndicator("working");
			this.ui.requestRender();
			return;
		}
		if (this.session.isStreaming && this.activeStatusIndicator?.kind !== "working") {
			this.showStatusIndicator(
				new WorkingStatusIndicator(
					this.ui,
					this.workingMessage ?? this.defaultWorkingMessage,
					this.workingIndicatorOptions,
				),
			);
		}
		this.ui.requestRender();
	}

	private setWorkingIndicator(options?: WorkingIndicatorOptions): void {
		this.workingIndicatorOptions = options;
		if (this.activeStatusIndicator?.kind === "working") {
			this.activeStatusIndicator.setIndicator(options);
		}
		this.ui.requestRender();
	}

	private setHiddenThinkingLabel(label?: string): void {
		this.hiddenThinkingLabel = label ?? this.defaultHiddenThinkingLabel;
		for (const child of this.chatContainer.children) {
			if (child instanceof AssistantMessageComponent) {
				child.setHiddenThinkingLabel(this.hiddenThinkingLabel);
			}
		}
		if (this.streamingComponent) {
			this.streamingComponent.setHiddenThinkingLabel(this.hiddenThinkingLabel);
		}
		this.ui.requestRender();
	}

	/**
	 * Set an extension widget (string array or custom component).
	 */
	private setExtensionWidget(
		key: string,
		content: string[] | ((tui: TUI, thm: Theme) => Component & { dispose?(): void }) | undefined,
		options?: ExtensionWidgetOptions,
	): void {
		const placement = options?.placement ?? "aboveEditor";
		const removeExisting = (map: Map<string, Component & { dispose?(): void }>) => {
			const existing = map.get(key);
			if (existing?.dispose) existing.dispose();
			map.delete(key);
		};

		removeExisting(this.extensionWidgetsAbove);
		removeExisting(this.extensionWidgetsBelow);

		if (content === undefined) {
			this.renderWidgets();
			return;
		}

		let component: Component & { dispose?(): void };

		if (Array.isArray(content)) {
			// Wrap string array in a Container with Text components
			const container = new Container();
			for (const line of content.slice(0, InteractiveMode.MAX_WIDGET_LINES)) {
				container.addChild(new Text(line, 1, 0));
			}
			if (content.length > InteractiveMode.MAX_WIDGET_LINES) {
				container.addChild(new Text(theme.fg("muted", "... (widget truncated)"), 1, 0));
			}
			component = container;
		} else {
			// Factory function - create component
			component = content(this.ui, theme);
		}

		const targetMap = placement === "belowEditor" ? this.extensionWidgetsBelow : this.extensionWidgetsAbove;
		targetMap.set(key, component);
		this.renderWidgets();
	}

	private clearExtensionWidgets(): void {
		for (const widget of this.extensionWidgetsAbove.values()) {
			widget.dispose?.();
		}
		for (const widget of this.extensionWidgetsBelow.values()) {
			widget.dispose?.();
		}
		this.extensionWidgetsAbove.clear();
		this.extensionWidgetsBelow.clear();
		this.renderWidgets();
	}

	private resetExtensionUI(): void {
		if (this.extensionSelector) {
			this.hideExtensionSelector();
		}
		if (this.extensionInput) {
			this.hideExtensionInput();
		}
		if (this.extensionEditor) {
			this.hideExtensionEditor();
		}
		this.ui.hideOverlay();
		this.clearExtensionTerminalInputListeners();
		this.setExtensionFooter(undefined);
		this.setExtensionHeader(undefined);
		this.clearExtensionWidgets();
		this.footerDataProvider.clearExtensionStatuses();
		this.footer.invalidate();
		this.autocompleteProviderWrappers = [];
		this.setCustomEditorComponent(undefined);
		this.setupAutocompleteProvider();
		this.defaultEditor.onExtensionShortcut = undefined;
		this.updateTerminalTitle();
		this.workingMessage = undefined;
		this.workingVisible = true;
		this.setWorkingIndicator();
		if (this.activeStatusIndicator?.kind === "working") {
			this.activeStatusIndicator.setMessage(
				`${this.defaultWorkingMessage} (${keyText("app.interrupt")} to interrupt)`,
			);
		}
		this.setHiddenThinkingLabel();
	}

	// Maximum total widget lines to prevent viewport overflow
	private static readonly MAX_WIDGET_LINES = 10;

	/**
	 * Render all extension widgets to the widget container.
	 */
	private renderWidgets(): void {
		if (!this.widgetContainerAbove || !this.widgetContainerBelow) return;
		this.renderWidgetContainer(this.widgetContainerAbove, this.extensionWidgetsAbove, true, true);
		this.renderWidgetContainer(this.widgetContainerBelow, this.extensionWidgetsBelow, false, false);
		this.ui.requestRender();
	}

	private renderWidgetContainer(
		container: Container,
		widgets: Map<string, Component & { dispose?(): void }>,
		spacerWhenEmpty: boolean,
		leadingSpacer: boolean,
	): void {
		container.clear();

		if (widgets.size === 0) {
			if (spacerWhenEmpty) {
				container.addChild(new Spacer(1));
			}
			return;
		}

		if (leadingSpacer) {
			container.addChild(new Spacer(1));
		}
		for (const component of widgets.values()) {
			container.addChild(component);
		}
	}

	/**
	 * Set a custom footer component, or restore the built-in footer.
	 */
	private setExtensionFooter(
		factory:
			| ((tui: TUI, thm: Theme, footerData: ReadonlyFooterDataProvider) => Component & { dispose?(): void })
			| undefined,
	): void {
		// Dispose existing custom footer
		if (this.customFooter?.dispose) {
			this.customFooter.dispose();
		}

		// Remove current footer from UI
		if (this.customFooter) {
			this.ui.removeChild(this.customFooter);
		} else {
			this.ui.removeChild(this.footer);
		}

		if (factory) {
			// Create and add custom footer, passing the data provider
			this.customFooter = factory(this.ui, theme, this.footerDataProvider);
			this.ui.addChild(this.customFooter);
		} else {
			// Restore built-in footer
			this.customFooter = undefined;
			this.ui.addChild(this.footer);
		}

		this.ui.requestRender();
	}

	/**
	 * Set a custom header component, or restore the built-in header.
	 */
	private setExtensionHeader(factory: ((tui: TUI, thm: Theme) => Component & { dispose?(): void }) | undefined): void {
		// Header may not be initialized yet if called during early initialization
		if (!this.builtInHeader) {
			return;
		}

		// Dispose existing custom header
		if (this.customHeader?.dispose) {
			this.customHeader.dispose();
		}

		// Find the index of the current header in the header container
		const currentHeader = this.customHeader || this.builtInHeader;
		const index = this.headerContainer.children.indexOf(currentHeader);

		if (factory) {
			// Create and add custom header
			this.customHeader = factory(this.ui, theme);
			if (isExpandable(this.customHeader)) {
				this.customHeader.setExpanded(this.toolOutputExpanded);
			}
			if (index !== -1) {
				this.headerContainer.children[index] = this.customHeader;
			} else {
				// If not found (e.g. builtInHeader was never added), add at the top
				this.headerContainer.children.unshift(this.customHeader);
			}
		} else {
			// Restore built-in header
			this.customHeader = undefined;
			if (isExpandable(this.builtInHeader)) {
				this.builtInHeader.setExpanded(this.toolOutputExpanded);
			}
			if (index !== -1) {
				this.headerContainer.children[index] = this.builtInHeader;
			}
		}

		this.ui.requestRender();
	}

	private addExtensionTerminalInputListener(
		handler: (data: string) => { consume?: boolean; data?: string } | undefined,
	): () => void {
		const unsubscribe = this.ui.addInputListener(handler);
		this.extensionTerminalInputUnsubscribers.add(unsubscribe);
		return () => {
			unsubscribe();
			this.extensionTerminalInputUnsubscribers.delete(unsubscribe);
		};
	}

	private clearExtensionTerminalInputListeners(): void {
		for (const unsubscribe of this.extensionTerminalInputUnsubscribers) {
			unsubscribe();
		}
		this.extensionTerminalInputUnsubscribers.clear();
	}

	/**
	 * Create the ExtensionUIContext for extensions.
	 */
	private createProjectTrustContext(cwd: string): ProjectTrustContext {
		const ui = this.createExtensionUIContext();
		return {
			cwd,
			mode: "tui",
			hasUI: true,
			ui: {
				select: ui.select,
				confirm: ui.confirm,
				input: ui.input,
				notify: ui.notify,
			},
		};
	}

	private createExtensionUIContext(): ExtensionUIContext {
		return {
			select: (title, options, opts) => this.showExtensionSelector(title, options, opts),
			confirm: (title, message, opts) => this.showExtensionConfirm(title, message, opts),
			input: (title, placeholder, opts) => this.showExtensionInput(title, placeholder, opts),
			notify: (message, type) => this.showExtensionNotify(message, type),
			onTerminalInput: (handler) => this.addExtensionTerminalInputListener(handler),
			setStatus: (key, text) => this.setExtensionStatus(key, text),
			setWorkingMessage: (message) => {
				this.workingMessage = message;
				if (this.activeStatusIndicator?.kind === "working") {
					this.activeStatusIndicator.setMessage(message ?? this.defaultWorkingMessage);
				}
			},
			setWorkingVisible: (visible) => this.setWorkingVisible(visible),
			setWorkingIndicator: (options) => this.setWorkingIndicator(options),
			setHiddenThinkingLabel: (label) => this.setHiddenThinkingLabel(label),
			setWidget: (key, content, options) => this.setExtensionWidget(key, content, options),
			setFooter: (factory) => this.setExtensionFooter(factory),
			setHeader: (factory) => this.setExtensionHeader(factory),
			setTitle: (title) => this.ui.terminal.setTitle(title),
			custom: (factory, options) => this.showExtensionCustom(factory, options),
			pasteToEditor: (text) => this.editor.handleInput(`\x1b[200~${text}\x1b[201~`),
			setEditorText: (text) => this.editor.setText(text),
			getEditorText: () => this.editor.getExpandedText?.() ?? this.editor.getText(),
			editor: (title, prefill) => this.showExtensionEditor(title, prefill),
			addAutocompleteProvider: (factory) => {
				this.autocompleteProviderWrappers.push(factory);
				this.setupAutocompleteProvider();
			},
			setEditorComponent: (factory) => this.setCustomEditorComponent(factory),
			getEditorComponent: () => this.editorComponentFactory,
			get theme() {
				return theme;
			},
			getAllThemes: () => getAvailableThemesWithPaths(),
			getTheme: (name) => getThemeByName(name),
			setTheme: (themeOrName) => {
				if (themeOrName instanceof Theme) {
					return this.themeController.setThemeInstance(themeOrName);
				}
				const result = this.themeController.setThemeName(themeOrName);
				if (result.success) {
					if (this.settingsManager.getTheme() !== themeOrName) {
						this.settingsManager.setTheme(themeOrName);
					}
				}
				return result;
			},
			getToolsExpanded: () => this.toolOutputExpanded,
			setToolsExpanded: (expanded) => this.setToolsExpanded(expanded),
		};
	}

	/**
	 * Show a selector for extensions.
	 */
	private showExtensionSelector(
		title: string,
		options: string[],
		opts?: ExtensionUIDialogOptions,
	): Promise<string | undefined> {
		return new Promise((resolve) => {
			if (opts?.signal?.aborted) {
				resolve(undefined);
				return;
			}

			const onAbort = () => {
				this.hideExtensionSelector();
				resolve(undefined);
			};
			opts?.signal?.addEventListener("abort", onAbort, { once: true });

			this.extensionSelector = new ExtensionSelectorComponent(
				title,
				options,
				(option) => {
					opts?.signal?.removeEventListener("abort", onAbort);
					this.hideExtensionSelector();
					resolve(option);
				},
				() => {
					opts?.signal?.removeEventListener("abort", onAbort);
					this.hideExtensionSelector();
					resolve(undefined);
				},
				{ tui: this.ui, timeout: opts?.timeout, onToggleToolsExpanded: () => this.toggleToolOutputExpansion() },
			);

			this.editorContainer.clear();
			this.editorContainer.addChild(this.extensionSelector);
			this.ui.setFocus(this.extensionSelector);
			this.ui.requestRender();
		});
	}

	/**
	 * Hide the extension selector.
	 */
	private hideExtensionSelector(): void {
		this.extensionSelector?.dispose();
		this.editorContainer.clear();
		this.editorContainer.addChild(this.editor);
		this.extensionSelector = undefined;
		this.ui.setFocus(this.editor);
		this.ui.requestRender();
	}

	/**
	 * Show a confirmation dialog for extensions.
	 */
	private async showExtensionConfirm(
		title: string,
		message: string,
		opts?: ExtensionUIDialogOptions,
	): Promise<boolean> {
		const result = await this.showExtensionSelector(`${title}\n${message}`, ["Yes", "No"], opts);
		return result === "Yes";
	}

	private async promptForMissingSessionCwd(error: MissingSessionCwdError): Promise<string | undefined> {
		const confirmed = await this.showExtensionConfirm(
			"Session cwd not found",
			formatMissingSessionCwdPrompt(error.issue),
		);
		return confirmed ? error.issue.fallbackCwd : undefined;
	}

	/**
	 * Show a text input for extensions.
	 */
	private showExtensionInput(
		title: string,
		placeholder?: string,
		opts?: ExtensionUIDialogOptions,
	): Promise<string | undefined> {
		return new Promise((resolve) => {
			if (opts?.signal?.aborted) {
				resolve(undefined);
				return;
			}

			const onAbort = () => {
				this.hideExtensionInput();
				resolve(undefined);
			};
			opts?.signal?.addEventListener("abort", onAbort, { once: true });

			this.extensionInput = new ExtensionInputComponent(
				title,
				placeholder,
				(value) => {
					opts?.signal?.removeEventListener("abort", onAbort);
					this.hideExtensionInput();
					resolve(value);
				},
				() => {
					opts?.signal?.removeEventListener("abort", onAbort);
					this.hideExtensionInput();
					resolve(undefined);
				},
				{ tui: this.ui, timeout: opts?.timeout },
			);

			this.editorContainer.clear();
			this.editorContainer.addChild(this.extensionInput);
			this.ui.setFocus(this.extensionInput);
			this.ui.requestRender();
		});
	}

	/**
	 * Hide the extension input.
	 */
	private hideExtensionInput(): void {
		this.extensionInput?.dispose();
		this.editorContainer.clear();
		this.editorContainer.addChild(this.editor);
		this.extensionInput = undefined;
		this.ui.setFocus(this.editor);
		this.ui.requestRender();
	}

	/**
	 * Show a multi-line editor for extensions (with Ctrl+G support).
	 */
	private showExtensionEditor(title: string, prefill?: string): Promise<string | undefined> {
		return new Promise((resolve) => {
			this.extensionEditor = new ExtensionEditorComponent(
				this.ui,
				this.keybindings,
				title,
				prefill,
				(value) => {
					this.hideExtensionEditor();
					resolve(value);
				},
				() => {
					this.hideExtensionEditor();
					resolve(undefined);
				},
				undefined,
				this.settingsManager.getExternalEditorCommand(),
			);

			this.editorContainer.clear();
			this.editorContainer.addChild(this.extensionEditor);
			this.ui.setFocus(this.extensionEditor);
			this.ui.requestRender();
		});
	}

	/**
	 * Hide the extension editor.
	 */
	private hideExtensionEditor(): void {
		this.editorContainer.clear();
		this.editorContainer.addChild(this.editor);
		this.extensionEditor = undefined;
		this.ui.setFocus(this.editor);
		this.ui.requestRender();
	}

	/**
	 * Set a custom editor component from an extension.
	 * Pass undefined to restore the default editor.
	 */
	private setCustomEditorComponent(factory: EditorFactory | undefined): void {
		this.editorComponentFactory = factory;

		// Save text from current editor before switching
		const currentText = this.editor.getText();

		this.editorContainer.clear();

		if (factory) {
			// Create the custom editor with tui, theme, and keybindings
			const newEditor = factory(this.ui, getEditorTheme(), this.keybindings);

			// Wire up callbacks from the default editor
			newEditor.onSubmit = this.defaultEditor.onSubmit;
			newEditor.onChange = this.defaultEditor.onChange;

			// Copy text from previous editor
			newEditor.setText(currentText);

			// Copy appearance settings if supported
			if (newEditor.borderColor !== undefined) {
				newEditor.borderColor = this.defaultEditor.borderColor;
			}
			if (newEditor.setPaddingX !== undefined) {
				newEditor.setPaddingX(this.defaultEditor.getPaddingX());
			}

			// Set autocomplete if supported
			if (newEditor.setAutocompleteProvider && this.autocompleteProvider) {
				newEditor.setAutocompleteProvider(this.autocompleteProvider);
			}

			// If extending CustomEditor, copy app-level handlers
			// Use duck typing since instanceof fails across jiti module boundaries
			const customEditor = newEditor as unknown as Record<string, unknown>;
			if ("actionHandlers" in customEditor && customEditor.actionHandlers instanceof Map) {
				if (!customEditor.onEscape) {
					customEditor.onEscape = () => this.defaultEditor.onEscape?.();
				}
				if (!customEditor.onCtrlD) {
					customEditor.onCtrlD = () => this.defaultEditor.onCtrlD?.();
				}
				if (!customEditor.onPasteImage) {
					customEditor.onPasteImage = () => this.defaultEditor.onPasteImage?.();
				}
				if (!customEditor.onExtensionShortcut) {
					customEditor.onExtensionShortcut = (data: string) => this.defaultEditor.onExtensionShortcut?.(data);
				}
				// Copy action handlers (clear, suspend, model switching, etc.)
				for (const [action, handler] of this.defaultEditor.actionHandlers) {
					(customEditor.actionHandlers as Map<string, () => void>).set(action, handler);
				}
			}

			this.editor = newEditor;
		} else {
			// Restore default editor with text from custom editor
			this.defaultEditor.setText(currentText);
			this.editor = this.defaultEditor;
		}

		this.editorContainer.addChild(this.editor as Component);
		this.ui.setFocus(this.editor as Component);
		this.ui.requestRender();
	}

	/**
	 * Show a notification for extensions.
	 */
	private showExtensionNotify(message: string, type?: "info" | "warning" | "error"): void {
		if (type === "error") {
			this.showError(message);
		} else if (type === "warning") {
			this.showWarning(message);
		} else {
			this.showStatus(message);
		}
	}

	/** Show a custom component with keyboard focus. Overlay mode renders on top of existing content. */
	private async showExtensionCustom<T>(
		factory: (
			tui: TUI,
			theme: Theme,
			keybindings: KeybindingsManager,
			done: (result: T) => void,
		) => (Component & { dispose?(): void }) | Promise<Component & { dispose?(): void }>,
		options?: {
			overlay?: boolean;
			overlayOptions?: OverlayOptions | (() => OverlayOptions);
			onHandle?: (handle: OverlayHandle) => void;
		},
	): Promise<T> {
		const savedText = this.editor.getText();
		const isOverlay = options?.overlay ?? false;

		const restoreEditor = () => {
			this.editorContainer.clear();
			this.editorContainer.addChild(this.editor);
			this.editor.setText(savedText);
			this.ui.setFocus(this.editor);
			this.ui.requestRender();
		};

		return new Promise((resolve, reject) => {
			let component: Component & { dispose?(): void };
			let closed = false;

			const close = (result: T) => {
				if (closed) return;
				closed = true;
				if (isOverlay) this.ui.hideOverlay();
				else restoreEditor();
				// Note: both branches above already call requestRender
				resolve(result);
				try {
					component?.dispose?.();
				} catch {
					/* ignore dispose errors */
				}
			};

			Promise.resolve(factory(this.ui, theme, this.keybindings, close))
				.then((c) => {
					if (closed) return;
					component = c;
					if (isOverlay) {
						// Resolve overlay options - can be static or dynamic function
						const resolveOptions = (): OverlayOptions | undefined => {
							if (options?.overlayOptions) {
								const opts =
									typeof options.overlayOptions === "function"
										? options.overlayOptions()
										: options.overlayOptions;
								return opts;
							}
							// Fallback: use component's width property if available
							const w = (component as { width?: number }).width;
							return w ? { width: w } : undefined;
						};
						const handle = this.ui.showOverlay(component, resolveOptions());
						// Expose handle to caller for visibility control
						options?.onHandle?.(handle);
					} else {
						this.editorContainer.clear();
						this.editorContainer.addChild(component);
						this.ui.setFocus(component);
						this.ui.requestRender();
					}
				})
				.catch((err) => {
					if (closed) return;
					if (!isOverlay) restoreEditor();
					reject(err);
				});
		});
	}

	/**
	 * Show an extension error in the UI.
	 */
	private showExtensionError(extensionPath: string, error: string, stack?: string): void {
		const errorMsg = `Extension "${extensionPath}" error: ${error}`;
		const errorText = new Text(theme.fg("error", errorMsg), 1, 0);
		this.chatContainer.addChild(errorText);
		if (stack) {
			// Show stack trace in dim color, indented
			const stackLines = stack
				.split("\n")
				.slice(1) // Skip first line (duplicates error message)
				.map((line) => theme.fg("dim", `  ${line.trim()}`))
				.join("\n");
			if (stackLines) {
				this.chatContainer.addChild(new Text(stackLines, 1, 0));
			}
		}
		this.ui.requestRender();
	}

	// =========================================================================
	// Key Handlers
	// =========================================================================

	private setupKeyHandlers(): void {
		// Set up handlers on defaultEditor - they use this.editor for text access
		// so they work correctly regardless of which editor is active
		this.defaultEditor.onEscape = () => {
			if (this.session.isStreaming) {
				this.restoreQueuedMessagesToEditor({ abort: true });
			} else if (this.session.isBashRunning) {
				this.session.abortBash();
			} else if (this.isBashMode) {
				this.editor.setText("");
				this.isBashMode = false;
				this.updateEditorBorderColor();
			} else if (!this.editor.getText().trim()) {
				// Double-escape with empty editor triggers /tree, /fork, or nothing based on setting
				const action = this.settingsManager.getDoubleEscapeAction();
				if (action !== "none") {
					const now = Date.now();
					if (now - this.lastEscapeTime < 500) {
						if (action === "tree") {
							this.showTreeSelector();
						} else {
							this.showUserMessageSelector();
						}
						this.lastEscapeTime = 0;
					} else {
						this.lastEscapeTime = now;
					}
				}
			}
		};

		// Register app action handlers
		this.defaultEditor.onAction("app.clear", () => this.handleCtrlC());
		this.defaultEditor.onCtrlD = () => this.handleCtrlD();
		this.defaultEditor.onAction("app.suspend", () => this.handleCtrlZ());
		this.defaultEditor.onAction("app.thinking.cycle", () => this.cycleThinkingLevel());
		this.defaultEditor.onAction("app.mode.cycle", () => this.cyclePermissionMode());
		this.defaultEditor.onAction("app.model.cycleForward", () => this.cycleModel("forward"));
		this.defaultEditor.onAction("app.model.cycleBackward", () => this.cycleModel("backward"));

		// Global debug handler on TUI (works regardless of focus)
		this.ui.onDebug = () => this.handleDebugCommand();
		this.defaultEditor.onAction("app.model.select", () => void this.showModelSelector());
		// lunr: tool/thinking expand is per-item click; ctrl+o is not bound (tree filter still uses ctrl+o).
		this.defaultEditor.onAction("app.thinking.toggle", () => this.toggleThinkingBlockVisibility());
		this.defaultEditor.onAction("app.editor.external", () => this.openExternalEditor());
		this.defaultEditor.onAction("app.message.copy", () => void this.handleCopyCommand());
		this.defaultEditor.onAction("app.message.followUp", () => this.handleFollowUp());
		this.defaultEditor.onAction("app.message.dequeue", () => this.handleDequeue());
		this.defaultEditor.onAction("app.session.new", () => this.handleClearCommand());
		this.defaultEditor.onAction("app.session.tree", () => this.showTreeSelector());
		this.defaultEditor.onAction("app.session.fork", () => this.showUserMessageSelector());
		this.defaultEditor.onAction("app.session.resume", () => this.showSessionSelector());

		this.defaultEditor.onChange = (text: string) => {
			const wasBashMode = this.isBashMode;
			this.isBashMode = text.trimStart().startsWith("!");
			if (wasBashMode !== this.isBashMode) {
				this.updateEditorBorderColor();
			}
		};

		// Handle clipboard paste (keybinding: app.clipboard.pasteImage, Alt+V on Windows —
		// Ctrl+V is owned by the terminal and never reaches us). Images become `[image_n]`
		// chips; otherwise, paste plain text from the system clipboard.
		this.defaultEditor.onPasteImage = () => {
			void this.handleClipboardPaste();
		};

		this.ui.addInputListener((data) => {
			if (!this.ui.isPinned()) return;
			if (this.ui.hasCapturingOverlayFocus()) return;

			const viewH = this.ui.getChatViewportHeight();
			if (this.keybindings.matches(data, "app.chat.pageUp")) {
				this.ui.scrollChat(viewH);
				return { consume: true };
			}
			if (this.keybindings.matches(data, "app.chat.pageDown")) {
				this.ui.scrollChat(-viewH);
				return { consume: true };
			}

			const editorFocused = isFocusable(this.editor) && this.editor.focused;
			const singleLine = !this.editor.getText().includes("\n");
			const showingAutocomplete =
				"isShowingAutocomplete" in this.editor &&
				typeof (this.editor as { isShowingAutocomplete?: () => boolean }).isShowingAutocomplete === "function" &&
				(this.editor as { isShowingAutocomplete: () => boolean }).isShowingAutocomplete();
			if (!editorFocused || !singleLine || showingAutocomplete) return;

			if (this.keybindings.matches(data, "tui.editor.pageUp")) {
				this.ui.scrollChat(viewH);
				return { consume: true };
			}
			if (this.keybindings.matches(data, "tui.editor.pageDown")) {
				this.ui.scrollChat(-viewH);
				return { consume: true };
			}
		});
	}

	private writeClipboardImageFile(image: { bytes: Uint8Array; mimeType: string }): { path: string; mimeType: string } {
		const ext = extensionForImageMimeType(image.mimeType) ?? "png";
		const fileName = `pi-clipboard-${crypto.randomUUID()}.${ext}`;
		const filePath = path.join(os.tmpdir(), fileName);
		fs.writeFileSync(filePath, Buffer.from(image.bytes));
		return { path: filePath, mimeType: image.mimeType };
	}

	private insertImageChip(attachment: { path: string; mimeType: string }): number {
		if (this.editor.insertImageMarker) {
			return this.editor.insertImageMarker(attachment);
		}
		const existing = collectImageMarkerIds(this.editor.getText());
		const used = new Set([...existing, ...this.fallbackImageAttachments.keys()]);
		let id = 1;
		while (used.has(id)) id++;
		this.fallbackImageAttachments.set(id, { id, path: attachment.path, mimeType: attachment.mimeType });
		this.editor.insertTextAtCursor?.(formatImageMarker(id));
		return id;
	}

	private takeSubmittedImages(submittedText?: string): EditorImageAttachment[] {
		if (this.editor.takePendingImages) {
			this.fallbackImageAttachments.clear();
			return this.editor.takePendingImages();
		}
		if (this.editor.getPendingImages) {
			this.fallbackImageAttachments.clear();
			return this.editor.getPendingImages();
		}
		const pending: EditorImageAttachment[] = [];
		for (const id of collectImageMarkerIds(submittedText ?? this.editor.getText())) {
			const attachment = this.fallbackImageAttachments.get(id);
			if (attachment) pending.push(attachment);
		}
		this.fallbackImageAttachments.clear();
		return pending;
	}

	private consumeStagedSubmitImages(): ImageContent[] | undefined {
		const staged = this.stagedSubmitImages;
		this.stagedSubmitImages = undefined;
		return staged;
	}

	private async loadImageAttachments(attachments: EditorImageAttachment[]): Promise<ImageContent[] | undefined> {
		if (attachments.length === 0) return undefined;
		const images: ImageContent[] = [];
		const autoResizeImages = this.settingsManager.getImageAutoResize();
		for (const attachment of attachments) {
			try {
				const bytes = fs.readFileSync(attachment.path);
				const processed = await processImage(bytes, attachment.mimeType, { autoResizeImages });
				if (!processed.ok) {
					this.showStatus(processed.message);
					continue;
				}
				images.push({
					type: "image",
					mimeType: processed.mimeType,
					data: processed.data,
				});
			} catch (err) {
				this.showStatus(
					`Could not attach ${formatImageMarker(attachment.id)}: ${err instanceof Error ? err.message : String(err)}`,
				);
			}
		}
		return images.length > 0 ? images : undefined;
	}

	private restoreEditorDraft(text: string, images?: ImageContent[]): void {
		this.editor.setText(text);
		if (!images || images.length === 0) return;
		const ids = collectImageMarkerIds(text);
		if (ids.length === 0 || !this.editor.restoreImageMarkers) return;
		const attachments: EditorImageAttachment[] = [];
		for (let i = 0; i < Math.min(ids.length, images.length); i++) {
			const id = ids[i]!;
			const image = images[i]!;
			const saved = this.writeClipboardImageFile({
				bytes: Buffer.from(image.data, "base64"),
				mimeType: image.mimeType,
			});
			attachments.push({ id, path: saved.path, mimeType: saved.mimeType });
		}
		this.editor.restoreImageMarkers(attachments);
	}

	private restoreEditorFromTreeResult(result: { editorText?: string; editorImages?: ImageContent[] }): void {
		this.restoreEditorDraft(result.editorText ?? "", result.editorImages);
	}

	private async handleClipboardPaste(): Promise<void> {
		try {
			const image = await readClipboardImage();
			if (image) {
				const saved = this.writeClipboardImageFile(image);
				const id = this.insertImageChip(saved);
				this.ui.requestRender();
				this.showStatus(`Pasted ${formatImageMarker(id)}`);
				return;
			}

			const text = await readClipboardText();
			if (text) {
				this.editor.insertTextAtCursor?.(text);
				this.ui.requestRender();
				return;
			}

			this.showStatus("Clipboard contains no image or text");
		} catch (err) {
			this.showStatus(`Clipboard paste failed: ${err instanceof Error ? err.message : String(err)}`);
		}
	}

	private setupEditorSubmitHandler(): void {
		this.defaultEditor.onSubmit = async (text: string) => {
			text = text.trim();
			if (!text) {
				this.takeSubmittedImages(text);
				return;
			}
			const images =
				this.consumeStagedSubmitImages() ?? (await this.loadImageAttachments(this.takeSubmittedImages(text)));
			this.ui.setChatScroll(0);

			// Handle commands
			if (text === "/settings") {
				void this.showSettingsSelector();
				this.editor.setText("");
				return;
			}
			if (text === "/scoped-models") {
				this.editor.setText("");
				await this.showModelsSelector();
				return;
			}
			if (text === "/model" || text.startsWith("/model ")) {
				const searchTerm = text.startsWith("/model ") ? text.slice(7).trim() : undefined;
				this.editor.setText("");
				await this.handleModelCommand(searchTerm);
				return;
			}
			if (text === "/refresh") {
				this.editor.setText("");
				await this.handleRefreshCommand();
				return;
			}
			if (text === "/export" || text.startsWith("/export ")) {
				await this.handleExportCommand(text);
				this.editor.setText("");
				return;
			}
			if (text === "/import" || text.startsWith("/import ")) {
				await this.handleImportCommand(text);
				this.editor.setText("");
				return;
			}
			if (text === "/share") {
				await this.handleShareCommand();
				this.editor.setText("");
				return;
			}
			if (text === "/copy") {
				await this.handleCopyCommand();
				this.editor.setText("");
				return;
			}
			if (text === "/name" || text.startsWith("/name ") || text === "/title" || text.startsWith("/title ")) {
				this.handleNameCommand(text);
				this.editor.setText("");
				return;
			}
			if (text === "/session") {
				this.handleSessionCommand();
				this.editor.setText("");
				return;
			}
			if (text === "/usage") {
				this.editor.setText("");
				await this.handleUsageCommand();
				return;
			}
			if (text === "/fast" || text.startsWith("/fast ")) {
				this.editor.setText("");
				this.handleFastCommand(text === "/fast" ? "" : text.slice(6).trim());
				return;
			}
			if (text === "/context") {
				this.editor.setText("");
				this.handleContextCommand();
				return;
			}
			if (text === "/plan" || text.startsWith("/plan ")) {
				this.editor.setText("");
				await this.handlePlanCommand(text === "/plan" ? "" : text.slice(6).trim());
				return;
			}
			if (text === "/hotkeys") {
				this.handleHotkeysCommand();
				this.editor.setText("");
				return;
			}
			if (text === "/fork") {
				this.showUserMessageSelector();
				this.editor.setText("");
				return;
			}
			if (text === "/clone") {
				this.editor.setText("");
				await this.handleCloneCommand();
				return;
			}
			if (text === "/tree") {
				this.showTreeSelector();
				this.editor.setText("");
				return;
			}
			if (text === "/undo") {
				this.editor.setText("");
				await this.handleUndoCommand();
				return;
			}
			if (text === "/edit") {
				this.editor.setText("");
				await this.handleEditCommand();
				return;
			}
			if (text === "/redo") {
				this.editor.setText("");
				await this.handleRedoCommand();
				return;
			}
			if (text === "/mode" || text.startsWith("/mode ")) {
				this.editor.setText("");
				this.handleModeCommand(text === "/mode" ? "" : text.slice(6).trim());
				return;
			}
			if (text === "/manual" || text === "/yolo" || text === "/auto") {
				this.editor.setText("");
				this.applyPermissionMode(text.slice(1) as PermissionMode);
				return;
			}
			if (text === "/processes") {
				this.editor.setText("");
				this.showProcessesSelector();
				return;
			}
			if (text === "/rollback") {
				this.editor.setText("");
				await this.handleRollbackCommand();
				return;
			}
			if (text === "/trust") {
				this.showTrustSelector();
				this.editor.setText("");
				return;
			}
			if (text === "/login" || text === "/auth" || text.startsWith("/login ") || text.startsWith("/auth ")) {
				// lunr: /auth is an alias of /login; the provider arg follows the first space.
				const providerRef = text.includes(" ") ? text.slice(text.indexOf(" ") + 1).trim() : undefined;
				this.editor.setText("");
				await this.handleLoginCommand(providerRef);
				return;
			}
			if (text === "/logout" || text === "/deauth") {
				// lunr: /deauth is an alias of /logout.
				this.showOAuthSelector("logout");
				this.editor.setText("");
				return;
			}
			if (text === "/new") {
				this.editor.setText("");
				await this.handleClearCommand();
				return;
			}
			if (text === "/compact" || text.startsWith("/compact ")) {
				const customInstructions = text.startsWith("/compact ") ? text.slice(9).trim() : undefined;
				this.editor.setText("");
				await this.handleCompactCommand(customInstructions);
				return;
			}
			if (text === "/reload") {
				this.editor.setText("");
				await this.handleReloadCommand();
				return;
			}
			if (text === "/init") {
				this.editor.setText("");
				await this.handleInitCommand();
				return;
			}
			if (text === "/swarm" || text.startsWith("/swarm ")) {
				const task = text.startsWith("/swarm ") ? text.slice(7).trim() : "";
				this.editor.setText("");
				await this.handleSwarmCommand(task);
				return;
			}
			if (text === "/debug") {
				this.handleDebugCommand();
				this.editor.setText("");
				return;
			}
			if (text === "/arminsayshi") {
				this.handleArminSaysHi();
				this.editor.setText("");
				return;
			}
			if (text === "/resume" || text === "/sessions") {
				this.showSessionSelector();
				this.editor.setText("");
				return;
			}
			if (text === "/quit" || text === "/exit") {
				// lunr: /exit is an alias of /quit.
				this.editor.setText("");
				await this.shutdown();
				return;
			}

			// Handle bash command (! for normal, !! for excluded from context)
			if (text.startsWith("!")) {
				const isExcluded = text.startsWith("!!");
				const command = isExcluded ? text.slice(2).trim() : text.slice(1).trim();
				if (command) {
					if (this.session.isBashRunning) {
						this.showWarning("A bash command is already running. Press Esc to cancel it first.");
						this.editor.setText(text);
						return;
					}
					this.editor.addToHistory?.(text);
					await this.handleBashCommand(command, isExcluded);
					this.isBashMode = false;
					this.updateEditorBorderColor();
					return;
				}
			}

			// Queue input during compaction (extension commands execute immediately)
			if (this.session.isCompacting) {
				if (this.isExtensionCommand(text)) {
					this.editor.addToHistory?.(text);
					this.editor.setText("");
					await this.promptAfterDeferredBuiltins(text);
				} else {
					this.queueCompactionMessage(text, "steer", images);
				}
				return;
			}

			// If streaming, use prompt() with steer behavior
			// This handles extension commands (execute immediately), prompt template expansion, and queueing
			if (this.session.isStreaming) {
				this.editor.addToHistory?.(text);
				this.editor.setText("");
				await this.promptAfterDeferredBuiltins(text, { streamingBehavior: "steer", images });
				this.updatePendingMessagesDisplay();
				this.ui.requestRender();
				return;
			}

			// Normal message submission
			// First, move any pending bash components to chat
			this.flushPendingBashComponents();

			if (this.onInputCallback) {
				this.onInputCallback({ text, images });
			} else {
				this.pendingUserInputs.push({ text, images });
			}
			this.editor.addToHistory?.(text);
		};
	}

	/** Smooth streaming tick rate (~30 FPS). TUI already caps paints at 16ms. */
	private static readonly SMOOTH_STREAMING_TICK_MS = 33;

	private getSmoothStreamingOptions(): SmoothStreamingOptions {
		return { hideThinking: this.hideThinkingBlock };
	}

	private clearSmoothStreamingTimer(): void {
		if (this.smoothStreamingTimer) {
			clearInterval(this.smoothStreamingTimer);
			this.smoothStreamingTimer = undefined;
		}
	}

	private startSmoothStreaming(): void {
		if (this.smoothStreamingTimer) return;
		this.smoothStreamingTimer = setInterval(() => {
			this.advanceSmoothStreaming();
		}, InteractiveMode.SMOOTH_STREAMING_TICK_MS);
		// Never keep the process alive just to finish a reveal animation.
		this.smoothStreamingTimer.unref?.();
	}

	/** Apply the current reveal slice and sync tool cards that have crossed the frontier. */
	private paintSmoothStreamingFrame(): void {
		const target = this.streamingTargetMessage;
		if (!this.streamingComponent || !target) return;
		const opts = this.getSmoothStreamingOptions();
		const revealed = sliceMessageContent(target, this.streamingDisplayedLength, opts);
		this.streamingComponent.updateContent(revealed, { thinkingSource: target });
		this.syncRevealedStreamingTools(revealed, target);
		this.ui.requestRender();
	}

	/**
	 * Create/update tool cards only once smooth streaming has revealed past their
	 * leading text. Args always come from the full target so later arg deltas apply.
	 */
	private syncRevealedStreamingTools(revealed: AssistantMessage, target: AssistantMessage): void {
		const revealedIds = new Set<string>();
		for (const block of revealed.content) {
			if (block.type === "toolCall") revealedIds.add(block.id);
		}
		for (const content of target.content) {
			if (content.type !== "toolCall" || !revealedIds.has(content.id)) continue;
			if (!this.pendingTools.has(content.id)) {
				const component = new ToolExecutionComponent(
					content.name,
					content.id,
					content.arguments,
					{
						showImages: this.settingsManager.getShowImages(),
						imageWidthCells: this.settingsManager.getImageWidthCells(),
					},
					this.getRegisteredToolDefinition(content.name),
					this.ui,
					this.sessionManager.getCwd(),
				);
				component.setExpanded(this.toolOutputExpanded);
				this.addToolComponentToChat(component);
				this.pendingTools.set(content.id, component);
			} else {
				const component = this.pendingTools.get(content.id);
				if (component) {
					component.updateArgs(content.arguments);
				}
			}
		}
	}

	private advanceSmoothStreaming(): void {
		const target = this.streamingTargetMessage;
		if (!this.streamingComponent || !target) {
			this.stopSmoothStreaming();
			return;
		}
		const opts = this.getSmoothStreamingOptions();
		const total = countMessageGraphemes(target, opts);
		if (this.streamingDisplayedLength >= total) {
			// Caught up: stop the interval until new backlog arrives.
			this.clearSmoothStreamingTimer();
			return;
		}
		const backlog = total - this.streamingDisplayedLength;
		this.streamingDisplayedLength += computeSmoothRevealStep(backlog);
		if (this.streamingDisplayedLength > total) {
			this.streamingDisplayedLength = total;
		}
		this.paintSmoothStreamingFrame();
		if (this.streamingDisplayedLength >= total) {
			this.clearSmoothStreamingTimer();
		}
	}

	private stopSmoothStreaming(): void {
		this.clearSmoothStreamingTimer();
		this.streamingTargetMessage = undefined;
		this.streamingDisplayedLength = 0;
	}

	/** Apply a mid-stream smoothStreaming setting change without rewinding. */
	private applySmoothStreamingSettingChange(enabled: boolean): void {
		this.settingsManager.setSmoothStreaming(enabled);
		if (!this.streamingComponent || !this.streamingMessage) return;

		if (enabled) {
			// Already showing the full live message; start the reveal cursor at the
			// current visible length so the next deltas only animate new text.
			this.streamingTargetMessage = this.streamingMessage;
			const opts = this.getSmoothStreamingOptions();
			this.streamingDisplayedLength = countMessageGraphemes(this.streamingMessage, opts);
			this.paintSmoothStreamingFrame();
			this.startSmoothStreaming();
			return;
		}

		// Off: stop the timer and snap to the full target immediately.
		this.clearSmoothStreamingTimer();
		this.streamingTargetMessage = this.streamingMessage;
		this.streamingComponent.updateContent(this.streamingMessage);
		this.syncRevealedStreamingTools(this.streamingMessage, this.streamingMessage);
		this.ui.requestRender();
	}

	private subscribeToAgent(): void {
		this.unsubscribe = this.session.subscribe(async (event) => {
			await this.handleEvent(event);
		});
	}

	private async handleEvent(event: AgentSessionEvent): Promise<void> {
		if (!this.isInitialized) {
			await this.init();
		}

		this.footer.invalidate();

		switch (event.type) {
			case "agent_start":
				this.pendingTools.clear();
				if (this.settingsManager.getShowTerminalProgress()) {
					this.ui.terminal.setProgress(true);
				}
				// Restore main escape handler if retry handler is still active
				// (retry success event fires later, but we need main handler now)
				if (this.retryEscapeHandler) {
					this.defaultEditor.onEscape = this.retryEscapeHandler;
					this.retryEscapeHandler = undefined;
				}
				if (this.workingVisible) {
					this.showStatusIndicator(
						new WorkingStatusIndicator(
							this.ui,
							this.workingMessage ?? this.defaultWorkingMessage,
							this.workingIndicatorOptions,
						),
					);
				} else {
					this.clearStatusIndicator();
				}
				this.ui.requestRender();
				break;

			case "queue_update":
				this.updatePendingMessagesDisplay();
				this.ui.requestRender();
				break;

			case "entry_appended":
				if (event.entry.type === "custom") {
					this.addCustomEntryToChat(event.entry);
					this.ui.requestRender();
				}
				break;

			case "session_info_changed":
				this.updateTerminalTitle();
				this.footer.invalidate();
				this.ui.requestRender();
				break;

			case "thinking_level_changed":
				this.footer.invalidate();
				this.updateEditorBorderColor();
				break;

			case "message_start":
				if (event.message.role === "custom") {
					this.addMessageToChat(event.message);
					this.ui.requestRender();
				} else if (event.message.role === "user") {
					// lunr: a new user message invalidates /redo
					this.redoStack.length = 0;
					// lunr: begin a new rollback turn (captures tree-scope baseline when enabled)
					rollbackBeginTurn(this.sessionManager.getCwd(), this.sessionManager.getSessionId());
					this.stopSmoothStreaming();
					this.addMessageToChat(event.message);
					this.updatePendingMessagesDisplay();
					this.ui.requestRender();
				} else if (event.message.role === "assistant") {
					this.streamingComponent = new AssistantMessageComponent(
						undefined,
						this.hideThinkingBlock,
						this.getMarkdownThemeWithSettings(),
						this.hiddenThinkingLabel,
						this.outputPad,
						this.settingsManager.getGutterRail(),
						this.thinkingCollapse,
					);
					// lunr: collapsible reasoning — fresh timing array for this message.
					this.thinkingRunTimings = [];
					this.streamingComponent.setThinkingTimings(this.thinkingRunTimings);
					// lunr: ctrl+o expansion — inherit the global expand state.
					this.streamingComponent.setExpanded(this.toolOutputExpanded);
					this.streamingMessage = event.message;
					this.streamingTargetMessage = event.message;
					this.streamingDisplayedLength = 0;
					this.chatContainer.addChild(this.streamingComponent);
					if (this.settingsManager.getSmoothStreaming()) {
						this.streamingComponent.updateContent(
							sliceMessageContent(this.streamingMessage, 0, this.getSmoothStreamingOptions()),
							{ thinkingSource: this.streamingMessage },
						);
					} else {
						this.streamingComponent.updateContent(this.streamingMessage);
					}
					this.ui.requestRender();
				}
				break;

			case "message_update":
				if (this.streamingComponent && event.message.role === "assistant") {
					this.streamingMessage = event.message;
					this.streamingTargetMessage = event.message;
					// lunr: collapsible reasoning — time runs against the FULL message
					// so smooth-streaming reveal lag doesn't inflate durations.
					updateThinkingRunTimings(this.thinkingRunTimings, this.streamingMessage.content, Date.now(), false);
					if (this.settingsManager.getSmoothStreaming()) {
						// Smooth streaming: timer reveals graphemes. Tool cards sync from
						// the reveal frontier so they don't jump ahead of leading text.
						const opts = this.getSmoothStreamingOptions();
						const total = countMessageGraphemes(this.streamingMessage, opts);
						if (this.streamingDisplayedLength >= total) {
							// Already caught up (or tool-only): paint so new tool cards appear.
							this.paintSmoothStreamingFrame();
							this.clearSmoothStreamingTimer();
						} else {
							this.startSmoothStreaming();
						}
					} else {
						this.streamingComponent.updateContent(this.streamingMessage);
						this.syncRevealedStreamingTools(this.streamingMessage, this.streamingMessage);
						this.ui.requestRender();
					}
				}
				break;

			case "message_end":
				if (event.message.role === "user") break;
				if (this.streamingComponent && event.message.role === "assistant") {
					// Snap remaining graphemes on end (abort/error and normal stop).
					// Avoids multi-second leftover animation; paint always follows below.
					this.stopSmoothStreaming();
					this.streamingMessage = event.message;
					let errorMessage: string | undefined;
					if (this.streamingMessage.stopReason === "aborted") {
						const retryAttempt = this.session.retryAttempt;
						errorMessage =
							retryAttempt > 0
								? `Aborted after ${retryAttempt} retry attempt${retryAttempt > 1 ? "s" : ""}`
								: "Operation aborted";
						this.streamingMessage.errorMessage = errorMessage;
					}
					// lunr: collapsible reasoning — close any open run and remember the
					// timings so rebuildChatFromMessages can re-attach them.
					updateThinkingRunTimings(this.thinkingRunTimings, this.streamingMessage.content, Date.now(), true);
					this.thinkingTimingsByMessage.set(this.streamingMessage, this.thinkingRunTimings);
					this.streamingComponent.updateContent(this.streamingMessage);

					if (this.streamingMessage.stopReason === "aborted" || this.streamingMessage.stopReason === "error") {
						if (!errorMessage) {
							errorMessage = this.streamingMessage.errorMessage || "Error";
						}
						for (const [, component] of this.pendingTools.entries()) {
							component.updateResult({
								content: [{ type: "text", text: errorMessage }],
								isError: true,
							});
						}
						this.pendingTools.clear();
					} else {
						// Args are now complete - trigger diff computation for edit tools
						for (const [, component] of this.pendingTools.entries()) {
							component.setArgsComplete();
						}
						this.maybeShowCacheMissNotice(this.streamingMessage);
					}
					this.streamingComponent = undefined;
					this.streamingMessage = undefined;
					this.footer.invalidate();
				}
				this.ui.requestRender();
				break;

			case "tool_execution_start": {
				// If smooth streaming still has leading text unrevealed, flush through
				// this tool so the running card is not hidden behind the typewriter.
				if (this.settingsManager.getSmoothStreaming() && this.streamingTargetMessage && this.streamingComponent) {
					const opts = this.getSmoothStreamingOptions();
					const needed = countGraphemesBeforeToolCall(this.streamingTargetMessage, event.toolCallId, opts);
					if (this.streamingDisplayedLength < needed) {
						this.streamingDisplayedLength = needed;
					}
					this.paintSmoothStreamingFrame();
				}
				let component = this.pendingTools.get(event.toolCallId);
				if (!component) {
					component = new ToolExecutionComponent(
						event.toolName,
						event.toolCallId,
						event.args,
						{
							showImages: this.settingsManager.getShowImages(),
							imageWidthCells: this.settingsManager.getImageWidthCells(),
						},
						this.getRegisteredToolDefinition(event.toolName),
						this.ui,
						this.sessionManager.getCwd(),
					);
					component.setExpanded(this.toolOutputExpanded);
					this.addToolComponentToChat(component);
					this.pendingTools.set(event.toolCallId, component);
				}
				component.markExecutionStarted();
				this.ui.requestRender();
				break;
			}

			case "tool_execution_update": {
				const component = this.pendingTools.get(event.toolCallId);
				if (component) {
					component.updateResult({ ...event.partialResult, isError: false }, true);
					this.ui.requestRender();
				}
				break;
			}

			case "tool_execution_end": {
				const component = this.pendingTools.get(event.toolCallId);
				if (component) {
					component.updateResult({ ...event.result, isError: event.isError });
					this.pendingTools.delete(event.toolCallId);
					this.ui.requestRender();
				}
				break;
			}

			case "agent_end":
				if (this.settingsManager.getShowTerminalProgress()) {
					this.ui.terminal.setProgress(false);
				}
				this.clearStatusIndicator("working");
				this.stopSmoothStreaming();
				if (this.streamingComponent) {
					this.chatContainer.removeChild(this.streamingComponent);
					this.streamingComponent = undefined;
					this.streamingMessage = undefined;
				}
				this.pendingTools.clear();

				if (this.swarmMode) {
					this.swarmMode = false;
					this.setExtensionStatus("swarm", undefined);
				}

				this.maybeAutoNameSession();

				// lunr: capture tree-scope changes at turn boundary (bash side-effects).
				captureTreeChanges(this.sessionManager.getCwd(), this.sessionManager.getSessionId());

				this.ui.requestRender();
				break;

			case "agent_settled":
				await this.checkShutdownRequested();
				break;

			case "compaction_start": {
				if (this.settingsManager.getShowTerminalProgress()) {
					this.ui.terminal.setProgress(true);
				}
				// Keep editor active; submissions are queued during compaction.
				this.autoCompactionEscapeHandler = this.defaultEditor.onEscape;
				this.defaultEditor.onEscape = () => {
					this.session.abortCompaction();
				};
				this.showStatusIndicator(new CompactionStatusIndicator(this.ui, event.reason));
				this.ui.requestRender();
				break;
			}

			case "compaction_end": {
				if (this.settingsManager.getShowTerminalProgress()) {
					this.ui.terminal.setProgress(false);
				}
				if (this.autoCompactionEscapeHandler) {
					this.defaultEditor.onEscape = this.autoCompactionEscapeHandler;
					this.autoCompactionEscapeHandler = undefined;
				}
				this.clearStatusIndicator("compaction");
				if (event.aborted) {
					if (event.reason === "manual") {
						this.showError("Compaction cancelled");
					} else {
						this.showStatus("Auto-compaction cancelled");
					}
				} else if (event.result) {
					this.chatContainer.clear();
					this.rebuildChatFromMessages();
					this.addMessageToChat(
						createCompactionSummaryMessage(
							event.result.summary,
							event.result.tokensBefore,
							new Date().toISOString(),
						),
					);
					this.footer.invalidate();
				} else if (event.errorMessage) {
					if (event.reason === "manual") {
						this.showError(event.errorMessage);
					} else {
						this.chatContainer.addChild(new Spacer(1));
						this.chatContainer.addChild(new Text(theme.fg("error", event.errorMessage), 1, 0));
					}
				}
				void this.flushCompactionQueue({ willRetry: event.willRetry });
				this.ui.requestRender();
				break;
			}

			case "auto_retry_start": {
				// Set up escape to abort retry
				this.retryEscapeHandler = this.defaultEditor.onEscape;
				this.defaultEditor.onEscape = () => {
					this.session.abortRetry();
				};
				this.showStatusIndicator(
					new RetryStatusIndicator(this.ui, event.attempt, event.maxAttempts, event.delayMs),
				);
				this.ui.requestRender();
				break;
			}

			case "auto_retry_end": {
				// Restore escape handler
				if (this.retryEscapeHandler) {
					this.defaultEditor.onEscape = this.retryEscapeHandler;
					this.retryEscapeHandler = undefined;
				}
				this.clearStatusIndicator("retry");
				// Show error only on final failure (success shows normal response)
				if (!event.success) {
					this.showError(`Retry failed after ${event.attempt} attempts: ${event.finalError || "Unknown error"}`);
				}
				this.ui.requestRender();
				break;
			}

			// lunr: one-line status when a failed run rotates to the next pooled subscription key.
			case "subscription_rotation": {
				this.showStatus(`Switched ${event.providerId} key to '${event.keyName}' (previous key hit its limit)`);
				break;
			}
		}
	}

	/** Extract text content from a user message */
	private getUserMessageText(message: Message): string {
		if (message.role !== "user") return "";
		const textBlocks =
			typeof message.content === "string"
				? [{ type: "text", text: message.content }]
				: message.content.filter((c: { type: string }) => c.type === "text");
		return textBlocks.map((c) => (c as { text: string }).text).join("");
	}

	/**
	 * Show a status message in the chat.
	 *
	 * If multiple status messages are emitted back-to-back (without anything else being added to the chat),
	 * we update the previous status line instead of appending new ones to avoid log spam.
	 */
	private showStatus(message: string): void {
		const children = this.chatContainer.children;
		const last = children.length > 0 ? children[children.length - 1] : undefined;
		const secondLast = children.length > 1 ? children[children.length - 2] : undefined;

		if (last && secondLast && last === this.lastStatusText && secondLast === this.lastStatusSpacer) {
			this.lastStatusText.setText(theme.fg("dim", message));
			this.ui.requestRender();
			return;
		}

		const spacer = new Spacer(1);
		const text = new Text(theme.fg("dim", message), 1, 0);
		this.chatContainer.addChild(spacer);
		this.chatContainer.addChild(text);
		this.lastStatusSpacer = spacer;
		this.lastStatusText = text;
		this.ui.requestRender();
	}

	private addCustomEntryToChat(entry: Extract<SessionEntry, { type: "custom" }>): void {
		const renderer = this.session.extensionRunner.getEntryRenderer(entry.customType);
		if (!renderer) {
			return;
		}
		const component = new CustomEntryComponent(entry, renderer);
		component.setExpanded(this.toolOutputExpanded);
		if (!component.hasContent()) {
			return;
		}

		if (this.streamingComponent) {
			const streamingIndex = this.chatContainer.children.indexOf(this.streamingComponent);
			if (streamingIndex >= 0) {
				this.chatContainer.children.splice(streamingIndex, 0, component);
				return;
			}
		}

		this.chatContainer.addChild(component);
	}

	private addMessageToChat(message: AgentMessage, options?: { populateHistory?: boolean }): void {
		switch (message.role) {
			case "bashExecution": {
				const component = new BashExecutionComponent(message.command, this.ui, message.excludeFromContext);
				if (message.output) {
					component.appendOutput(message.output);
				}
				component.setComplete(
					message.exitCode,
					message.cancelled,
					message.truncated ? ({ truncated: true } as TruncationResult) : undefined,
					message.fullOutputPath,
				);
				this.chatContainer.addChild(component);
				break;
			}
			case "custom": {
				if (message.display) {
					const renderer = this.session.extensionRunner.getMessageRenderer(message.customType);
					const component = new CustomMessageComponent(message, renderer, this.getMarkdownThemeWithSettings());
					component.setExpanded(this.toolOutputExpanded);
					this.chatContainer.addChild(component);
				}
				break;
			}
			case "compactionSummary": {
				this.chatContainer.addChild(new Spacer(1));
				const component = new CompactionSummaryMessageComponent(message, this.getMarkdownThemeWithSettings());
				component.setExpanded(this.toolOutputExpanded);
				this.chatContainer.addChild(component);
				break;
			}
			case "branchSummary": {
				this.chatContainer.addChild(new Spacer(1));
				const component = new BranchSummaryMessageComponent(message, this.getMarkdownThemeWithSettings());
				component.setExpanded(this.toolOutputExpanded);
				this.chatContainer.addChild(component);
				break;
			}
			case "user": {
				const textContent = this.getUserMessageText(message);
				if (textContent) {
					if (this.chatContainer.children.length > 0) {
						this.chatContainer.addChild(new Spacer(1));
					}
					const skillBlock = parseSkillBlock(textContent);
					// lunr: render injected prompts (/swarm /research /goal) collapsed instead
					// of dumping the full multi-line prompt body verbatim.
					const injected = detectInjectedPrompt(textContent);
					if (injected) {
						const component = new InjectedPromptMessageComponent(
							injected.kind,
							injected.summary,
							textContent,
							this.getMarkdownThemeWithSettings(),
						);
						component.setExpanded(this.toolOutputExpanded);
						this.chatContainer.addChild(component);
					} else if (skillBlock) {
						// Render skill block (collapsible)
						const component = new SkillInvocationMessageComponent(
							skillBlock,
							this.getMarkdownThemeWithSettings(),
						);
						component.setExpanded(this.toolOutputExpanded);
						this.chatContainer.addChild(component);
						// Render user message separately if present
						if (skillBlock.userMessage) {
							this.chatContainer.addChild(new Spacer(1));
							const userComponent = new UserMessageComponent(
								skillBlock.userMessage,
								this.getMarkdownThemeWithSettings(),
								this.outputPad,
								this.settingsManager.getGutterRail(),
							);
							this.chatContainer.addChild(userComponent);
						}
					} else {
						const userComponent = new UserMessageComponent(
							textContent,
							this.getMarkdownThemeWithSettings(),
							this.outputPad,
							this.settingsManager.getGutterRail(),
						);
						this.chatContainer.addChild(userComponent);
					}
					if (options?.populateHistory) {
						this.editor.addToHistory?.(textContent);
					}
				}
				break;
			}
			case "assistant": {
				const assistantComponent = new AssistantMessageComponent(
					message,
					this.hideThinkingBlock,
					this.getMarkdownThemeWithSettings(),
					this.hiddenThinkingLabel,
					this.outputPad,
					this.settingsManager.getGutterRail(),
					this.thinkingCollapse,
				);
				// lunr: collapsible reasoning — re-attach live timings when this
				// message was streamed in this session (undefined = history).
				assistantComponent.setThinkingTimings(this.thinkingTimingsByMessage.get(message));
				// lunr: ctrl+o expansion — inherit the global expand state.
				assistantComponent.setExpanded(this.toolOutputExpanded);
				this.chatContainer.addChild(assistantComponent);
				break;
			}
			case "toolResult": {
				// Tool results are rendered inline with tool calls, handled separately
				break;
			}
			default: {
				const _exhaustive: never = message;
			}
		}
	}

	private renderSessionItems(
		items: readonly RenderSessionItem[],
		options: { updateFooter?: boolean; populateHistory?: boolean } = {},
	): void {
		this.pendingTools.clear();
		const renderedPendingTools = new Map<string, ToolExecutionComponent>();
		// Cache-miss notices are not persisted; re-derive them from the full entry
		// list and re-inject them after the assistant messages that paid for them.
		const cacheMisses = this.settingsManager.getShowCacheMissNotices()
			? collectCacheMisses(this.sessionManager.getEntries(), this.session.modelRuntime)
			: new Map<AssistantMessage, CacheMiss>();

		if (options.updateFooter) {
			this.footer.invalidate();
			this.updateEditorBorderColor();
		}

		for (const item of items) {
			if (isCustomSessionEntry(item)) {
				this.addCustomEntryToChat(item);
				continue;
			}

			const message = item;
			// Assistant messages need special handling for tool calls
			if (message.role === "assistant") {
				this.addMessageToChat(message);
				// Render tool call components
				for (const content of message.content) {
					if (content.type === "toolCall") {
						const component = new ToolExecutionComponent(
							content.name,
							content.id,
							content.arguments,
							{
								showImages: this.settingsManager.getShowImages(),
								imageWidthCells: this.settingsManager.getImageWidthCells(),
							},
							this.getRegisteredToolDefinition(content.name),
							this.ui,
							this.sessionManager.getCwd(),
						);
						component.setExpanded(this.toolOutputExpanded);
						this.addToolComponentToChat(component);

						if (message.stopReason === "aborted" || message.stopReason === "error") {
							let errorMessage: string;
							if (message.stopReason === "aborted") {
								const retryAttempt = this.session.retryAttempt;
								errorMessage =
									retryAttempt > 0
										? `Aborted after ${retryAttempt} retry attempt${retryAttempt > 1 ? "s" : ""}`
										: "Operation aborted";
							} else {
								errorMessage = message.errorMessage || "Error";
							}
							component.updateResult({ content: [{ type: "text", text: errorMessage }], isError: true });
						} else {
							renderedPendingTools.set(content.id, component);
						}
					}
				}
				if (message.stopReason !== "aborted" && message.stopReason !== "error") {
					const miss = cacheMisses.get(message);
					if (miss) this.addCacheMissNotice(miss);
				}
			} else if (message.role === "toolResult") {
				// Match tool results to pending tool components
				const component = renderedPendingTools.get(message.toolCallId);
				if (component) {
					component.updateResult(message);
					renderedPendingTools.delete(message.toolCallId);
				}
			} else {
				// All other messages use standard rendering
				this.addMessageToChat(message, options);
			}
		}

		for (const [toolCallId, component] of renderedPendingTools) {
			this.pendingTools.set(toolCallId, component);
		}
		this.ui.requestRender();
	}

	/**
	 * Render session entries to chat. Used for initial load and rebuild after compaction.
	 * @param entries Compaction-aware session entries to render
	 * @param options.updateFooter Update footer state
	 * @param options.populateHistory Add user messages to editor history
	 */
	private renderSessionEntries(
		entries: SessionEntry[],
		options: { updateFooter?: boolean; populateHistory?: boolean } = {},
	): void {
		const items = entries.flatMap((entry): RenderSessionItem[] => {
			if (entry.type === "custom") {
				return [entry];
			}
			return sessionEntryToContextMessages(entry);
		});
		this.renderSessionItems(items, options);
	}

	/**
	 * Show a transcript notice when a completed assistant message paid for a
	 * significant cache miss. Only states observable facts: the miss itself,
	 * a model switch, or an idle gap past the cache TTL.
	 */
	private maybeShowCacheMissNotice(message: AssistantMessage): void {
		if (!this.settingsManager.getShowCacheMissNotices()) return;

		// Entries don't contain `message` yet: message_end fires before persistence.
		const miss = detectCacheMiss(this.sessionManager.getEntries(), message, this.session.modelRuntime);
		if (miss) this.addCacheMissNotice(miss);
	}

	private addCacheMissNotice(miss: CacheMiss): void {
		if (miss.missedTokens < 20_000 && miss.missedCost < 0.1) return;

		const cost = miss.missedCost >= 0.01 ? ` (~$${miss.missedCost.toFixed(2)})` : "";
		const reBilled = `${formatTokens(miss.missedTokens)} tokens re-billed${cost}`;
		let label = "Cache miss";
		if (miss.modelChanged) {
			label = "Cache miss after model switch";
		} else if (miss.idleMs >= CACHE_TTL_MS) {
			label = `Cache miss after ${Math.round(miss.idleMs / 60_000)}m idle`;
		}
		const text = theme.fg("warning", `${label}: ${reBilled}`);
		this.chatContainer.addChild(new Spacer(1));
		this.chatContainer.addChild(new Text(text, 1, 0));
	}

	renderInitialMessages(): void {
		const entries = this.sessionManager.buildContextEntries();
		this.renderSessionEntries(entries, {
			updateFooter: true,
			populateHistory: true,
		});
		this.renderProjectTrustWarningIfNeeded();

		// Show compaction info if session was compacted
		const allEntries = this.sessionManager.getEntries();
		const compactionCount = allEntries.filter((e) => e.type === "compaction").length;
		if (compactionCount > 0) {
			const times = compactionCount === 1 ? "1 time" : `${compactionCount} times`;
			this.showStatus(`Session compacted ${times}`);
		}
	}

	private renderProjectTrustWarningIfNeeded(): void {
		if (this.settingsManager.isProjectTrusted() || !hasTrustRequiringProjectResources(this.sessionManager.getCwd())) {
			return;
		}

		if (this.chatContainer.children.length > 0) {
			this.chatContainer.addChild(new Spacer(1));
		}
		this.chatContainer.addChild(
			new Text(
				theme.fg(
					"warning",
					`This project is not trusted. Project ${CONFIG_DIR_NAME} resources and packages are ignored. Use /trust to save a trust decision, then restart pi.`,
				),
				1,
				0,
			),
		);
	}

	async getUserInput(): Promise<QueuedUserInput> {
		const queuedInput = this.pendingUserInputs.shift();
		if (queuedInput !== undefined) {
			return queuedInput;
		}

		return new Promise((resolve) => {
			this.onInputCallback = (input: QueuedUserInput) => {
				this.onInputCallback = undefined;
				resolve(input);
			};
		});
	}

	private rebuildChatFromMessages(): void {
		this.chatContainer.clear();
		this.renderSessionEntries(this.sessionManager.buildContextEntries());
	}

	// =========================================================================
	// Key handlers
	// =========================================================================

	private handleCtrlC(): void {
		const now = Date.now();
		if (now - this.lastSigintTime < 500) {
			void this.shutdown();
		} else {
			this.clearEditor();
			this.lastSigintTime = now;
		}
	}

	private handleCtrlD(): void {
		// Only called when editor is empty (enforced by CustomEditor)
		void this.shutdown();
	}

	/**
	 * Gracefully shutdown the agent.
	 * Stops the TUI before emitting shutdown events so extension UI cleanup cannot
	 * repaint the final frame while the process is exiting.
	 */
	private isShuttingDown = false;

	private async shutdown(options?: { fromSignal?: boolean }): Promise<void> {
		if (this.isShuttingDown) return;
		this.isShuttingDown = true;
		this.stopSmoothStreaming();
		if (this.planUsageTimer) {
			clearInterval(this.planUsageTimer);
			this.planUsageTimer = undefined;
		}
		getUsageServiceBridge()?.setOnUpdate(undefined);
		// Keep signal handlers registered until terminal cleanup has completed.
		// `signal-exit` checks the listener list during the same SIGTERM/SIGHUP
		// dispatch and re-sends the signal if only its own listeners remain.

		if (options?.fromSignal) {
			// Signal-triggered shutdown (SIGTERM/SIGHUP). Emit extension cleanup
			// (session_shutdown) BEFORE touching the terminal. Extension teardown
			// such as removing sockets does not write to the tty, so it must not be
			// skipped if a later terminal-restore write fails on a dead or stalled
			// terminal. If the terminal is gone, the restore writes below emit EIO,
			// which the stdout/stderr error handler turns into emergencyTerminalExit;
			// the render loop is already idle, so this cannot hot-spin (see #4144).
			await this.runtimeHost.dispose();
			this.themeController.disableAutoSync();
			await this.ui.terminal.drainInput(1000);
			try {
				processRegistry.killAll();
			} catch {}
			this.stop();
			process.exit(0);
		}

		// Interactive quit (Ctrl+D, Ctrl+C, /quit, extension shutdown()). Stop the
		// TUI before emitting shutdown events so extension UI cleanup cannot repaint
		// the final frame while the process is exiting.
		// Drain any in-flight Kitty key release events before stopping.
		// This prevents escape sequences from leaking to the parent shell over slow SSH.
		this.themeController.disableAutoSync();
		await this.ui.terminal.drainInput(1000);

		// lunr: quit prompt — ask about killing tracked processes before stopping the TUI.
		const tracked = processRegistry.list().filter((p) => p.status !== "exited");
		if (tracked.length > 0) {
			const shouldKill = await this.confirmDisclaimerWithLabels(
				"Session processes still running",
				`${tracked.length} process(es) started during this session are still alive.\nKill them before exit?`,
			);
			if (shouldKill) {
				processRegistry.killAll();
			}
		}

		this.stop();
		await this.runtimeHost.dispose();

		// lunr: exit summary card — print a closing card for non-empty sessions,
		// before the resume line. Fires on all interactive exits reaching shutdown
		// (/quit, double ctrl+c, ctrl+d). Skipped for empty sessions (0 user msgs).
		const entries = this.sessionManager?.getEntries?.();
		const exitStats = entries ? computeExitCardStats(entries) : { turns: 0, tokens: 0, filesChanged: 0 };
		const cardLines = buildExitCard(exitStats);
		if (cardLines.length > 0) {
			process.stdout.write(`${chalk.dim(cardLines.join("\n"))}\n`);
		}

		const resumeCommand =
			this.sessionManager && typeof this.sessionManager.isPersisted === "function"
				? formatResumeCommand(this.sessionManager)
				: undefined;
		if (resumeCommand) {
			process.stdout.write(`${chalk.dim("To resume this session:")} ${resumeCommand}\n`);
		}

		process.exit(0);
	}

	private emergencyTerminalExit(): never {
		this.isShuttingDown = true;
		this.unregisterSignalHandlers();
		killTrackedDetachedChildren();
		try {
			processRegistry.killAll();
		} catch {}
		// The terminal is gone. Do not run normal shutdown because TUI and
		// extension cleanup can write restore sequences and re-trigger EIO.
		process.exit(129);
	}

	/**
	 * Last-resort handler for uncaught exceptions. The TUI puts stdin into raw
	 * mode and hides the cursor; without this handler, an uncaught throw from
	 * anywhere (e.g. an extension's async `ChildProcess.on("exit")` callback)
	 * tears down the process while leaving the terminal in raw mode with no
	 * cursor, requiring `stty sane && reset` to recover.
	 *
	 * Unlike emergencyTerminalExit, the terminal is still alive here, so we
	 * call ui.stop() to restore cooked mode, the cursor, and disable bracketed
	 * paste / Kitty / modifyOtherKeys sequences.
	 */
	private uncaughtCrash(error: Error): never {
		if (this.isShuttingDown) {
			process.exit(1);
		}
		this.isShuttingDown = true;
		try {
			this.unregisterSignalHandlers();
		} catch {}
		try {
			killTrackedDetachedChildren();
		} catch {}
		try {
			this.ui.stop();
		} catch {}
		console.error(`${APP_NAME} exiting due to uncaughtException:`);
		console.error(error);
		process.exit(1);
	}

	/**
	 * Check if shutdown was requested and perform shutdown if so.
	 */
	private async checkShutdownRequested(): Promise<void> {
		if (!this.shutdownRequested) return;
		await this.shutdown();
	}

	private registerSignalHandlers(): void {
		this.unregisterSignalHandlers();

		const signals: NodeJS.Signals[] = ["SIGTERM"];
		if (process.platform !== "win32") {
			signals.push("SIGHUP");
		}

		for (const signal of signals) {
			const handler = () => {
				// SIGHUP no longer hard-exits: graceful shutdown emits session_shutdown
				// first, then attempts terminal restore. A genuinely dead terminal
				// surfaces as an EIO on the restore writes, which the stdout/stderr
				// error handler converts into emergencyTerminalExit (see #4144, #5080).
				killTrackedDetachedChildren();
				void this.shutdown({ fromSignal: true });
			};
			process.prependListener(signal, handler);
			this.signalCleanupHandlers.push(() => process.off(signal, handler));
		}

		const terminalErrorHandler = (error: Error) => {
			if (isDeadTerminalError(error)) {
				this.emergencyTerminalExit();
			}
			throw error;
		};
		process.stdout.on("error", terminalErrorHandler);
		process.stderr.on("error", terminalErrorHandler);
		this.signalCleanupHandlers.push(() => process.stdout.off("error", terminalErrorHandler));
		this.signalCleanupHandlers.push(() => process.stderr.off("error", terminalErrorHandler));

		// Restore the terminal before the process dies on any uncaught throw.
		// Without this, an unhandled exception from extension code (or anywhere
		// in pi) leaves the terminal in raw mode with no cursor.
		const uncaughtExceptionHandler = (error: Error) => this.uncaughtCrash(error);
		process.prependListener("uncaughtException", uncaughtExceptionHandler);
		this.signalCleanupHandlers.push(() => process.off("uncaughtException", uncaughtExceptionHandler));
	}

	private unregisterSignalHandlers(): void {
		for (const cleanup of this.signalCleanupHandlers) {
			cleanup();
		}
		this.signalCleanupHandlers = [];
	}

	private handleCtrlZ(): void {
		if (process.platform === "win32") {
			this.showStatus("Suspend to background is not supported on Windows");
			return;
		}

		// Keep the event loop alive while suspended. Without this, stopping the TUI
		// can leave Node with no ref'ed handles, causing the process to exit on fg
		// before the SIGCONT handler gets a chance to restore the terminal.
		const suspendKeepAlive = setInterval(() => {}, 2 ** 30);

		// Ignore SIGINT while suspended so Ctrl+C in the terminal does not
		// kill the backgrounded process. The handler is removed on resume.
		const ignoreSigint = () => {};
		process.on("SIGINT", ignoreSigint);

		// Set up handler to restore TUI when resumed
		process.once("SIGCONT", () => {
			clearInterval(suspendKeepAlive);
			process.removeListener("SIGINT", ignoreSigint);
			this.ui.start();
			this.ui.requestRender(true);
		});

		try {
			// Stop the TUI (restore terminal to normal mode)
			this.ui.stop();

			// Send SIGTSTP to process group (pid=0 means all processes in group)
			process.kill(0, "SIGTSTP");
		} catch (error) {
			clearInterval(suspendKeepAlive);
			process.removeListener("SIGINT", ignoreSigint);
			throw error;
		}
	}

	private async handleFollowUp(): Promise<void> {
		const text = (this.editor.getExpandedText?.() ?? this.editor.getText()).trim();
		if (!text) return;

		// Queue input during compaction (extension commands execute immediately)
		if (this.session.isCompacting) {
			if (this.isExtensionCommand(text)) {
				this.editor.addToHistory?.(text);
				this.editor.setText("");
				await this.promptAfterDeferredBuiltins(text);
			} else {
				const images = await this.loadImageAttachments(this.takeSubmittedImages(text));
				this.queueCompactionMessage(text, "followUp", images);
			}
			return;
		}

		// Alt+Enter queues a follow-up message (waits until agent finishes)
		// This handles extension commands (execute immediately), prompt template expansion, and queueing
		if (this.session.isStreaming) {
			const images = await this.loadImageAttachments(this.takeSubmittedImages(text));
			this.editor.addToHistory?.(text);
			this.editor.setText("");
			await this.promptAfterDeferredBuiltins(text, { streamingBehavior: "followUp", images });
			this.updatePendingMessagesDisplay();
			this.ui.requestRender();
		}
		// If not streaming, Alt+Enter acts like regular Enter (trigger onSubmit)
		else if (this.editor.onSubmit) {
			const images = await this.loadImageAttachments(this.takeSubmittedImages(text));
			this.stagedSubmitImages = images;
			this.editor.setText("");
			this.editor.onSubmit(text);
		}
	}

	private handleDequeue(): void {
		const restored = this.restoreQueuedMessagesToEditor();
		if (restored === 0) {
			this.showStatus("No queued messages to restore");
		} else {
			this.showStatus(`Restored ${restored} queued message${restored > 1 ? "s" : ""} to editor`);
		}
	}

	private updateEditorBorderColor(): void {
		if (this.isBashMode) {
			this.editor.borderColor = theme.getBashModeBorderColor();
		} else {
			const level = this.session.thinkingLevel || "off";
			this.editor.borderColor = theme.getThinkingBorderColor(level);
		}
		this.ui.requestRender();
	}

	private cycleThinkingLevel(): void {
		const newLevel = this.session.cycleThinkingLevel();
		if (newLevel === undefined) {
			this.showStatus("Current model does not support thinking");
		} else {
			this.footer.invalidate();
			this.updateEditorBorderColor();
			this.showStatus(`Thinking level: ${newLevel}`);
		}
	}

	private cyclePermissionMode(): void {
		this.applyPermissionMode(nextPermissionMode(getPermissionMode()), { silent: true });
	}

	private async cycleModel(direction: "forward" | "backward"): Promise<void> {
		try {
			const result = await this.session.cycleModel(direction);
			if (result === undefined) {
				const msg = this.session.scopedModels.length > 0 ? "Only one model in scope" : "Only one model available";
				this.showStatus(msg);
			} else {
				this.footer.invalidate();
				this.updateEditorBorderColor();
				const thinkingStr =
					result.model.reasoning && result.thinkingLevel !== "off" ? ` (thinking: ${result.thinkingLevel})` : "";
				this.showStatus(`Switched to ${result.model.name || result.model.id}${thinkingStr}`);
				void this.maybeWarnAboutAnthropicSubscriptionAuth(result.model);
			}
		} catch (error) {
			this.showError(error instanceof Error ? error.message : String(error));
		}
	}

	private toggleToolOutputExpansion(): void {
		this.setToolsExpanded(!this.toolOutputExpanded);
	}

	private setToolsExpanded(expanded: boolean): void {
		this.toolOutputExpanded = expanded;
		const activeHeader = this.customHeader ?? this.builtInHeader;
		if (isExpandable(activeHeader)) {
			activeHeader.setExpanded(expanded);
		}
		for (const container of [this.loadedResourcesContainer, this.chatContainer]) {
			for (const child of container.children) {
				if (isExpandable(child)) {
					child.setExpanded(expanded);
				}
			}
		}
		// lunr: let extensions (lunr-todos widget) react to ctrl+o expansion.
		(globalThis as any)[Symbol.for("@lunr/tools-expanded-changed")]?.(expanded);
		this.ui.requestRender();
	}

	// lunr: consecutive same-tool grouping — a tool component appended right after
	// another component for the SAME tool becomes a group continuation (no top
	// spacer / top pad); the previous card drops its bottom pad. Mixed neighbors
	// and singletons keep Box(1, 1). Adjacency is decided at add time only.
	private addToolComponentToChat(component: ToolExecutionComponent): void {
		const last = this.chatContainer.children[this.chatContainer.children.length - 1];
		applySameToolGrouping(last instanceof ToolExecutionComponent ? last : undefined, component);
		this.chatContainer.addChild(component);
	}

	private toggleThinkingBlockVisibility(): void {
		this.hideThinkingBlock = !this.hideThinkingBlock;
		this.settingsManager.setHideThinkingBlock(this.hideThinkingBlock);

		// Rebuild chat from session messages
		this.chatContainer.clear();
		this.rebuildChatFromMessages();

		// If streaming, re-add the streaming component with updated visibility and re-render.
		// Re-slice to the current revealed length so hide-thinking does not dump the tail.
		if (this.streamingComponent && this.streamingMessage) {
			this.streamingComponent.setHideThinkingBlock(this.hideThinkingBlock);
			if (this.settingsManager.getSmoothStreaming() && this.streamingTargetMessage) {
				const opts = this.getSmoothStreamingOptions();
				const total = countMessageGraphemes(this.streamingTargetMessage, opts);
				if (this.streamingDisplayedLength > total) {
					this.streamingDisplayedLength = total;
				}
				this.paintSmoothStreamingFrame();
				// Restart the timer if new (or still-pending) backlog exists.
				if (this.streamingDisplayedLength < total) {
					this.startSmoothStreaming();
				}
			} else {
				this.streamingComponent.updateContent(this.streamingMessage);
			}
			this.chatContainer.addChild(this.streamingComponent);
		}

		this.showStatus(`Thinking blocks: ${this.hideThinkingBlock ? "hidden" : "visible"}`);
	}

	private async openExternalEditor(): Promise<void> {
		const editorCmd = this.settingsManager.getExternalEditorCommand();
		if (!editorCmd) {
			this.showWarning("No editor configured. Set externalEditor in settings.json or $VISUAL/$EDITOR.");
			return;
		}

		const currentText = this.editor.getExpandedText?.() ?? this.editor.getText();
		const tmpFile = path.join(os.tmpdir(), `pi-editor-${Date.now()}.pi.md`);

		try {
			// Write current content to temp file
			fs.writeFileSync(tmpFile, currentText, "utf-8");

			// Stop TUI to release terminal
			this.ui.stop();

			// Split by space to support editor arguments (e.g., "code --wait")
			const [editor, ...editorArgs] = editorCmd.split(" ");

			process.stdout.write(`Launching external editor: ${editorCmd}\nPi will resume when the editor exits.\n`);

			// Do not use spawnSync here. On Windows, synchronous child_process calls can keep
			// Node/libuv's console input read active after ui.stop() pauses stdin, racing
			// vim/nvim for the console input buffer until Ctrl+C cancels the pending read.
			const status = await new Promise<number | null>((resolve) => {
				const child = spawn(editor, [...editorArgs, tmpFile], {
					stdio: "inherit",
					shell: process.platform === "win32",
				});
				child.on("error", () => resolve(null));
				child.on("close", (code) => resolve(code));
			});

			// On successful exit (status 0), replace editor content
			if (status === 0) {
				const newContent = fs.readFileSync(tmpFile, "utf-8").replace(/\n$/, "");
				this.editor.setText(newContent);
			}
			// On non-zero exit, keep original text (no action needed)
		} finally {
			// Clean up temp file
			try {
				fs.unlinkSync(tmpFile);
			} catch {
				// Ignore cleanup errors
			}

			// Restart TUI
			this.ui.start();
			// Force full re-render since external editor uses alternate screen
			this.ui.requestRender(true);
		}
	}

	// =========================================================================
	// UI helpers
	// =========================================================================

	clearEditor(): void {
		this.editor.setText("");
		this.ui.requestRender();
	}

	showError(errorMessage: string): void {
		this.chatContainer.addChild(new Spacer(1));
		this.chatContainer.addChild(new Text(theme.fg("error", `Error: ${errorMessage}`), 1, 0));
		this.ui.requestRender();
	}

	showWarning(warningMessage: string): void {
		this.chatContainer.addChild(new Spacer(1));
		this.chatContainer.addChild(new Text(theme.fg("warning", `Warning: ${warningMessage}`), 1, 0));
		this.ui.requestRender();
	}

	/**
	 * Get all queued messages (read-only).
	 * Combines session queue and compaction queue.
	 */
	private getAllQueuedMessages(): { steering: string[]; followUp: string[] } {
		return {
			steering: [
				...this.session.getSteeringMessages(),
				...this.compactionQueuedMessages.filter((msg) => msg.mode === "steer").map((msg) => msg.text),
			],
			followUp: [
				...this.session.getFollowUpMessages(),
				...this.compactionQueuedMessages.filter((msg) => msg.mode === "followUp").map((msg) => msg.text),
			],
		};
	}

	/**
	 * Clear all queued messages and return their contents.
	 * Clears both session queue and compaction queue.
	 */
	private clearAllQueues(): { steering: string[]; followUp: string[] } {
		const { steering, followUp } = this.session.clearQueue();
		const compactionSteering = this.compactionQueuedMessages
			.filter((msg) => msg.mode === "steer")
			.map((msg) => msg.text);
		const compactionFollowUp = this.compactionQueuedMessages
			.filter((msg) => msg.mode === "followUp")
			.map((msg) => msg.text);
		this.compactionQueuedMessages = [];
		return {
			steering: [...steering, ...compactionSteering],
			followUp: [...followUp, ...compactionFollowUp],
		};
	}

	private updatePendingMessagesDisplay(): void {
		this.pendingMessagesContainer.clear();
		const { steering: steeringMessages, followUp: followUpMessages } = this.getAllQueuedMessages();
		if (steeringMessages.length > 0 || followUpMessages.length > 0) {
			this.pendingMessagesContainer.addChild(new Spacer(1));
			for (const message of steeringMessages) {
				const text = theme.fg("dim", `Steering: ${message}`);
				this.pendingMessagesContainer.addChild(new TruncatedText(text, 1, 0));
			}
			for (const message of followUpMessages) {
				const text = theme.fg("dim", `Follow-up: ${message}`);
				this.pendingMessagesContainer.addChild(new TruncatedText(text, 1, 0));
			}
			const dequeueHint = this.getAppKeyDisplay("app.message.dequeue");
			const hintText = theme.fg("dim", `↳ ${dequeueHint} to edit all queued messages`);
			this.pendingMessagesContainer.addChild(new TruncatedText(hintText, 1, 0));
		}
	}

	private restoreQueuedMessagesToEditor(options?: { abort?: boolean; currentText?: string }): number {
		const { steering, followUp } = this.clearAllQueues();
		const allQueued = [...steering, ...followUp];
		if (allQueued.length === 0) {
			this.updatePendingMessagesDisplay();
			if (options?.abort) {
				this.agent.abort();
			}
			return 0;
		}
		const queuedText = allQueued.join("\n\n");
		const currentText = options?.currentText ?? this.editor.getText();
		const combinedText = [queuedText, currentText].filter((t) => t.trim()).join("\n\n");
		this.editor.setText(combinedText);
		this.updatePendingMessagesDisplay();
		if (options?.abort) {
			this.agent.abort();
		}
		return allQueued.length;
	}

	private queueCompactionMessage(text: string, mode: "steer" | "followUp", images?: ImageContent[]): void {
		this.compactionQueuedMessages.push({ text, mode, images });
		this.editor.addToHistory?.(text);
		this.editor.setText("");
		this.updatePendingMessagesDisplay();
		this.showStatus("Queued message for after compaction");
	}

	private isExtensionCommand(text: string): boolean {
		if (!text.startsWith("/")) return false;

		const extensionRunner = this.session.extensionRunner;

		const spaceIndex = text.indexOf(" ");
		const commandName = spaceIndex === -1 ? text.slice(1) : text.slice(1, spaceIndex);
		return !!extensionRunner.getCommand(commandName);
	}

	private async flushCompactionQueue(options?: { willRetry?: boolean }): Promise<void> {
		if (this.compactionQueuedMessages.length === 0) {
			return;
		}

		const queuedMessages = [...this.compactionQueuedMessages];
		this.compactionQueuedMessages = [];
		this.updatePendingMessagesDisplay();

		const restoreQueue = (error: unknown) => {
			this.session.clearQueue();
			this.compactionQueuedMessages = queuedMessages;
			this.updatePendingMessagesDisplay();
			this.showError(
				`Failed to send queued message${queuedMessages.length > 1 ? "s" : ""}: ${
					error instanceof Error ? error.message : String(error)
				}`,
			);
		};

		try {
			if (options?.willRetry) {
				// When retry is pending, queue messages for the retry turn
				for (const message of queuedMessages) {
					if (this.isExtensionCommand(message.text)) {
						await this.promptAfterDeferredBuiltins(message.text);
					} else if (message.mode === "followUp") {
						await this.session.followUp(message.text, message.images);
					} else {
						await this.session.steer(message.text, message.images);
					}
				}
				this.updatePendingMessagesDisplay();
				return;
			}

			// Find first non-extension-command message to use as prompt
			const firstPromptIndex = queuedMessages.findIndex((message) => !this.isExtensionCommand(message.text));
			if (firstPromptIndex === -1) {
				// All extension commands - execute them all
				for (const message of queuedMessages) {
					await this.promptAfterDeferredBuiltins(message.text);
				}
				return;
			}

			// Execute any extension commands before the first prompt
			const preCommands = queuedMessages.slice(0, firstPromptIndex);
			const firstPrompt = queuedMessages[firstPromptIndex];
			const rest = queuedMessages.slice(firstPromptIndex + 1);

			for (const message of preCommands) {
				await this.promptAfterDeferredBuiltins(message.text);
			}

			// Send first prompt (starts streaming)
			const promptPromise = this.promptAfterDeferredBuiltins(firstPrompt.text, {
				images: firstPrompt.images,
			}).catch((error) => {
				restoreQueue(error);
			});

			// Queue remaining messages
			for (const message of rest) {
				if (this.isExtensionCommand(message.text)) {
					await this.promptAfterDeferredBuiltins(message.text);
				} else if (message.mode === "followUp") {
					await this.session.followUp(message.text, message.images);
				} else {
					await this.session.steer(message.text, message.images);
				}
			}
			this.updatePendingMessagesDisplay();
			void promptPromise;
		} catch (error) {
			restoreQueue(error);
		}
	}

	/** Move pending bash components from pending area to chat */
	private flushPendingBashComponents(): void {
		for (const component of this.pendingBashComponents) {
			this.pendingMessagesContainer.removeChild(component);
			this.chatContainer.addChild(component);
		}
		this.pendingBashComponents = [];
	}

	// =========================================================================
	// Selectors
	// =========================================================================

	/**
	 * Shows a selector component in place of the editor.
	 * @param create Factory that receives a `done` callback and returns the component and focus target
	 */
	private showSelector(create: (done: () => void) => { component: Component; focus: Component }): void {
		const done = () => {
			this.editorContainer.clear();
			this.editorContainer.addChild(this.editor);
			this.ui.setFocus(this.editor);
		};
		const { component, focus } = create(done);
		this.editorContainer.clear();
		this.editorContainer.addChild(component);
		this.ui.setFocus(focus);
		this.ui.requestRender();
	}

	private async showSettingsSelector(): Promise<void> {
		// lunr: prefetch the pool size for the Subscriptions row label (render paths stay sync).
		const subscriptionCount = await this.countSubscriptions();
		this.showSelector((done) => {
			const selector = new SettingsSelectorComponent(
				{
					autoCompact: this.session.autoCompactionEnabled,
					showImages: this.settingsManager.getShowImages(),
					imageWidthCells: this.settingsManager.getImageWidthCells(),
					autoResizeImages: this.settingsManager.getImageAutoResize(),
					blockImages: this.settingsManager.getBlockImages(),
					enableSkillCommands: this.settingsManager.getEnableSkillCommands(),
					steeringMode: this.session.steeringMode,
					followUpMode: this.session.followUpMode,
					transport: this.settingsManager.getTransport(),
					httpIdleTimeoutMs: this.settingsManager.getHttpIdleTimeoutMs(),
					thinkingLevel: this.session.thinkingLevel,
					availableThinkingLevels: this.session.getAvailableThinkingLevels(),
					currentTheme: this.settingsManager.getThemeSetting() || "moon",
					terminalTheme: this.themeController.getTerminalTheme(),
					availableThemes: getAvailableThemes(),
					hideThinkingBlock: this.hideThinkingBlock,
					thinkingCollapse: this.thinkingCollapse,
					cacheRetention: this.settingsManager.getCacheRetention() ?? "short",
					doubleEscapeAction: this.settingsManager.getDoubleEscapeAction(),
					treeFilterMode: this.settingsManager.getTreeFilterMode(),
					showHardwareCursor: this.settingsManager.getShowHardwareCursor(),
					showCacheMissNotices: this.settingsManager.getShowCacheMissNotices(),
					defaultProjectTrust: this.settingsManager.getDefaultProjectTrust(),
					editorPaddingX: this.settingsManager.getEditorPaddingX(),
					outputPad: this.settingsManager.getOutputPad(),
					autocompleteMaxVisible: this.settingsManager.getAutocompleteMaxVisible(),
					quietStartup: this.settingsManager.getQuietStartup(),
					smoothStreaming: this.settingsManager.getSmoothStreaming(),
					sessionRetentionDays: this.settingsManager.getSessionRetentionDays(),
					clearOnShrink: this.settingsManager.getClearOnShrink(),
					showTerminalProgress: this.settingsManager.getShowTerminalProgress(),
					modelTiers: this.settingsManager.getModelTiers(),
					memoryEnabled: this.settingsManager.getMemoryEnabled(),
					memoryCharCap: this.settingsManager.getMemoryCharCap(),
					searchCurator: getSearchCuratorSetting(),
					// lunr: TUI customize settings
					gutterRail: this.settingsManager.getGutterRail(),
					promptSymbol: this.settingsManager.getPromptSymbol(),
					footerMcp: this.settingsManager.getFooterMcp(),
					footerLsp: this.settingsManager.getFooterLsp(),
					footerContext: this.settingsManager.getFooterContext(),
					footerTokens: this.settingsManager.getFooterTokens(),
					footerCacheHitRate: this.settingsManager.getFooterCacheHitRate(),
					footerTps: this.settingsManager.getFooterTps(),
					footerStatuses: this.settingsManager.getFooterStatuses(),
					footerGit: this.settingsManager.getFooterGit(),
					footerPlan: this.settingsManager.getFooterPlan(),
					footerPlanBar: this.settingsManager.getFooterPlanBar(),
					planUsageWindow: this.settingsManager.getPlanUsageWindow(),
					defaultPermissionMode: this.settingsManager.getDefaultPermissionMode(),
					rollbackEnabled: this.settingsManager.getRollbackEnabled(),
					rollbackTurns: this.settingsManager.getRollbackTurns(),
					rollbackCapture: this.settingsManager.getRollbackCapture(),
					rollbackScope: this.settingsManager.getRollbackScope(),
					// lunr: multi-subscription pools
					autoManageSubscriptions: this.settingsManager.getAutoManageSubscriptions(),
					subscriptionCount,
				},
				{
					onAutoCompactChange: (enabled) => {
						this.session.setAutoCompactionEnabled(enabled);
						this.footer.setAutoCompactEnabled(enabled);
					},
					onShowImagesChange: (enabled) => {
						this.settingsManager.setShowImages(enabled);
						for (const child of this.chatContainer.children) {
							if (child instanceof ToolExecutionComponent) {
								child.setShowImages(enabled);
							}
						}
					},
					onImageWidthCellsChange: (width) => {
						this.settingsManager.setImageWidthCells(width);
						for (const child of this.chatContainer.children) {
							if (child instanceof ToolExecutionComponent) {
								child.setImageWidthCells(width);
							}
						}
					},
					onAutoResizeImagesChange: (enabled) => {
						this.settingsManager.setImageAutoResize(enabled);
					},
					onBlockImagesChange: (blocked) => {
						this.settingsManager.setBlockImages(blocked);
					},
					onEnableSkillCommandsChange: (enabled) => {
						this.settingsManager.setEnableSkillCommands(enabled);
						this.setupAutocompleteProvider();
					},
					onSteeringModeChange: (mode) => {
						this.session.setSteeringMode(mode);
					},
					onFollowUpModeChange: (mode) => {
						this.session.setFollowUpMode(mode);
					},
					onTransportChange: (transport) => {
						this.settingsManager.setTransport(transport);
						this.session.agent.transport = transport;
					},
					onHttpIdleTimeoutMsChange: (timeoutMs) => {
						this.settingsManager.setHttpIdleTimeoutMs(timeoutMs);
						configureHttpDispatcher(timeoutMs);
						this.showStatus(`HTTP idle timeout: ${formatHttpIdleTimeoutMs(timeoutMs)}`);
					},
					onThinkingLevelChange: (level) => {
						this.session.setThinkingLevel(level);
						this.footer.invalidate();
						this.updateEditorBorderColor();
					},
					onThemeChange: (themeSetting) => {
						this.settingsManager.setTheme(themeSetting);
						void this.themeController.applyFromSettings();
					},
					onThemePreview: (themeName) => this.themeController.preview(themeName),
					onHideThinkingBlockChange: (hidden) => {
						this.hideThinkingBlock = hidden;
						this.settingsManager.setHideThinkingBlock(hidden);
						for (const child of this.chatContainer.children) {
							if (child instanceof AssistantMessageComponent) {
								child.setHideThinkingBlock(hidden);
							}
						}
						this.chatContainer.clear();
						this.rebuildChatFromMessages();
					},
					onThinkingCollapseChange: (collapse) => {
						this.thinkingCollapse = collapse;
						this.settingsManager.setThinkingCollapse(collapse);
						for (const child of this.chatContainer.children) {
							if (child instanceof AssistantMessageComponent) {
								child.setThinkingCollapse(collapse);
							}
						}
						this.chatContainer.clear();
						this.rebuildChatFromMessages();
					},
					onShowCacheMissNoticesChange: (shown) => {
						this.settingsManager.setShowCacheMissNotices(shown);
						this.rebuildChatFromMessages();
					},
					onCacheRetentionChange: (retention) => {
						this.settingsManager.setCacheRetention(retention);
					},
					onQuietStartupChange: (enabled) => {
						this.settingsManager.setQuietStartup(enabled);
					},
					onSmoothStreamingChange: (enabled) => {
						this.applySmoothStreamingSettingChange(enabled);
					},
					onSessionRetentionDaysChange: (days) => {
						this.settingsManager.setSessionRetentionDays(days);
					},
					onDefaultProjectTrustChange: (defaultProjectTrust) => {
						this.settingsManager.setDefaultProjectTrust(defaultProjectTrust);
					},
					onDoubleEscapeActionChange: (action) => {
						this.settingsManager.setDoubleEscapeAction(action);
					},
					onTreeFilterModeChange: (mode) => {
						this.settingsManager.setTreeFilterMode(mode);
					},
					onShowHardwareCursorChange: (enabled) => {
						this.settingsManager.setShowHardwareCursor(enabled);
						this.ui.setShowHardwareCursor(enabled);
					},
					onEditorPaddingXChange: (padding) => {
						this.settingsManager.setEditorPaddingX(padding);
						this.defaultEditor.setPaddingX(padding);
						if (this.editor !== this.defaultEditor && this.editor.setPaddingX !== undefined) {
							this.editor.setPaddingX(padding);
						}
					},
					onOutputPadChange: (padding) => {
						this.settingsManager.setOutputPad(padding);
						this.outputPad = padding;
						if (this.streamingComponent || this.session.isStreaming) {
							for (const child of this.chatContainer.children) {
								if (child instanceof AssistantMessageComponent || child instanceof UserMessageComponent) {
									child.setOutputPad(padding);
								}
							}
							if (this.streamingComponent) {
								this.streamingComponent.setOutputPad(padding);
							}
							this.ui.requestRender();
							return;
						}
						this.rebuildChatFromMessages();
					},
					onAutocompleteMaxVisibleChange: (maxVisible) => {
						this.settingsManager.setAutocompleteMaxVisible(maxVisible);
						this.defaultEditor.setAutocompleteMaxVisible(maxVisible);
						if (this.editor !== this.defaultEditor && this.editor.setAutocompleteMaxVisible !== undefined) {
							this.editor.setAutocompleteMaxVisible(maxVisible);
						}
					},
					onClearOnShrinkChange: (enabled) => {
						this.settingsManager.setClearOnShrink(enabled);
						this.ui.setClearOnShrink(enabled);
						if (!enabled && !this.activeStatusIndicator) {
							this.statusContainer.clear();
						}
					},
					onShowTerminalProgressChange: (enabled) => {
						this.settingsManager.setShowTerminalProgress(enabled);
					},
					onModelTiersEnabledChange: (enabled) => {
						this.settingsManager.setModelTiersEnabled(enabled);
						getModelTiersBridge()?.refreshToolDescription();
					},
					onModelTierModelChange: (tier, model) => {
						this.settingsManager.setTierModel(tier, model);
					},
					onModelTierThinkingChange: (tier, level) => {
						this.settingsManager.setTierThinking(tier, level);
					},
					getTierThinkingLevels: (tier) => {
						const model = this.resolveModelReference(this.settingsManager.getTierModel(tier));
						if (model) return getSupportedThinkingLevels(model);
						return this.session.getAvailableThinkingLevels();
					},
					onMemoryCharCapChange: (cap) => {
						this.settingsManager.setMemoryCharCap(cap);
					},
					onMemoryEnabledChange: (enabled) => {
						this.settingsManager.setMemoryEnabled(enabled);
						this.session.refreshToolRegistry();
						this.showStatus(`Agent memory: ${enabled ? "on" : "off"}`);
					},
					onSearchCuratorChange: (setting) => {
						if (!setSearchCuratorSetting(setting)) {
							this.showError("pi-web-access is not loaded; curator setting unavailable.");
						}
					},
					// lunr: TUI customize callbacks
					onGutterRailChange: (enabled) => {
						this.settingsManager.setGutterRail(enabled);
					},
					onPromptSymbolChange: (enabled) => {
						this.settingsManager.setPromptSymbol(enabled);
					},
					onFooterMcpChange: (enabled) => {
						this.settingsManager.setFooterMcp(enabled);
					},
					onFooterLspChange: (enabled) => {
						this.settingsManager.setFooterLsp(enabled);
					},
					onFooterContextChange: (enabled) => {
						this.settingsManager.setFooterContext(enabled);
					},
					onFooterTokensChange: (enabled) => {
						this.settingsManager.setFooterTokens(enabled);
					},
					onFooterCacheHitRateChange: (enabled) => {
						this.settingsManager.setFooterCacheHitRate(enabled);
					},
					onFooterTpsChange: (enabled) => {
						this.settingsManager.setFooterTps(enabled);
					},
					onFooterStatusesChange: (enabled) => {
						this.settingsManager.setFooterStatuses(enabled);
					},
					onFooterGitChange: (enabled) => {
						this.settingsManager.setFooterGit(enabled);
					},
					onFooterPlanChange: (enabled) => {
						this.settingsManager.setFooterPlan(enabled);
					},
					onFooterPlanBarChange: (enabled) => {
						this.settingsManager.setFooterPlanBar(enabled);
					},
					onPlanUsageWindowChange: (window) => {
						this.settingsManager.setPlanUsageWindow(window);
					},
					onDefaultPermissionModeChange: (mode) => {
						this.settingsManager.setDefaultPermissionMode(mode);
					},
					onRollbackEnabledChange: (enabled) => {
						this.settingsManager.setRollbackEnabled(enabled);
					},
					onRollbackTurnsChange: (turns) => {
						this.settingsManager.setRollbackTurns(turns);
					},
					onRollbackCaptureChange: (mode) => {
						this.settingsManager.setRollbackCapture(mode);
					},
					onRollbackScopeChange: (scope) => {
						this.settingsManager.setRollbackScope(scope);
					},
					isRollbackSessionForceEnabled: () => isRollbackSessionForceEnabled(this.sessionManager.getSessionId()),
					// lunr: multi-subscription pool callbacks
					onAutoManageSubscriptionsChange: (enabled) => {
						this.settingsManager.setAutoManageSubscriptions(enabled);
					},
					subscriptions: {
						list: () => this.listSubscriptionRows(),
						setActive: (providerId, id) =>
							this.session.modelRuntime.subscriptionManager.setActive(providerId, id),
						rename: (providerId, id, name) =>
							this.session.modelRuntime.subscriptionManager.renameKey(providerId, id, name),
						clearExhaustion: (providerId, id) =>
							this.session.modelRuntime.subscriptionManager.clearExhaustion(providerId, id),
						remove: (providerId, id) => this.session.modelRuntime.subscriptionManager.removeKey(providerId, id),
						autoManage: () => this.settingsManager.getAutoManageSubscriptions(),
						requestRender: () => this.ui.requestRender(),
					},
					createModelTierPicker: (_tier, currentModelRef, done) => {
						const selector = new ModelSelectorComponent(
							this.ui,
							this.resolveModelReference(currentModelRef),
							this.settingsManager,
							this.session.modelRuntime,
							this.session.scopedModels,
							(model) => {
								done(`${model.provider}/${model.id}`);
								this.ui.requestRender();
							},
							() => {
								done();
								this.ui.requestRender();
							},
							undefined,
							{ persistDefault: false },
						);
						return selector;
					},
					onCancel: () => {
						done();
						this.ui.requestRender();
					},
				},
			);
			return { component: selector, focus: selector.getSettingsList() };
		});
	}

	private async handleModelCommand(searchTerm?: string): Promise<void> {
		if (!searchTerm) {
			await this.showModelSelector();
			return;
		}

		const model = await this.findExactModelMatch(searchTerm);
		if (model) {
			try {
				await this.session.setModel(model);
				this.footer.invalidate();
				this.updateEditorBorderColor();
				this.showStatus(`Model: ${model.id}`);
				void this.maybeWarnAboutAnthropicSubscriptionAuth(model);
				this.checkDaxnutsEasterEgg(model);
			} catch (error) {
				this.showError(error instanceof Error ? error.message : String(error));
			}
			return;
		}

		await this.showModelSelector(searchTerm);
	}

	// lunr: /refresh — local provider /models + official GitHub overlay. Never pi.dev.
	// Ollama Cloud still re-registers via its bridge after the core refresh.
	private async handleRefreshCommand(): Promise<void> {
		const runtime = this.session.modelRuntime;
		const allowNetwork = runtime.isNetworkAllowed();
		if (!allowNetwork) {
			this.showStatus("Model network refresh is disabled (offline mode). Reloaded from cache only.");
		}
		let result: Awaited<ReturnType<typeof runtime.refresh>>;
		try {
			result = await runtime.refresh({ allowNetwork, force: true });
		} catch (error) {
			this.showStatus(`Model refresh failed: ${error instanceof Error ? error.message : String(error)}`);
			this.footer.invalidate();
			return;
		}

		const incomplete = result.incomplete;
		const toPrompt = incomplete.slice(0, INCOMPLETE_PROMPT_CAP);
		const extraDefaults = Math.max(0, incomplete.length - toPrompt.length);
		const persisted = [];
		const asked: string[] = [];
		let skipRemaining = false;
		for (const item of toPrompt) {
			if (skipRemaining) {
				persisted.push(modelToUserEntry(item.draft));
				continue;
			}
			const choice = await this.showExtensionSelector(describeIncomplete(item), [
				"Enter details",
				"Use defaults",
				"Skip remaining",
			]);
			asked.push(`${item.provider}/${item.id}`);
			if (choice === "Skip remaining") {
				skipRemaining = true;
				persisted.push(modelToUserEntry(item.draft));
				continue;
			}
			if (choice === "Enter details") {
				const detailed = await this.promptIncompleteModelDetails(item);
				persisted.push(modelToUserEntry(detailed ?? item.draft));
				continue;
			}
			persisted.push(modelToUserEntry(item.draft));
		}
		for (const item of incomplete.slice(toPrompt.length)) {
			persisted.push(modelToUserEntry(item.draft));
		}
		if (persisted.length > 0) runtime.persistUserModels(persisted);

		let ollama: string | undefined;
		const runner = this.session.extensionRunner;
		const cloudRefresh = (globalThis as Record<symbol, unknown>)[Symbol.for("@lunr/ollama-cloud-refresh")] as
			| ((ctx: { ui: unknown }) => Promise<unknown>)
			| undefined;
		if (cloudRefresh && allowNetwork) {
			try {
				await cloudRefresh({ ui: runner.createCommandContext().ui });
			} catch (error) {
				ollama = `ollama-cloud refresh failed: ${error instanceof Error ? error.message : String(error)}`;
			}
		}
		this.session.refreshModelFromRegistry();
		this.showStatus(
			formatCatalogRefreshSummary({
				official: result.official
					? {
							source: result.official.source,
							modelCount: result.official.catalog.models.length,
							evictedUserRows: result.official.evictedUserRows,
						}
					: undefined,
				providers: result.live.map((entry) => ({
					id: entry.providerId,
					status: entry.status,
					added: entry.added,
					total: entry.total,
					error: entry.error,
				})),
				asked,
				extraDefaults,
				ollama,
			}),
		);
		this.footer.invalidate();
	}

	private async promptIncompleteModelDetails(item: IncompleteLiveModel): Promise<Model<any> | undefined> {
		const contextInput = await this.showExtensionInput(
			`${item.provider}/${item.id} — context window:`,
			String(item.draft.contextWindow || 128000),
		);
		if (contextInput === undefined) return undefined;
		const maxTokensInput = await this.showExtensionInput(
			`${item.provider}/${item.id} — max output tokens:`,
			String(item.draft.maxTokens || 8192),
		);
		if (maxTokensInput === undefined) return undefined;
		const inputChoice = await this.showExtensionSelector(`${item.provider}/${item.id} — input:`, [
			"text",
			"text + image",
		]);
		if (inputChoice === undefined) return undefined;
		const reasoningChoice = await this.showExtensionSelector(`${item.provider}/${item.id} — reasoning:`, [
			"yes",
			"no",
		]);
		if (reasoningChoice === undefined) return undefined;
		let api = item.draft.api;
		if (item.availableApis.length > 1) {
			const apiChoice = await this.showExtensionSelector(
				`${item.provider}/${item.id} — API protocol:`,
				item.availableApis.map(String),
			);
			if (apiChoice === undefined) return undefined;
			api = apiChoice;
		}
		return applyIncompleteAnswers(item.draft, {
			contextWindow: parsePositiveInt(contextInput, item.draft.contextWindow),
			maxTokens: parsePositiveInt(maxTokensInput, item.draft.maxTokens),
			input: inputChoice === "text + image" ? ["text", "image"] : ["text"],
			reasoning: reasoningChoice === "yes",
			api,
		});
	}

	private async findExactModelMatch(searchTerm: string): Promise<Model<any> | undefined> {
		const models = await this.getModelCandidates();
		return findExactModelReferenceMatch(searchTerm, models);
	}

	/** Resolve a "provider/model" settings string to a runtime model, if it still exists. */
	private resolveModelReference(reference: string | undefined): Model<any> | undefined {
		if (!reference) return undefined;
		const slashIndex = reference.indexOf("/");
		if (slashIndex <= 0 || slashIndex === reference.length - 1) return undefined;
		return this.session.modelRuntime.getModel(reference.slice(0, slashIndex), reference.slice(slashIndex + 1));
	}

	private async getModelCandidates(): Promise<Model<any>[]> {
		if (this.session.scopedModels.length > 0) {
			return this.session.scopedModels.map((scoped) => scoped.model);
		}

		try {
			return [...(await refreshModelCandidatesForInit(this.session.modelRuntime))];
		} catch {
			return [];
		}
	}

	/** Update the footer's available provider count from current model candidates */
	private async updateAvailableProviderCount(): Promise<void> {
		const models = await this.getModelCandidates();
		const uniqueProviders = new Set(models.map((m) => m.provider));
		this.footerDataProvider.setAvailableProviderCount(uniqueProviders.size);
	}

	private async maybeWarnAboutAnthropicSubscriptionAuth(
		model: Model<any> | undefined = this.session.model,
	): Promise<void> {
		if (this.anthropicSubscriptionWarningShown) {
			return;
		}
		if (!model || model.provider !== "anthropic") {
			return;
		}

		try {
			if ((await this.session.modelRuntime.checkAuth("anthropic"))?.type === "oauth") {
				this.anthropicSubscriptionWarningShown = true;
				this.showWarning(ANTHROPIC_SUBSCRIPTION_AUTH_WARNING);
				return;
			}
			const apiKey = (await this.session.modelRuntime.getAuth(model.provider))?.auth.apiKey;
			if (!isAnthropicSubscriptionAuthKey(apiKey)) {
				return;
			}
			this.anthropicSubscriptionWarningShown = true;
			this.showWarning(ANTHROPIC_SUBSCRIPTION_AUTH_WARNING);
		} catch {
			// Ignore auth lookup failures for warning-only checks.
		}
	}

	private maybeSaveImplicitProjectTrustAfterReload(): boolean {
		const cwd = this.sessionManager.getCwd();
		if (this.autoTrustOnReloadCwd !== cwd) {
			return false;
		}
		if (!this.settingsManager.isProjectTrusted() || !hasTrustRequiringProjectResources(cwd)) {
			return false;
		}

		const trustStore = new ProjectTrustStore(this.runtimeHost.services.agentDir);
		try {
			if (trustStore.get(cwd) !== null) {
				this.autoTrustOnReloadCwd = undefined;
				return false;
			}
			trustStore.set(cwd, true);
			this.autoTrustOnReloadCwd = undefined;
			return true;
		} catch (error) {
			this.showWarning(
				`Could not save project trust after reload: ${error instanceof Error ? error.message : String(error)}`,
			);
			return false;
		}
	}

	private showTrustSelector(): void {
		const cwd = this.sessionManager.getCwd();
		const trustStore = new ProjectTrustStore(this.runtimeHost.services.agentDir);
		const savedDecision = trustStore.getEntry(cwd);
		this.showSelector((done) => {
			const selector = new TrustSelectorComponent({
				cwd,
				savedDecision,
				projectTrusted: this.settingsManager.isProjectTrusted(),
				onSelect: (selection) => {
					trustStore.setMany(selection.updates);
					done();
					this.showStatus(
						`Saved trust decision: ${selection.trusted ? "trusted" : "untrusted"}. Restart pi for this to take effect.`,
					);
				},
				onCancel: () => {
					done();
					this.ui.requestRender();
				},
			});
			return { component: selector, focus: selector };
		});
	}

	private async showModelSelector(initialSearchInput?: string): Promise<void> {
		// lunr: prefetch active subscription names so row rendering stays synchronous.
		const subscriptionNames = await this.getActiveSubscriptionNames();
		this.showSelector((done) => {
			const selector = new ModelSelectorComponent(
				this.ui,
				this.session.model,
				this.settingsManager,
				this.session.modelRuntime,
				this.session.scopedModels,
				async (model) => {
					try {
						await this.session.setModel(model);
						this.footer.invalidate();
						this.updateEditorBorderColor();
						done();
						this.showStatus(`Model: ${model.id}`);
						void this.maybeWarnAboutAnthropicSubscriptionAuth(model);
						this.checkDaxnutsEasterEgg(model);
						// lunr: offer manual subscription switching for pooled providers.
						await this.maybeShowSubscriptionPicker(model.provider);
					} catch (error) {
						done();
						this.showError(error instanceof Error ? error.message : String(error));
					}
				},
				() => {
					done();
					this.ui.requestRender();
				},
				initialSearchInput,
				{ subscriptionNames },
			);
			return { component: selector, focus: selector };
		});
	}

	// lunr: providerId → active subscription name for providers with a multi-key pool.
	// Empty when auto-manage subscriptions is on (no manual switching UI then).
	private async getActiveSubscriptionNames(): Promise<ReadonlyMap<string, string>> {
		const names = new Map<string, string>();
		if (this.settingsManager.getAutoManageSubscriptions()) return names;
		try {
			const manager = this.session.modelRuntime.subscriptionManager;
			for (const { providerId, type } of await this.session.modelRuntime.listCredentials()) {
				if (type !== "api_key") continue;
				const subs = await manager.list(providerId);
				if (subs.length < 2) continue;
				const active = await manager.getActive(providerId);
				if (active) names.set(providerId, active.name);
			}
		} catch {
			// Pool read failures must not break the model selector.
		}
		return names;
	}

	// lunr: after a model pick, offer manual subscription switching for pooled providers.
	// Skipped when auto-manage is on; the model selection stands either way.
	private async maybeShowSubscriptionPicker(providerId: string): Promise<void> {
		if (this.settingsManager.getAutoManageSubscriptions()) return;
		const manager = this.session.modelRuntime.subscriptionManager;
		const subs = await manager.list(providerId);
		if (subs.length < 2) return;
		const activeId = (await manager.getActive(providerId))?.id;
		const labels = subs.map((sub) => this.formatSubscriptionPickerLabel(sub, sub.id === activeId));
		this.showSelector((done) => {
			const selector = new ExtensionSelectorComponent(
				`Select active subscription for ${providerId}:`,
				labels,
				(option) => {
					done();
					const sub = subs[labels.indexOf(option)];
					if (!sub || sub.id === activeId) {
						this.ui.requestRender();
						return;
					}
					manager.setActive(providerId, sub.id).then(
						() => this.showStatus(`Subscription: ${sub.name} (${providerId})`),
						(error: unknown) =>
							this.showError(
								`Failed to switch subscription: ${error instanceof Error ? error.message : String(error)}`,
							),
					);
				},
				() => {
					done();
					this.ui.requestRender();
				},
			);
			return { component: selector, focus: selector };
		});
	}

	// lunr: picker row label — "work (active)", "Sub 3 · exhausted until 14:30".
	private formatSubscriptionPickerLabel(sub: SubEntry, isActive: boolean): string {
		let label = sub.name;
		if (isActive) label += " (active)";
		if (sub.exhaustedUntil !== undefined) {
			label += ` · exhausted until ${formatSubscriptionExhaustedUntil(sub.exhaustedUntil)}`;
		}
		return label;
	}

	// lunr: total pool size across providers, for the /settings Subscriptions row label.
	private async countSubscriptions(): Promise<number> {
		try {
			let count = 0;
			for (const { providerId, type } of await this.session.modelRuntime.listCredentials()) {
				if (type !== "api_key") continue;
				count += (await this.session.modelRuntime.subscriptionManager.list(providerId)).length;
			}
			return count;
		} catch {
			return 0;
		}
	}

	// lunr: plain-data rows for the /settings Subscriptions submenu (manager stays in core).
	private async listSubscriptionRows(): Promise<SubscriptionRowInfo[]> {
		const rows: SubscriptionRowInfo[] = [];
		const manager = this.session.modelRuntime.subscriptionManager;
		for (const { providerId, type } of await this.session.modelRuntime.listCredentials()) {
			if (type !== "api_key") continue;
			const providerName = this.session.modelRuntime.getProvider(providerId)?.name ?? providerId;
			const subs = await manager.list(providerId);
			const activeId = (await manager.getActive(providerId))?.id;
			for (const sub of subs) {
				rows.push({
					providerId,
					providerName,
					id: sub.id,
					name: sub.name,
					active: sub.id === activeId,
					...(sub.exhaustedUntil !== undefined ? { exhaustedUntil: sub.exhaustedUntil } : {}),
				});
			}
		}
		return rows;
	}

	private async showModelsSelector(): Promise<void> {
		// Get all available models
		await this.session.modelRuntime.refresh();
		const allModels = [...(await this.session.modelRuntime.getAvailable())];

		if (allModels.length === 0) {
			this.showStatus("No models available");
			return;
		}

		// Check if session has scoped models (from previous session-only changes or CLI --models)
		const sessionScopedModels = this.session.scopedModels;
		const hasSessionScope = sessionScopedModels.length > 0;

		// Build enabled model IDs from session state or settings
		let currentEnabledIds: string[] | null = null;

		if (hasSessionScope) {
			// Use current session's scoped models
			currentEnabledIds = sessionScopedModels.map((scoped) => `${scoped.model.provider}/${scoped.model.id}`);
		} else {
			// Fall back to settings
			const patterns = this.settingsManager.getEnabledModels();
			if (patterns !== undefined && patterns.length > 0) {
				const scopedModels = await resolveModelScope(patterns, this.session.modelRuntime);
				currentEnabledIds = scopedModels.map((scoped) => `${scoped.model.provider}/${scoped.model.id}`);
			}
		}

		// Helper to update session's scoped models (session-only, no persist)
		const updateSessionModels = async (enabledIds: string[] | null) => {
			currentEnabledIds = enabledIds === null ? null : [...enabledIds];
			if (enabledIds && enabledIds.length > 0 && enabledIds.length < allModels.length) {
				const newScopedModels = await resolveModelScope(enabledIds, this.session.modelRuntime);
				this.session.setScopedModels(
					newScopedModels.map((sm) => ({
						model: sm.model,
						thinkingLevel: sm.thinkingLevel,
					})),
				);
			} else {
				// All enabled or none enabled = no filter
				this.session.setScopedModels([]);
			}
			await this.updateAvailableProviderCount();
			this.ui.requestRender();
		};

		this.showSelector((done) => {
			const selector = new ScopedModelsSelectorComponent(
				{
					allModels,
					enabledModelIds: currentEnabledIds,
				},
				{
					onChange: async (enabledIds) => {
						await updateSessionModels(enabledIds);
					},
					onPersist: (enabledIds) => {
						// Persist to settings
						const newPatterns =
							enabledIds === null || enabledIds.length === allModels.length
								? undefined // All enabled = clear filter
								: enabledIds;
						this.settingsManager.setEnabledModels(newPatterns ? [...newPatterns] : undefined);
						this.showStatus("Model selection saved to settings");
					},
					onCancel: () => {
						done();
						this.ui.requestRender();
					},
				},
			);
			return { component: selector, focus: selector };
		});
	}

	private showUserMessageSelector(): void {
		const userMessages = this.session.getUserMessagesForForking();

		if (userMessages.length === 0) {
			this.showStatus("No messages to fork from");
			return;
		}

		const initialSelectedId = userMessages[userMessages.length - 1]?.entryId;

		this.showSelector((done) => {
			const selector = new UserMessageSelectorComponent(
				userMessages.map((m) => ({ id: m.entryId, text: m.text })),
				async (entryId) => {
					done();
					try {
						const result = await this.runtimeHost.fork(entryId);
						if (result.cancelled) {
							this.ui.requestRender();
							return;
						}

						this.editor.setText(result.selectedText ?? "");
						this.showStatus("Forked to new session");
					} catch (error: unknown) {
						this.showError(error instanceof Error ? error.message : String(error));
					}
				},
				() => {
					done();
					this.ui.requestRender();
				},
				initialSelectedId,
			);
			return { component: selector, focus: selector.getMessageList() };
		});
	}

	private async handleCloneCommand(): Promise<void> {
		const leafId = this.sessionManager.getLeafId();
		if (!leafId) {
			this.showStatus("Nothing to clone yet");
			return;
		}

		try {
			const result = await this.runtimeHost.fork(leafId, { position: "at" });
			if (result.cancelled) {
				this.ui.requestRender();
				return;
			}

			this.editor.setText("");
			this.showStatus("Cloned to new session");
		} catch (error: unknown) {
			this.showError(error instanceof Error ? error.message : String(error));
		}
	}

	private showTreeSelector(initialSelectedId?: string): void {
		const tree = this.sessionManager.getTree();
		const realLeafId = this.sessionManager.getLeafId();
		const initialFilterMode = this.settingsManager.getTreeFilterMode();

		if (tree.length === 0) {
			this.showStatus("No entries in session");
			return;
		}

		this.showSelector((done) => {
			const selector = new TreeSelectorComponent(
				tree,
				realLeafId,
				this.ui.terminal.rows,
				async (entryId) => {
					// Selecting the current leaf is a no-op (already there)
					if (entryId === realLeafId) {
						done();
						this.showStatus("Already at this point");
						return;
					}

					// Ask about summarization
					done(); // Close selector first

					// Loop until user makes a complete choice or cancels to tree
					let wantsSummary = false;
					let customInstructions: string | undefined;

					// Check if we should skip the prompt (user preference to always default to no summary)
					if (!this.settingsManager.getBranchSummarySkipPrompt()) {
						while (true) {
							const summaryChoice = await this.showExtensionSelector("Summarize branch?", [
								"No summary",
								"Summarize",
								"Summarize with custom prompt",
							]);

							if (summaryChoice === undefined) {
								// User pressed escape - re-show tree selector with same selection
								this.showTreeSelector(entryId);
								return;
							}

							wantsSummary = summaryChoice !== "No summary";

							if (summaryChoice === "Summarize with custom prompt") {
								customInstructions = await this.showExtensionEditor("Custom summarization instructions");
								if (customInstructions === undefined) {
									// User cancelled - loop back to summary selector
									continue;
								}
							}

							// User made a complete choice
							break;
						}
					}

					// Set up escape handler and status indicator if summarizing
					let showingSummaryIndicator = false;
					const originalOnEscape = this.defaultEditor.onEscape;

					if (wantsSummary) {
						this.defaultEditor.onEscape = () => {
							this.session.abortBranchSummary();
						};
						this.chatContainer.addChild(new Spacer(1));
						this.showStatusIndicator(new BranchSummaryStatusIndicator(this.ui));
						showingSummaryIndicator = true;
						this.ui.requestRender();
					}

					try {
						const result = await this.session.navigateTree(entryId, {
							summarize: wantsSummary,
							customInstructions,
						});

						if (result.aborted) {
							// Summarization aborted - re-show tree selector with same selection
							this.showStatus("Branch summarization cancelled");
							this.showTreeSelector(entryId);
							return;
						}
						if (result.cancelled) {
							this.showStatus("Navigation cancelled");
							return;
						}

						// Update UI
						this.chatContainer.clear();
						this.renderInitialMessages();
						if (result.editorText && !this.editor.getText().trim()) {
							this.restoreEditorFromTreeResult(result);
						}
						this.showStatus("Navigated to selected point");
						void this.flushCompactionQueue({ willRetry: false });
					} catch (error) {
						this.showError(error instanceof Error ? error.message : String(error));
					} finally {
						if (showingSummaryIndicator) {
							this.clearStatusIndicator("branchSummary");
						}
						this.defaultEditor.onEscape = originalOnEscape;
					}
				},
				() => {
					done();
					this.ui.requestRender();
				},
				(entryId, label) => {
					this.sessionManager.appendLabelChange(entryId, label);
					this.ui.requestRender();
				},
				initialSelectedId,
				initialFilterMode,
			);
			selector.onCopy = async (text) => {
				if (!text) {
					this.showError("Selected entry has no text to copy");
					return;
				}
				try {
					await copyToClipboard(text);
					this.showStatus("Copied selected message to clipboard");
				} catch (error) {
					this.showError(error instanceof Error ? error.message : String(error));
				}
			};
			return { component: selector, focus: selector };
		});
	}

	private showSessionSelector(): void {
		this.showSelector((done) => {
			const selector = new SessionSelectorComponent(
				(onProgress) =>
					SessionManager.list(this.sessionManager.getCwd(), this.sessionManager.getSessionDir(), onProgress),
				(onProgress) =>
					this.sessionManager.usesDefaultSessionDir()
						? SessionManager.listAll(onProgress)
						: SessionManager.listAll(this.sessionManager.getSessionDir(), onProgress),
				async (sessionPath) => {
					done();
					await this.handleResumeSession(sessionPath);
				},
				() => {
					done();
					this.ui.requestRender();
				},
				() => {
					void this.shutdown();
				},
				() => this.ui.requestRender(),
				{
					renameSession: async (sessionFilePath: string, nextName: string | undefined) => {
						const next = (nextName ?? "").trim();
						if (!next) return;
						const mgr = SessionManager.open(sessionFilePath);
						mgr.appendSessionInfo(next);
					},
					showRenameHint: true,
					keybindings: this.keybindings,
				},

				this.sessionManager.getSessionFile(),
			);
			return { component: selector, focus: selector };
		});
	}

	private async handleResumeSession(
		sessionPath: string,
		options?: Parameters<ExtensionCommandContext["switchSession"]>[1],
	): Promise<{ cancelled: boolean }> {
		this.clearStatusIndicator();
		try {
			const result = await this.runtimeHost.switchSession(sessionPath, {
				withSession: options?.withSession,
				projectTrustContextFactory: (cwd) => this.createProjectTrustContext(cwd),
			});
			if (result.cancelled) {
				return result;
			}
			this.showStatus("Resumed session");
			return result;
		} catch (error: unknown) {
			if (error instanceof MissingSessionCwdError) {
				const selectedCwd = await this.promptForMissingSessionCwd(error);
				if (!selectedCwd) {
					this.showStatus("Resume cancelled");
					return { cancelled: true };
				}
				const result = await this.runtimeHost.switchSession(sessionPath, {
					cwdOverride: selectedCwd,
					withSession: options?.withSession,
					projectTrustContextFactory: (cwd) => this.createProjectTrustContext(cwd),
				});
				if (result.cancelled) {
					return result;
				}
				this.showStatus("Resumed session in current cwd");
				return result;
			}
			return this.handleFatalRuntimeError("Failed to resume session", error);
		}
	}

	private getLoginProviderOptions(authType?: "oauth" | "api_key"): AuthSelectorProvider[] {
		const options: AuthSelectorProvider[] = [];
		for (const provider of this.session.modelRuntime.getProviders()) {
			const authStatus = this.session.modelRuntime.getProviderAuthStatus(provider.id);
			const status = authStatus.configured
				? {
						type: this.session.modelRuntime.isUsingOAuth(provider.id) ? ("oauth" as const) : ("api_key" as const),
						source: authStatus.label ?? authStatus.source,
					}
				: undefined;
			if ((!authType || authType === "oauth") && provider.auth.oauth) {
				options.push({
					id: provider.id,
					name: provider.name,
					authType: "oauth",
					method: provider.auth.oauth,
					status,
				});
			}
			if ((!authType || authType === "api_key") && provider.auth.apiKey) {
				options.push({
					id: provider.id,
					name: provider.name,
					authType: "api_key",
					method: provider.auth.apiKey,
					status,
				});
			}
		}
		return options.sort((a, b) => a.name.localeCompare(b.name));
	}

	private async getLogoutProviderOptions(): Promise<AuthSelectorProvider[]> {
		// lunr: providers with a multi-key pool list one entry per subscription plus an
		// "all subscriptions" entry, encoding the target as "<providerId>#<subId|all>".
		const options: AuthSelectorProvider[] = [];
		for (const { providerId, type } of await this.session.modelRuntime.listCredentials()) {
			const name = this.session.modelRuntime.getProvider(providerId)?.name ?? providerId;
			const status = { type, source: "stored credential" };
			if (type === "api_key") {
				const subs = await this.session.modelRuntime.subscriptionManager.list(providerId);
				if (subs.length > 1) {
					for (const sub of subs) {
						options.push({
							id: `${providerId}#${sub.id}`,
							name: `${name} — ${sub.name}`,
							authType: type,
							status,
						});
					}
					options.push({ id: `${providerId}#all`, name: `${name} — all subscriptions`, authType: type, status });
					continue;
				}
			}
			options.push({ id: providerId, name, authType: type, status });
		}
		return options.sort((a, b) => a.name.localeCompare(b.name));
	}

	// lunr: remove every pool key for a provider; removing the last key also deletes
	// the auth.json credential, so the modelRuntime.logout afterwards is a no-op delete.
	private async removeAllSubscriptions(providerId: string): Promise<void> {
		const manager = this.session.modelRuntime.subscriptionManager;
		const subs = await manager.list(providerId);
		for (const sub of subs) {
			await manager.removeKey(providerId, sub.id);
		}
	}

	private findLoginProviderOptions(providerRef: string): AuthSelectorProvider[] {
		const normalizedProviderRef = providerRef.trim().toLowerCase();
		if (!normalizedProviderRef) {
			return [];
		}

		return this.getLoginProviderOptions().filter(
			(provider) =>
				provider.id.toLowerCase() === normalizedProviderRef ||
				provider.name.toLowerCase() === normalizedProviderRef,
		);
	}

	private async handleLoginCommand(providerRef?: string): Promise<void> {
		await this.session.modelRuntime.getAvailable();
		if (!providerRef) {
			this.showLoginAuthTypeSelector();
			return;
		}

		const providerOptions = this.findLoginProviderOptions(providerRef);
		if (providerOptions.length === 1) {
			await this.startProviderLogin(providerOptions[0]!);
			return;
		}

		if (providerOptions.length > 1) {
			const providerIds = new Set(providerOptions.map((provider) => provider.id));
			if (providerIds.size === 1) {
				this.showLoginAuthTypeSelector(providerOptions);
				return;
			}
		}

		this.showLoginProviderSelector(undefined, providerRef);
	}

	private async startProviderLogin(providerOption: AuthSelectorProvider): Promise<void> {
		// lunr: gate Anthropic OAuth (subscription) login with a ToS disclaimer.
		if (providerOption.id === "anthropic" && providerOption.authType === "oauth") {
			const confirmed = await this.confirmDisclaimer("Anthropic Terms of Service", ANTHROPIC_TOS_DISCLAIMER);
			if (!confirmed) {
				this.showStatus("Login cancelled.");
				return;
			}
		}
		if (providerOption.authType === "oauth") {
			if (providerOption.id === "xai" && hasGrokCliXaiAuth()) {
				const choice = await this.showExtensionSelector("xAI SuperGrok is already signed in via Grok CLI.", [
					"Use existing Grok CLI login",
					"Sign in again",
				]);
				if (choice === undefined) return;
				if (choice === "Use existing Grok CLI login") {
					try {
						const imported = await this.session.modelRuntime.importGrokCliXaiAuth();
						if (!imported) {
							this.showError("Could not read the Grok CLI SuperGrok session. Run /login xai again.");
							return;
						}
						await this.completeProviderAuthentication(
							providerOption.id,
							providerOption.name,
							"oauth",
							this.session.model,
							{ actionLabel: "Imported Grok CLI login", savedNote: "Using ~/.grok/auth.json" },
						);
					} catch (error: unknown) {
						this.showError(
							`Failed to import Grok CLI login: ${error instanceof Error ? error.message : String(error)}`,
						);
					}
					return;
				}
			}
			await this.showLoginDialog(providerOption.id, providerOption.name);
		} else if (providerOption.method?.login) {
			await this.showApiKeyLoginDialog(providerOption.id, providerOption.name);
		} else {
			this.showAmbientAuthDialog(providerOption);
		}
	}

	/** lunr: show a Yes/No disclaimer dialog using the existing selector pattern. */
	private confirmDisclaimer(title: string, message: string): Promise<boolean> {
		return new Promise<boolean>((resolve) => {
			this.showSelector((done) => {
				const selector = new ExtensionSelectorComponent(
					title,
					["Continue", "Cancel"],
					(option) => {
						done();
						resolve(option === "Continue");
					},
					() => {
						done();
						resolve(false);
					},
					// lunr: default to Cancel (index 1) for safety; show the disclaimer as message text.
					{ message },
				);
				selector.setSelectedIndex(1);
				return { component: selector, focus: selector };
			});
		});
	}

	/** lunr: like confirmDisclaimer but with custom option labels. */
	private confirmDisclaimerWithLabels(
		title: string,
		message: string,
		opts?: { labels?: [string, string]; defaultIndex?: number },
	): Promise<boolean> {
		const labels = opts?.labels ?? (["Kill all", "Leave running"] as [string, string]);
		const defaultIndex = opts?.defaultIndex ?? 1;
		return new Promise<boolean>((resolve) => {
			this.showSelector((done) => {
				const selector = new ExtensionSelectorComponent(
					title,
					labels,
					(option) => {
						done();
						resolve(option === labels[0]);
					},
					() => {
						done();
						resolve(false);
					},
					{ message },
				);
				selector.setSelectedIndex(defaultIndex);
				return { component: selector, focus: selector };
			});
		});
	}

	private showLoginAuthTypeSelector(providerOptions?: AuthSelectorProvider[]): void {
		const oauthProvider = providerOptions?.find((provider) => provider.authType === "oauth");
		const oauthLoginLabel =
			oauthProvider?.method && "loginLabel" in oauthProvider.method ? oauthProvider.method.loginLabel : undefined;
		const subscriptionLabel = oauthLoginLabel ?? "Sign in with an account";
		const apiKeyLabel = "Sign in with an API key";
		const availableAuthTypes = providerOptions
			? new Set(providerOptions.map((provider) => provider.authType))
			: new Set<AuthSelectorProvider["authType"]>(["oauth", "api_key"]);
		const options: string[] = [];
		if (availableAuthTypes.has("oauth")) {
			options.push(subscriptionLabel);
		}
		if (availableAuthTypes.has("api_key")) {
			options.push(apiKeyLabel);
		}
		// lunr: custom-endpoint wizard is only offered from the top-level auth-type menu
		// (no provider chosen yet), not when re-authenticating a specific provider.
		const customProviderLabel = "Connect a custom API endpoint…";
		if (!providerOptions) {
			options.push(customProviderLabel);
		}

		if (options.length === 0) {
			this.showStatus("No login methods available.");
			return;
		}

		if (providerOptions && options.length === 1) {
			const providerOption = providerOptions[0];
			if (providerOption) {
				void this.startProviderLogin(providerOption);
			}
			return;
		}

		const title = providerOptions?.[0]
			? `Select authentication method for ${providerOptions[0].name}:`
			: "Select authentication method:";
		this.showSelector((done) => {
			const selector = new ExtensionSelectorComponent(
				title,
				options,
				(option) => {
					done();
					// lunr: intercept before the oauth/api_key label mapping below.
					if (option === customProviderLabel) {
						void this.showCustomProviderWizard();
						return;
					}
					const authType = option === subscriptionLabel ? "oauth" : "api_key";
					if (providerOptions) {
						const providerOption = providerOptions.find((provider) => provider.authType === authType);
						if (providerOption) {
							void this.startProviderLogin(providerOption);
						}
						return;
					}
					this.showLoginProviderSelector(authType);
				},
				() => {
					done();
					this.ui.requestRender();
				},
			);
			return { component: selector, focus: selector };
		});
	}

	private showLoginProviderSelector(authType?: AuthSelectorProvider["authType"], initialSearchInput?: string): void {
		const providerOptions = this.getLoginProviderOptions(authType);
		if (providerOptions.length === 0) {
			const message =
				authType === "oauth"
					? "No subscription providers available."
					: authType === "api_key"
						? "No API key providers available."
						: "No login providers available.";
			this.showStatus(message);
			return;
		}

		this.showSelector((done) => {
			const selector = new OAuthSelectorComponent(
				"login",
				providerOptions,
				async (providerId, selectedAuthType) => {
					done();

					const providerOption = providerOptions.find(
						(provider) => provider.id === providerId && provider.authType === selectedAuthType,
					);
					if (!providerOption) {
						return;
					}

					await this.startProviderLogin(providerOption);
				},
				() => {
					done();
					if (authType) {
						this.showLoginAuthTypeSelector();
					} else {
						this.ui.requestRender();
					}
				},
				initialSearchInput,
			);
			return { component: selector, focus: selector };
		});
	}

	// lunr: /login "Connect a custom API endpoint…" wizard. Writes the provider to
	// models.json via upsertCustomProvider, then stores the key through the standard
	// api_key login route (auth.json) once reloadConfig has composed the provider.
	private async showCustomProviderWizard(): Promise<void> {
		const cancelled = () => this.showStatus("Custom provider setup cancelled.");

		const nameInput = await this.showExtensionInput("Custom provider — display name:", "My Provider");
		const name = nameInput?.trim();
		if (!name) {
			cancelled();
			return;
		}
		const existingIds = this.session.modelRuntime.getProviders().map((provider) => provider.id);
		const providerId = slugifyProviderId(name, existingIds);

		const baseUrlInput = await this.showExtensionInput(`Base URL for ${name}:`, "https://api.example.com/v1");
		if (baseUrlInput === undefined) {
			cancelled();
			return;
		}
		const baseUrl = baseUrlInput.trim();
		if (!isValidProviderBaseUrl(baseUrl)) {
			this.showError(`Invalid base URL "${baseUrl}" — it must start with http:// or https://.`);
			return;
		}

		const api = await this.showExtensionSelector(`API protocol for ${name}:`, [
			"openai-completions",
			"openai-responses",
			"anthropic-messages",
			"google-generative-ai",
		]);
		if (!api) {
			cancelled();
			return;
		}

		const modelIdsInput = await this.showExtensionInput(
			`Model IDs exposed by ${name} (comma-separated):`,
			"model-a, model-b",
		);
		if (modelIdsInput === undefined) {
			cancelled();
			return;
		}
		const modelIds = parseModelIds(modelIdsInput);
		if (modelIds.length === 0) {
			this.showError("At least one model ID is required.");
			return;
		}

		const apiKeyInput = await this.showExtensionInput(`API key for ${name}:`);
		const apiKey = apiKeyInput?.trim();
		if (!apiKey) {
			cancelled();
			return;
		}

		const modelsJsonPath = path.join(getAgentDir(), "models.json");
		try {
			await upsertCustomProvider(modelsJsonPath, providerId, {
				name,
				baseUrl,
				api,
				models: modelIds.map((id) => ({ id })),
			});
			await this.session.modelRuntime.reloadConfig();
		} catch (error: unknown) {
			this.showError(`Failed to add custom provider: ${error instanceof Error ? error.message : String(error)}`);
			return;
		}

		try {
			// The composed provider's fabricated apiKey.login persists via credentials.modify
			// (auth.json) — the same storage route showApiKeyLoginDialog uses.
			await this.session.modelRuntime.login(providerId, "api_key", {
				prompt: () => Promise.resolve(apiKey),
				notify: () => {},
			});
		} catch (error: unknown) {
			await this.updateAvailableProviderCount();
			this.showError(
				`Custom provider '${name}' was added to models.json, but saving its API key failed: ${error instanceof Error ? error.message : String(error)}. Use /login to store a key.`,
			);
			return;
		}

		await this.updateAvailableProviderCount();
		this.footer.invalidate();
		this.updateEditorBorderColor();

		const refineNote = `Refine contextWindow/cost by editing ${modelsJsonPath}.`;
		const modelCount = modelIds.length;
		if (modelCount === 1) {
			const modelId = modelIds[0];
			const model = modelId ? this.session.modelRuntime.getModel(providerId, modelId) : undefined;
			if (model) {
				const choice = await this.showExtensionSelector(
					`Custom provider '${name}' added. Select ${model.id} as the active model?`,
					["Select model", "Skip"],
				);
				if (choice === "Select model") {
					try {
						await this.session.setModel(model);
						this.showStatus(`Custom provider '${name}' added (1 model). Selected ${model.id}. ${refineNote}`);
						return;
					} catch (error: unknown) {
						this.showError(
							`Selecting ${model.id} failed: ${error instanceof Error ? error.message : String(error)}. Use /model to select a model.`,
						);
					}
				}
			}
		}
		this.showStatus(
			`Custom provider '${name}' added (${modelCount} model${modelCount === 1 ? "" : "s"}). ${refineNote}`,
		);
	}

	private async showOAuthSelector(mode: "login" | "logout"): Promise<void> {
		if (mode === "login") {
			this.showLoginAuthTypeSelector();
			return;
		}

		const providerOptions = await this.getLogoutProviderOptions();
		if (providerOptions.length === 0) {
			this.showStatus(
				"No stored credentials to remove. /logout only removes credentials saved by /login; environment variables and models.json config are unchanged.",
			);
			return;
		}

		this.showSelector((done) => {
			const selector = new OAuthSelectorComponent(
				mode,
				providerOptions,
				async (providerId: string) => {
					done();

					const providerOption = providerOptions.find((provider) => provider.id === providerId);
					if (!providerOption) {
						return;
					}

					try {
						// lunr: pool-aware logout — "provider#subId" removes one key,
						// "provider#all" and plain entries remove everything.
						const hashIndex = providerOption.id.indexOf("#");
						if (hashIndex > 0) {
							const poolProviderId = providerOption.id.slice(0, hashIndex);
							const subRef = providerOption.id.slice(hashIndex + 1);
							if (subRef !== "all") {
								await this.session.modelRuntime.subscriptionManager.removeKey(poolProviderId, subRef);
								await this.updateAvailableProviderCount();
								this.showStatus(`Removed subscription ${providerOption.name}.`);
								return;
							}
							await this.removeAllSubscriptions(poolProviderId);
							await this.session.modelRuntime.logout(poolProviderId);
							await this.updateAvailableProviderCount();
							const poolProviderName =
								this.session.modelRuntime.getProvider(poolProviderId)?.name ?? poolProviderId;
							this.showStatus(`Removed all subscriptions for ${poolProviderName}`);
							return;
						}

						// lunr: a plain entry can still have a lazy-imported pool — clear it so
						// rotation cannot resurrect the removed credential.
						await this.removeAllSubscriptions(providerOption.id);
						await this.session.modelRuntime.logout(providerOption.id);
						await this.updateAvailableProviderCount();
						const message =
							providerOption.authType === "oauth"
								? `Logged out of ${providerOption.name}`
								: `Removed stored API key for ${providerOption.name}. Environment variables and models.json config are unchanged.`;
						this.showStatus(message);
					} catch (error: unknown) {
						this.showError(`Logout failed: ${error instanceof Error ? error.message : String(error)}`);
					}
				},
				() => {
					done();
					this.ui.requestRender();
				},
			);
			return { component: selector, focus: selector };
		});
	}

	private async completeProviderAuthentication(
		providerId: string,
		providerName: string,
		authType: "oauth" | "api_key",
		previousModel: Model<any> | undefined,
		// lunr: lets the subscription add-flow adjust the status wording.
		overrides?: { actionLabel?: string; savedNote?: string },
	): Promise<void> {
		await this.session.modelRuntime.getAvailable();

		const actionLabel =
			overrides?.actionLabel ??
			(authType === "oauth" ? `Logged in to ${providerName}` : `Saved API key for ${providerName}`);
		const savedNote = overrides?.savedNote ?? `Credentials saved to ${getAuthPath()}`;

		let selectedModel: Model<any> | undefined;
		let selectionError: string | undefined;
		if (isUnknownModel(previousModel)) {
			const availableModels = await this.session.modelRuntime.getAvailable();
			const providerModels = availableModels.filter((model) => model.provider === providerId);
			if (!hasDefaultModelProvider(providerId)) {
				selectionError = `${actionLabel}, but no default model is configured for provider "${providerId}". Use /model to select a model.`;
			} else if (providerModels.length === 0) {
				selectionError = `${actionLabel}, but no models are available for that provider. Use /model to select a model.`;
			} else {
				const defaultModelId = defaultModelPerProvider[providerId];
				selectedModel = providerModels.find((model) => model.id === defaultModelId);
				if (!selectedModel) {
					selectionError = `${actionLabel}, but its default model "${defaultModelId}" is not available. Use /model to select a model.`;
				} else {
					try {
						await this.session.setModel(selectedModel);
					} catch (error: unknown) {
						selectedModel = undefined;
						const errorMessage = error instanceof Error ? error.message : String(error);
						selectionError = `${actionLabel}, but selecting its default model failed: ${errorMessage}. Use /model to select a model.`;
					}
				}
			}
		}

		await this.updateAvailableProviderCount();
		this.footer.invalidate();
		this.updateEditorBorderColor();
		if (selectedModel) {
			this.showStatus(`${actionLabel}. Selected ${selectedModel.id}. ${savedNote}`);
			void this.maybeWarnAboutAnthropicSubscriptionAuth(selectedModel);
			this.checkDaxnutsEasterEgg(selectedModel);
		} else {
			this.showStatus(`${actionLabel}. ${savedNote}`);
			if (selectionError) {
				this.showError(selectionError);
			} else {
				void this.maybeWarnAboutAnthropicSubscriptionAuth();
			}
		}
	}

	private showAmbientAuthDialog(providerOption: AuthSelectorProvider): void {
		const restoreEditor = () => {
			this.editorContainer.clear();
			this.editorContainer.addChild(this.editor);
			this.ui.setFocus(this.editor);
			this.ui.requestRender();
		};

		const dialog = new LoginDialogComponent(
			this.ui,
			providerOption.id,
			() => restoreEditor(),
			providerOption.name,
			`${providerOption.name} setup`,
		);
		dialog.showInfo(`${providerOption.method?.name ?? "Authentication"} is configured outside pi.`, [], true);

		this.editorContainer.clear();
		this.editorContainer.addChild(dialog);
		this.ui.setFocus(dialog);
		this.ui.requestRender();
	}

	private async showApiKeyLoginDialog(providerId: string, providerName: string): Promise<void> {
		const previousModel = this.session.model;

		// lunr: multi-subscription pools — when the provider already has stored keys,
		// ask whether the new key joins the pool or replaces it.
		const subscriptionManager = this.session.modelRuntime.subscriptionManager;
		const existingSubs = await subscriptionManager.list(providerId);
		let poolMode: "add" | "replace" | undefined;
		if (existingSubs.length > 0) {
			poolMode = await this.chooseSubscriptionLoginMode(providerName, existingSubs.length);
			if (poolMode === undefined) {
				this.showStatus("Login cancelled.");
				return;
			}
		}

		const dialog = new LoginDialogComponent(
			this.ui,
			providerId,
			(_success, _message) => {
				// Completion handled below
			},
			providerName,
		);

		if (providerId === "amazon-bedrock") {
			dialog.showDetails([
				theme.fg("text", "You can also use an AWS profile, IAM keys, or role-based credentials."),
				theme.fg("muted", "See:"),
				theme.fg("accent", `  ${path.join(getDocsPath(), "providers.md")}`),
			]);
		}

		this.editorContainer.clear();
		this.editorContainer.addChild(dialog);
		this.ui.setFocus(dialog);
		this.ui.requestRender();

		const restoreEditor = () => {
			this.editorContainer.clear();
			this.editorContainer.addChild(this.editor);
			this.ui.setFocus(this.editor);
			this.ui.requestRender();
		};

		try {
			const credential = await this.loginProvider(dialog, providerId, "api_key");
			restoreEditor();
			if (poolMode === "add" && credential.type === "api_key" && credential.key !== undefined) {
				const entry = await subscriptionManager.addKey(providerId, credential.key);
				await this.completeProviderAuthentication(providerId, providerName, "api_key", previousModel, {
					actionLabel: `Added subscription "${entry.name}" for ${providerName}`,
					savedNote: "Key stored in the subscription pool. Rename it in /settings → Subscriptions.",
				});
				return;
			}
			if (poolMode === "replace") {
				await this.replaceSubscriptionPool(providerId, credential);
			}
			await this.completeProviderAuthentication(providerId, providerName, "api_key", previousModel);
		} catch (error: unknown) {
			restoreEditor();
			const errorMsg = error instanceof Error ? error.message : String(error);
			if (errorMsg !== "Login cancelled") {
				this.showError(`Failed to save API key for ${providerName}: ${errorMsg}`);
			}
		}
	}

	// lunr: add-vs-replace picker shown when an API-key provider already has stored keys.
	private chooseSubscriptionLoginMode(providerName: string, keyCount: number): Promise<"add" | "replace" | undefined> {
		return new Promise((resolve) => {
			this.showSelector((done) => {
				const selector = new ExtensionSelectorComponent(
					`${providerName} already has ${keyCount} stored key${keyCount === 1 ? "" : "s"}.`,
					["Add as new subscription", "Replace existing"],
					(option) => {
						done();
						resolve(option === "Add as new subscription" ? "add" : "replace");
					},
					() => {
						done();
						resolve(undefined);
					},
					{
						message:
							"Add keeps the existing keys in a pool you can switch between; Replace overwrites them with the new key.",
					},
				);
				return { component: selector, focus: selector };
			});
		});
	}

	// lunr: "Replace existing" collapses the pool to the single key login just stored,
	// so rotation cannot resurrect replaced keys. Removing the last pool key deletes the
	// auth.json credential, and addKey re-mirrors the same key the login already stored.
	private async replaceSubscriptionPool(providerId: string, credential: Credential): Promise<void> {
		const subscriptionManager = this.session.modelRuntime.subscriptionManager;
		const subs = await subscriptionManager.list(providerId);
		for (const sub of subs) {
			await subscriptionManager.removeKey(providerId, sub.id);
		}
		if (credential.type === "api_key" && credential.key !== undefined) {
			await subscriptionManager.addKey(providerId, credential.key);
		}
	}

	private showAuthSelect(
		dialog: LoginDialogComponent,
		prompt: Extract<AuthPrompt, { type: "select" }>,
	): Promise<string> {
		return new Promise((resolve, reject) => {
			const restoreDialog = () => {
				this.editorContainer.clear();
				this.editorContainer.addChild(dialog);
				this.ui.setFocus(dialog);
				this.ui.requestRender();
			};
			const labels = prompt.options.map((option) => option.label);
			const selector = new ExtensionSelectorComponent(
				prompt.message,
				labels,
				(optionLabel) => {
					restoreDialog();
					const id = prompt.options.find((option) => option.label === optionLabel)?.id;
					if (id) resolve(id);
					else reject(new Error("Login cancelled"));
				},
				() => {
					restoreDialog();
					reject(new Error("Login cancelled"));
				},
			);
			this.editorContainer.clear();
			this.editorContainer.addChild(selector);
			this.ui.setFocus(selector);
			this.ui.requestRender();
		});
	}

	private async showAuthPrompt(dialog: LoginDialogComponent, prompt: AuthPrompt): Promise<string> {
		let response: Promise<string>;
		if (prompt.type === "select") {
			response = this.showAuthSelect(dialog, prompt);
		} else if (prompt.type === "manual_code") {
			response = dialog.showManualInput(prompt.message);
		} else {
			response = dialog.showPrompt(prompt.message, prompt.placeholder);
		}
		if (!prompt.signal) return response;
		if (prompt.signal.aborted) throw new Error("Login cancelled");
		const signal = prompt.signal;
		let onAbort: (() => void) | undefined;
		const aborted = new Promise<string>((_resolve, reject) => {
			onAbort = () => reject(new Error("Login cancelled"));
			signal.addEventListener("abort", onAbort, { once: true });
		});
		try {
			return await Promise.race([response, aborted]);
		} finally {
			if (onAbort) signal.removeEventListener("abort", onAbort);
		}
	}

	private notifyAuthDialog(dialog: LoginDialogComponent, event: AuthEvent): void {
		if (event.type === "auth_url") {
			dialog.showAuth(event.url, event.instructions);
		} else if (event.type === "device_code") {
			dialog.showDeviceCode(event);
			dialog.showWaiting("Waiting for authentication...");
		} else if (event.type === "info") {
			dialog.showInfo(event.message, event.links);
		} else {
			dialog.showProgress(event.message);
		}
	}

	private async loginProvider(
		dialog: LoginDialogComponent,
		providerId: string,
		method: "api_key" | "oauth",
	): Promise<Credential> {
		return this.session.modelRuntime.login(providerId, method, {
			signal: dialog.signal,
			prompt: (prompt) => this.showAuthPrompt(dialog, prompt),
			notify: (event) => this.notifyAuthDialog(dialog, event),
		});
	}

	private async showLoginDialog(providerId: string, providerName: string): Promise<void> {
		const previousModel = this.session.model;
		const dialog = new LoginDialogComponent(this.ui, providerId, (_success, _message) => {}, providerName);
		this.editorContainer.clear();
		this.editorContainer.addChild(dialog);
		this.ui.setFocus(dialog);
		this.ui.requestRender();

		const restoreEditor = () => {
			this.editorContainer.clear();
			this.editorContainer.addChild(this.editor);
			this.ui.setFocus(this.editor);
			this.ui.requestRender();
		};

		try {
			await this.loginProvider(dialog, providerId, "oauth");
			restoreEditor();
			await this.completeProviderAuthentication(providerId, providerName, "oauth", previousModel);
		} catch (error: unknown) {
			restoreEditor();
			const errorMsg = error instanceof Error ? error.message : String(error);
			if (errorMsg !== "Login cancelled") {
				this.showError(`Failed to login to ${providerName}: ${errorMsg}`);
			}
		}
	}

	// =========================================================================
	// Command handlers
	// =========================================================================

	private async handleInitCommand(): Promise<void> {
		if (this.session.isStreaming) {
			this.showWarning("Wait for the current response to finish before running /init.");
			return;
		}

		let prompt = INIT_PROMPT;
		const agentsMdPath = path.join(this.sessionManager.getCwd(), "AGENTS.md");
		if (fs.existsSync(agentsMdPath)) {
			const choice = await this.showExtensionSelector("AGENTS.md already exists in this project.", [
				"Cancel",
				"Overwrite",
				"Append",
			]);
			if (choice === "Overwrite") {
				prompt = `${INIT_PROMPT}\n${INIT_EXISTING_FILE_INSTRUCTIONS.overwrite}`;
			} else if (choice === "Append") {
				prompt = `${INIT_PROMPT}\n${INIT_EXISTING_FILE_INSTRUCTIONS.append}`;
			} else {
				this.showStatus("Init cancelled");
				return;
			}
		}

		await this.sendUserMessageAfterDeferredBuiltins(prompt);
		this.showStatus("AGENTS.md is being generated — run /reload when it finishes to load it into context.");
	}

	private async handleSwarmCommand(task: string): Promise<void> {
		if (task.length === 0) {
			if (this.swarmMode) {
				this.showStatus("Swarm mode is active — run /subagents-fleet to monitor the fleet.");
			} else {
				this.showStatus(
					"Usage: /swarm <task> — decomposes the task across parallel subagents. Monitor with /subagents-fleet.",
				);
			}
			return;
		}
		if (this.session.isStreaming) {
			this.showWarning("Wait for the current response to finish before running /swarm.");
			return;
		}

		this.swarmMode = true;
		this.setExtensionStatus("swarm", "swarm");
		await this.sendUserMessageAfterDeferredBuiltins(buildSwarmPrompt(task));
	}

	// lunr: /undo and /edit rewind the same session via navigateTree (no fork).
	// /undo leaves the editor alone; /edit pastes the undone user text. /redo
	// pops the previous leaf and navigates back.
	private async rewindLastTurn(
		command: "undo" | "edit",
	): Promise<{ editorText?: string; editorImages?: ImageContent[] } | undefined> {
		if (this.session.isStreaming) {
			this.showWarning(`Wait for the current response to finish before running /${command}.`);
			return undefined;
		}

		const nothingLabel = command === "undo" ? "Nothing to undo" : "Nothing to edit";
		const branch = this.sessionManager.getBranch();
		let lastUserIndex = -1;
		for (let i = branch.length - 1; i >= 0; i--) {
			const entry = branch[i];
			if (entry.type === "message" && entry.message.role === "user") {
				lastUserIndex = i;
				break;
			}
		}
		if (lastUserIndex === -1) {
			this.showStatus(nothingLabel);
			return undefined;
		}

		const lastUser = branch[lastUserIndex];
		const leafId = this.sessionManager.getLeafId();
		let targetId: string;
		if (lastUser.id === leafId) {
			if (!lastUser.parentId) {
				this.showStatus(nothingLabel);
				return undefined;
			}
			targetId = lastUser.parentId;
		} else {
			targetId = lastUser.id;
		}

		try {
			const result = await this.session.navigateTree(targetId, {});
			if (result.cancelled) {
				this.showStatus(command === "undo" ? "Undo cancelled" : "Edit cancelled");
				return undefined;
			}
			if (leafId) {
				this.redoStack.push(leafId);
			}
			this.chatContainer.clear();
			this.renderInitialMessages();
			void this.flushCompactionQueue({ willRetry: false });
			return { editorText: result.editorText, editorImages: result.editorImages };
		} catch (error) {
			this.showError(error instanceof Error ? error.message : String(error));
			return undefined;
		}
	}

	private async handleUndoCommand(): Promise<void> {
		const result = await this.rewindLastTurn("undo");
		if (!result) return;
		this.showStatus("Undone. /redo to restore.");
	}

	private async handleEditCommand(): Promise<void> {
		const result = await this.rewindLastTurn("edit");
		if (!result) return;
		this.restoreEditorFromTreeResult({
			editorText: result.editorText ?? "",
			editorImages: result.editorImages,
		});
		this.showStatus("Editing last message. /redo to restore.");
	}

	private async handleRedoCommand(): Promise<void> {
		if (this.session.isStreaming) {
			this.showWarning("Wait for the current response to finish before running /redo.");
			return;
		}

		const targetId = this.redoStack.pop();
		if (!targetId) {
			this.showStatus("Nothing to redo");
			return;
		}

		try {
			const result = await this.session.navigateTree(targetId, {});
			if (result.cancelled) {
				this.redoStack.push(targetId);
				this.showStatus("Navigation cancelled");
				return;
			}
			this.chatContainer.clear();
			this.renderInitialMessages();
			if (result.editorText && !this.editor.getText().trim()) {
				this.restoreEditorFromTreeResult(result);
			}
			this.showStatus("Redone");
			void this.flushCompactionQueue({ willRetry: false });
		} catch (error) {
			this.showError(error instanceof Error ? error.message : String(error));
		}
	}

	private async handleReloadCommand(): Promise<void> {
		if (this.session.isStreaming) {
			this.showWarning("Wait for the current response to finish before reloading.");
			return;
		}
		if (this.session.isCompacting) {
			this.showWarning("Wait for compaction to finish before reloading.");
			return;
		}

		this.resetExtensionUI();

		const reloadBox = new Container();
		const borderColor = (s: string) => theme.fg("border", s);
		reloadBox.addChild(new DynamicBorder(borderColor));
		reloadBox.addChild(new Spacer(1));
		reloadBox.addChild(
			new Text(
				theme.fg("muted", "Reloading keybindings, extensions, skills, prompts, themes, and context files..."),
				1,
				0,
			),
		);
		reloadBox.addChild(new Spacer(1));
		reloadBox.addChild(new DynamicBorder(borderColor));

		const previousEditor = this.editor;
		this.editorContainer.clear();
		this.editorContainer.addChild(reloadBox);
		this.ui.setFocus(reloadBox);
		this.ui.requestRender(true);
		await new Promise((resolve) => process.nextTick(resolve));

		const dismissReloadBox = (editor: Component) => {
			this.editorContainer.clear();
			this.editorContainer.addChild(editor);
			this.ui.setFocus(editor);
			this.ui.requestRender();
		};

		let chatRestoredBeforeSessionStart = false;
		let reloadBoxDismissed = false;
		const restoreChatBeforeSessionStart = () => {
			if (chatRestoredBeforeSessionStart) {
				return;
			}
			this.hideThinkingBlock = this.settingsManager.getHideThinkingBlock();
			this.thinkingCollapse = this.settingsManager.getThinkingCollapse();
			this.outputPad = this.settingsManager.getOutputPad();
			this.rebuildChatFromMessages();
			chatRestoredBeforeSessionStart = true;
		};

		try {
			await this.session.reload({ beforeSessionStart: restoreChatBeforeSessionStart });
			restoreChatBeforeSessionStart();
			configureHttpDispatcher(this.settingsManager.getHttpIdleTimeoutMs());
			this.keybindings.reload();
			const activeHeader = this.customHeader ?? this.builtInHeader;
			if (isExpandable(activeHeader)) {
				activeHeader.setExpanded(this.toolOutputExpanded);
			}
			setRegisteredThemes(this.session.resourceLoader.getThemes().themes);
			await this.themeController.applyFromSettings();
			const editorPaddingX = this.settingsManager.getEditorPaddingX();
			const autocompleteMaxVisible = this.settingsManager.getAutocompleteMaxVisible();
			this.defaultEditor.setPaddingX(editorPaddingX);
			this.defaultEditor.setAutocompleteMaxVisible(autocompleteMaxVisible);
			if (this.editor !== this.defaultEditor) {
				this.editor.setPaddingX?.(editorPaddingX);
				this.editor.setAutocompleteMaxVisible?.(autocompleteMaxVisible);
			}
			this.ui.setShowHardwareCursor(this.settingsManager.getShowHardwareCursor());
			const clearOnShrink = this.settingsManager.getClearOnShrink();
			this.ui.setClearOnShrink(clearOnShrink);
			if (!clearOnShrink && !this.activeStatusIndicator) {
				this.statusContainer.clear();
			}
			this.setupAutocompleteProvider();
			const runner = this.session.extensionRunner;
			this.setupExtensionShortcuts(runner);
			this.showLoadedResources({
				force: false,
				showDiagnosticsWhenQuiet: true,
			});
			const savedImplicitProjectTrust = this.maybeSaveImplicitProjectTrustAfterReload();
			const modelsJsonError = this.session.modelRuntime.getError();
			if (modelsJsonError) {
				this.showError(`models.json error: ${modelsJsonError}`);
			}
			this.showStatus(
				savedImplicitProjectTrust
					? "Reloaded keybindings, extensions, skills, prompts, themes, and context files; saved project trust"
					: "Reloaded keybindings, extensions, skills, prompts, themes, and context files",
			);
			dismissReloadBox(this.editor as Component);
			reloadBoxDismissed = true;
		} catch (error) {
			if (!reloadBoxDismissed) {
				dismissReloadBox(previousEditor as Component);
			}
			this.showError(`Reload failed: ${error instanceof Error ? error.message : String(error)}`);
		}
	}

	private async handleExportCommand(text: string): Promise<void> {
		const outputPath = this.getPathCommandArgument(text, "/export");

		try {
			if (outputPath?.endsWith(".jsonl")) {
				const filePath = this.session.exportToJsonl(outputPath);
				this.showStatus(`Session exported to: ${filePath}`);
			} else {
				const filePath = await this.session.exportToHtml(outputPath);
				this.showStatus(`Session exported to: ${filePath}`);
			}
		} catch (error: unknown) {
			this.showError(`Failed to export session: ${error instanceof Error ? error.message : "Unknown error"}`);
		}
	}

	private getPathCommandArgument(text: string, command: "/export" | "/import"): string | undefined {
		if (text === command) {
			return undefined;
		}
		if (!text.startsWith(`${command} `)) {
			return undefined;
		}

		const argsString = text.slice(command.length + 1).trimStart();
		if (!argsString) {
			return undefined;
		}

		const firstChar = argsString[0];
		if (firstChar === '"' || firstChar === "'") {
			const closingQuoteIndex = argsString.indexOf(firstChar, 1);
			if (closingQuoteIndex < 0) {
				return undefined;
			}
			return argsString.slice(1, closingQuoteIndex);
		}

		const firstWhitespaceIndex = argsString.search(/\s/);
		if (firstWhitespaceIndex < 0) {
			return argsString;
		}
		return argsString.slice(0, firstWhitespaceIndex);
	}

	private async handleImportCommand(text: string): Promise<void> {
		const inputPath = this.getPathCommandArgument(text, "/import");
		if (!inputPath) {
			this.showError("Usage: /import <path.jsonl>");
			return;
		}

		const confirmed = await this.showExtensionConfirm("Import session", `Replace current session with ${inputPath}?`);
		if (!confirmed) {
			this.showStatus("Import cancelled");
			return;
		}

		try {
			this.clearStatusIndicator();
			const result = await this.runtimeHost.importFromJsonl(inputPath);
			if (result.cancelled) {
				this.showStatus("Import cancelled");
				return;
			}
			this.showStatus(`Session imported from: ${inputPath}`);
		} catch (error: unknown) {
			if (error instanceof MissingSessionCwdError) {
				const selectedCwd = await this.promptForMissingSessionCwd(error);
				if (!selectedCwd) {
					this.showStatus("Import cancelled");
					return;
				}
				const result = await this.runtimeHost.importFromJsonl(inputPath, selectedCwd);
				if (result.cancelled) {
					this.showStatus("Import cancelled");
					return;
				}
				this.showStatus(`Session imported from: ${inputPath}`);
				return;
			}
			if (error instanceof SessionImportFileNotFoundError) {
				this.showError(`Failed to import session: ${error.message}`);
				return;
			}
			await this.handleFatalRuntimeError("Failed to import session", error);
		}
	}

	private async handleShareCommand(): Promise<void> {
		// Check if gh is available and logged in
		try {
			const authResult = spawnSync("gh", ["auth", "status"], { encoding: "utf-8" });
			if (authResult.status !== 0) {
				this.showError("GitHub CLI is not logged in. Run 'gh auth login' first.");
				return;
			}
		} catch {
			this.showError("GitHub CLI (gh) is not installed. Install it from https://cli.github.com/");
			return;
		}

		// Export to a temp file
		const tmpFile = path.join(os.tmpdir(), "session.html");
		try {
			await this.session.exportToHtml(tmpFile);
		} catch (error: unknown) {
			this.showError(`Failed to export session: ${error instanceof Error ? error.message : "Unknown error"}`);
			return;
		}

		// Show cancellable loader, replacing the editor
		const loader = new BorderedLoader(this.ui, theme, "Creating gist...");
		this.editorContainer.clear();
		this.editorContainer.addChild(loader);
		this.ui.setFocus(loader);
		this.ui.requestRender();

		const restoreEditor = () => {
			loader.dispose();
			this.editorContainer.clear();
			this.editorContainer.addChild(this.editor);
			this.ui.setFocus(this.editor);
			try {
				fs.unlinkSync(tmpFile);
			} catch {
				// Ignore cleanup errors
			}
		};

		// Create a secret gist asynchronously
		let proc: ReturnType<typeof spawn> | null = null;

		loader.onAbort = () => {
			proc?.kill();
			restoreEditor();
			this.showStatus("Share cancelled");
		};

		try {
			const result = await new Promise<{ stdout: string; stderr: string; code: number | null }>((resolve) => {
				proc = spawn("gh", ["gist", "create", "--public=false", tmpFile]);
				let stdout = "";
				let stderr = "";
				proc.stdout?.on("data", (data) => {
					stdout += data.toString();
				});
				proc.stderr?.on("data", (data) => {
					stderr += data.toString();
				});
				proc.on("close", (code) => resolve({ stdout, stderr, code }));
			});

			if (loader.signal.aborted) return;

			restoreEditor();

			if (result.code !== 0) {
				const errorMsg = result.stderr?.trim() || "Unknown error";
				this.showError(`Failed to create gist: ${errorMsg}`);
				return;
			}

			// Extract gist ID from the URL returned by gh
			// gh returns something like: https://gist.github.com/username/GIST_ID
			const gistUrl = result.stdout?.trim();
			const gistId = gistUrl?.split("/").pop();
			if (!gistId) {
				this.showError("Failed to parse gist ID from gh output");
				return;
			}

			// Create the preview URL
			const previewUrl = getShareViewerUrl(gistId);
			this.showStatus(`Share URL: ${previewUrl}\nGist: ${gistUrl}`);
		} catch (error: unknown) {
			if (!loader.signal.aborted) {
				restoreEditor();
				this.showError(`Failed to create gist: ${error instanceof Error ? error.message : "Unknown error"}`);
			}
		}
	}

	private async handleCopyCommand(): Promise<void> {
		const text = this.session.getLastAssistantText();
		if (!text) {
			this.showError("No agent messages to copy yet.");
			return;
		}

		try {
			await copyToClipboard(text);
			this.showStatus("Copied last agent message to clipboard");
		} catch (error) {
			this.showError(error instanceof Error ? error.message : String(error));
		}
	}

	private handleNameCommand(text: string): void {
		const name = text.replace(/^\/(?:name|title)\s*/, "").trim();
		if (!name) {
			const currentName = this.sessionManager.getSessionName();
			if (currentName) {
				this.chatContainer.addChild(new Spacer(1));
				this.chatContainer.addChild(new Text(theme.fg("dim", `Session name: ${currentName}`), 1, 0));
			} else {
				this.showWarning("Usage: /name <name>");
			}
			this.ui.requestRender();
			return;
		}

		this.session.setSessionName(name);
		const sessionName = this.sessionManager.getSessionName();
		if (sessionName !== name) {
			this.showWarning(`Session name was normalized from ${JSON.stringify(name)} to ${JSON.stringify(sessionName)}`);
		}
		this.chatContainer.addChild(new Spacer(1));
		this.chatContainer.addChild(new Text(theme.fg("dim", `Session name set: ${sessionName ?? name}`), 1, 0));
		this.ui.requestRender();
	}

	/**
	 * lunr: fire-and-forget session auto-naming. After the FIRST assistant response in an
	 * unnamed session, asks the model for a short title derived from the first user message.
	 * Uses the light tier model when model tiers are enabled; otherwise the session model.
	 * Never blocks the UI; failures are swallowed to the debug log. Skipped entirely when
	 * the user has already named the session (via /name, /title, or --name).
	 */
	private maybeAutoNameSession(): void {
		if (this.autoNameTriggered) return;
		if (this.sessionManager.getSessionName()) return;
		const userMessages = this.session.getUserMessagesForForking();
		if (userMessages.length === 0) return; // No real turn yet; try again on the next agent_end.
		if (userMessages.length > 1) {
			// Resumed/older unnamed session — only auto-name fresh sessions.
			this.autoNameTriggered = true;
			return;
		}
		this.autoNameTriggered = true;
		void this.generateSessionTitle(userMessages[0].text);
	}

	private async generateSessionTitle(firstUserText: string): Promise<void> {
		try {
			let model = this.session.model;
			if (this.settingsManager.getModelTiersEnabled()) {
				const tierModel = this.resolveModelReference(this.settingsManager.getTierModel("light"));
				if (tierModel) {
					model = tierModel;
				}
			}
			if (!model) return;
			const prompt =
				"Generate a title of at most 6 words summarizing this request. " +
				"Reply with the title only — no quotes, no trailing punctuation, no prefix.\n\n" +
				`Request:\n${firstUserText.slice(0, 2000)}`;
			const response = await this.session.modelRuntime.complete(model, {
				messages: [{ role: "user", content: [{ type: "text", text: prompt }], timestamp: Date.now() }],
			});
			let title = response.content
				.filter((c): c is { type: "text"; text: string } => c.type === "text")
				.map((c) => c.text)
				.join(" ")
				.replace(/\s+/g, " ")
				.trim();
			title = title.replace(/^["'`]+|["'`.!?]+$/g, "").trim();
			if (!title) return;
			if (this.sessionManager.getSessionName()) return; // User named it while the request was in flight.
			this.session.setSessionName(title);
		} catch (error) {
			appendDebugLog(
				`auto-name: title generation failed: ${error instanceof Error ? error.message : String(error)}`,
			);
		}
	}

	private handleSessionCommand(): void {
		const stats = this.session.getSessionStats();
		const sessionName = this.sessionManager.getSessionName();
		const entries = this.sessionManager.getEntries();
		const cacheWaste = computeCacheWaste(entries, this.session.modelRuntime);

		// Cost/token totals per provider/model actually used (e.g. OpenRouter `auto`
		// resolves to a concrete responseModel), sorted by cost descending.
		const perModelMap = new Map<string, { key: string; cost: number; tokens: number }>();
		for (const entry of entries) {
			if (entry.type !== "message" || entry.message.role !== "assistant") continue;
			const message = entry.message;
			const usage = message.usage;
			const key = `${message.provider}/${message.responseModel ?? message.model}`;
			let bucket = perModelMap.get(key);
			if (!bucket) {
				bucket = { key, cost: 0, tokens: 0 };
				perModelMap.set(key, bucket);
			}
			bucket.cost += usage.cost.total;
			bucket.tokens += usage.input + usage.output + usage.cacheRead + usage.cacheWrite;
		}
		const perModel = Array.from(perModelMap.values()).sort((a, b) => b.cost - a.cost);

		let info = `${theme.bold("Session Info")}\n\n`;
		if (sessionName) {
			info += `${theme.fg("dim", "Name:")} ${sessionName}\n`;
		}
		info += `${theme.fg("dim", "File:")} ${stats.sessionFile ?? "In-memory"}\n`;
		info += `${theme.fg("dim", "ID:")} ${stats.sessionId}\n\n`;
		info += `${theme.bold("Messages")}\n`;
		info += `${theme.fg("dim", "Total:")} ${stats.totalMessages}\n`;
		info += `${theme.fg("dim", "User:")} ${stats.userMessages}\n`;
		info += `${theme.fg("dim", "Assistant:")} ${stats.assistantMessages}\n`;
		info += `${theme.fg("dim", "Tools:")} ${stats.toolCalls} calls, ${stats.toolResults} results\n\n`;
		info += `${theme.bold("Tokens")}\n`;
		// "Input" is the full prompt volume. With cache activity, split it into
		// cached (served from cache) vs uncached (everything else) - the only
		// provider-independent split. Cache writes, where reported, are a detail
		// of the uncached portion.
		const { input, cacheRead, cacheWrite } = stats.tokens;
		const promptTokens = input + cacheRead + cacheWrite;
		info += `${theme.fg("dim", "Input:")} ${promptTokens.toLocaleString()}\n`;
		if (promptTokens > 0 && (cacheRead > 0 || cacheWrite > 0)) {
			const hitRate = theme.fg("dim", `(${((cacheRead / promptTokens) * 100).toFixed(1)}%)`);
			info += `  ${theme.fg("dim", "Cached:")} ${cacheRead.toLocaleString()} ${hitRate}\n`;
			const written =
				cacheWrite > 0 ? ` ${theme.fg("dim", `(${cacheWrite.toLocaleString()} written to cache)`)}` : "";
			info += `  ${theme.fg("dim", "Uncached:")} ${(input + cacheWrite).toLocaleString()}${written}\n`;
		}
		info += `${theme.fg("dim", "Output:")} ${stats.tokens.output.toLocaleString()}\n`;
		info += `${theme.fg("dim", "Total:")} ${stats.tokens.total.toLocaleString()}\n`;

		if (stats.cost > 0 || cacheWaste.missedTokens > 0) {
			info += `\n${theme.bold("Cost")}\n`;
			info += `${theme.fg("dim", "Total:")} $${stats.cost.toFixed(3)}`;
			if (perModel.length > 1) {
				for (const entry of perModel) {
					info += `\n  ${theme.fg("dim", `${entry.key}:`)} $${entry.cost.toFixed(3)} ${theme.fg("dim", `(${formatTokens(entry.tokens)} tokens)`)}`;
				}
			}
			if (cacheWaste.missedTokens > 0) {
				const missLabel = cacheWaste.missCount === 1 ? "1 miss" : `${cacheWaste.missCount} misses`;
				const detail = `${cacheWaste.missedTokens.toLocaleString()} tokens, ${missLabel}`;
				info +=
					cacheWaste.missedCost >= 0.0001
						? `\n${theme.fg("dim", "Cache Re-billed:")} $${cacheWaste.missedCost.toFixed(3)} ${theme.fg("dim", `(${detail})`)}`
						: `\n${theme.fg("dim", "Cache Re-billed:")} ${detail}`;
			}
		}

		this.chatContainer.addChild(new Spacer(1));
		this.chatContainer.addChild(new Text(info, 1, 0));
		this.ui.requestRender();
	}

	private collectUsageBreakdown(): ReturnType<typeof computeContextBreakdown> | undefined {
		const model = this.session.model;
		const contextWindow = model?.contextWindow ?? 0;
		if (!model || contextWindow <= 0) return undefined;
		const tools = this.session
			.getActiveToolNames()
			.map((name) => this.session.getToolDefinition(name))
			.filter((definition) => definition !== undefined);
		return computeContextBreakdown({
			systemPrompt: this.session.systemPrompt,
			tools,
			messages: this.session.messages,
			contextWindow,
		});
	}

	private handleFastCommand(arg: string): void {
		if (this.session.model?.provider !== "openai-codex") {
			this.showStatus("Fast mode is for OpenAI Codex subscriptions.");
			return;
		}

		const normalized = arg.toLowerCase();
		if (normalized === "status") {
			this.showStatus(`Fast mode is ${this.session.openaiFastMode ? "on" : "off"}.`);
			return;
		}
		if (normalized !== "" && normalized !== "on" && normalized !== "off") {
			this.showStatus("Usage: /fast [on|off|status]");
			return;
		}

		const enabled = normalized === "on" || (normalized === "" && !this.session.openaiFastMode);
		this.session.setOpenAIFastMode(enabled);
		this.showStatus(`Fast mode ${enabled ? "enabled" : "disabled"}.`);
		this.ui.requestRender();
	}

	// lunr: /usage — current session only: token totals, live context split,
	// context-window bar, and subscription plan usage.
	private async handleUsageCommand(): Promise<void> {
		const stats = this.session.getSessionStats();
		const sessionTotals =
			stats.tokens.total > 0
				? {
						input: stats.tokens.input,
						output: stats.tokens.output,
						cacheRead: stats.tokens.cacheRead,
						cacheWrite: stats.tokens.cacheWrite,
						total: stats.tokens.total,
					}
				: undefined;

		const context = this.session.getContextUsage();
		const provider = this.session.model?.provider;
		const planResult = await getAllPlanUsageResults(provider, this.session.modelRuntime);
		const breakdown = this.collectUsageBreakdown();

		const lines = renderUsageBox(
			{ sessionTotals, context, plan: planResult.usages, breakdown },
			this.ui.terminal.columns,
		);
		this.chatContainer.addChild(new Spacer(1));
		this.chatContainer.addChild(new Text(lines.join("\n"), 1, 0));
		if (planResult.errors.length > 0) this.showStatus(planResult.errors.join("\n"));
		this.ui.requestRender();
	}

	// lunr: /context — estimated breakdown of what consumes the context window
	// (system prompt incl. project context files, tool definitions, and the live
	// session messages). Uses session.messages, so post-compaction renders match
	// the context the model actually sees.
	private handleContextCommand(): void {
		const model = this.session.model;
		const contextWindow = model?.contextWindow ?? 0;
		if (!model || contextWindow <= 0) {
			this.showStatus("No model selected or context window unknown.");
			return;
		}

		const breakdown = this.collectUsageBreakdown();
		if (!breakdown) {
			this.showStatus("No model selected or context window unknown.");
			return;
		}

		const lines = renderContextBox({ breakdown, model: `${model.provider}/${model.id}` }, this.ui.terminal.columns);
		this.chatContainer.addChild(new Spacer(1));
		this.chatContainer.addChild(new Text(lines.join("\n"), 1, 0));
		this.ui.requestRender();
	}

	// lunr: /plan — shortcut onto the plan permission mode (not a second state machine).
	private async handlePlanCommand(args: string): Promise<void> {
		const sub = args.toLowerCase();
		const inPlan = getPermissionMode() === "plan";

		if (sub === "status") {
			this.showStatus(
				inPlan
					? "Plan mode is active — edit/write and mutating bash are blocked. /plan off to implement."
					: `Permission mode: ${getPermissionMode()}.`,
			);
			return;
		}

		// lunr: `/plan <text>` — any arg other than on/off/status is task text.
		// While not in plan: enter plan and send. While in plan: restore previous
		// mode and send with full tool access ("looks good, go implement it").
		if (sub !== "" && sub !== "on" && sub !== "off") {
			if (inPlan) {
				this.applyPermissionMode(this.restoreTargetAfterPlan());
			} else {
				this.applyPermissionMode("plan");
			}
			await this.sendUserMessageAfterDeferredBuiltins(args);
			return;
		}

		const next = sub === "" ? !inPlan : sub === "on";
		if (next === inPlan) {
			this.showStatus(next ? "Plan mode is already active." : "Plan mode is already off.");
			return;
		}

		if (next) {
			this.applyPermissionMode("plan");
		} else {
			this.applyPermissionMode(this.restoreTargetAfterPlan());
		}
	}

	// lunr: /mode — permission mode selector (manual / yolo / plan / auto). Per-session only.
	private handleModeCommand(args: string): void {
		const sub = args.toLowerCase();

		if (sub === "status") {
			this.showStatus(`Permission mode: ${getPermissionMode()}`);
			return;
		}

		if (sub === "manual" || sub === "yolo" || sub === "plan" || sub === "auto") {
			this.applyPermissionMode(sub);
			return;
		}

		if (sub !== "") {
			this.showStatus("Usage: /mode [manual|yolo|plan|auto] — bare /mode opens the selector.");
			return;
		}

		const options = [
			"Manual — approve every action",
			"YOLO — auto-approve tools, agent may still ask",
			"Plan — read-only; present a plan for approval",
			"Auto — fully autonomous, no questions",
		];
		void this.showExtensionSelector("Permission mode", options).then((choice) => {
			if (!choice) return;
			if (choice.startsWith("Manual")) this.applyPermissionMode("manual");
			else if (choice.startsWith("YOLO")) this.applyPermissionMode("yolo");
			else if (choice.startsWith("Plan")) this.applyPermissionMode("plan");
			else if (choice.startsWith("Auto")) this.applyPermissionMode("auto");
		});
	}

	/** Destination when leaving plan via approve / `/plan off` / `/plan <text>`. */
	private restoreTargetAfterPlan(): PermissionMode {
		return restorePermissionModeAfterPlan(
			this.previousPermissionMode,
			this.settingsManager.getDefaultPermissionMode(),
		);
	}

	/** Apply addendum + auto-rollback for a mode without a status toast. */
	private syncPermissionModeEffects(mode: PermissionMode): void {
		if (mode === "plan") {
			this.session.setSystemPromptAppend(PLAN_MODE_ADDENDUM);
		} else if (mode === "auto") {
			this.session.setSystemPromptAppend(AUTO_MODE_ADDENDUM);
			enableRollbackForSession(this.sessionManager.getSessionId());
		} else {
			this.session.setSystemPromptAppend(undefined);
		}
	}

	// lunr: `/goal` forces session auto without writing settings.
	private enterGoalAuto(): void {
		const next = nextModeForGoal(getPermissionMode(), "enter", this.preGoalPermissionMode);
		this.preGoalPermissionMode = next.saved;
		if (next.mode !== getPermissionMode()) {
			this.applyPermissionMode(next.mode, { silent: true });
		}
	}

	private leaveGoalAuto(): void {
		const next = nextModeForGoal(getPermissionMode(), "leave", this.preGoalPermissionMode);
		this.preGoalPermissionMode = next.saved;
		if (next.mode !== getPermissionMode()) {
			this.applyPermissionMode(next.mode, { silent: true });
		}
	}

	private applyPermissionMode(mode: PermissionMode, opts?: { silent?: boolean }): void {
		const prev = getPermissionMode();
		if (mode === "plan" && prev !== "plan") {
			this.previousPermissionMode = prev;
		}
		setPermissionMode(mode);
		this.ui.requestRender();

		if (mode === "plan") {
			this.session.setSystemPromptAppend(PLAN_MODE_ADDENDUM);
		} else if (mode === "auto") {
			this.session.setSystemPromptAppend(AUTO_MODE_ADDENDUM);
		} else {
			this.session.setSystemPromptAppend(undefined);
		}

		if (mode === "auto" && prev !== "auto") {
			enableRollbackForSession(this.sessionManager.getSessionId());
		} else if (mode !== "auto" && prev === "auto") {
			disableRollbackForSession(this.sessionManager.getSessionId());
		}

		if (opts?.silent) return;

		if (mode === "auto" && prev !== "auto") {
			this.showStatus("Auto mode active — fully autonomous. Rollback enabled for this session.");
		} else if (mode === "plan") {
			this.showStatus("Plan mode active — edit/write and mutating bash are blocked. /plan off to implement.");
		} else {
			this.showStatus(`Permission mode: ${mode}`);
		}
	}

	// lunr: approval dialog for manual permission mode.
	// lunr: adds "Switch to auto" / "Switch to yolo" at the bottom so users can
	// escape manual approval mid-turn without a separate /mode round-trip.
	private async showApprovalDialog(req: {
		toolName: string;
		action: string;
		detail: string;
	}): Promise<"once" | "session" | "reject"> {
		return new Promise((resolve) => {
			this.showSelector((done) => {
				const selector = new ExtensionSelectorComponent(
					`Approve ${req.action}?`,
					["Approve once", "Approve for session", "Reject", "Switch to auto", "Switch to yolo"],
					(option) => {
						done();
						if (option === "Approve once") resolve("once");
						else if (option === "Approve for session") resolve("session");
						else if (option === "Switch to auto") {
							this.applyPermissionMode("auto");
							resolve("once");
						} else if (option === "Switch to yolo") {
							this.applyPermissionMode("yolo");
							resolve("once");
						} else resolve("reject");
					},
					() => {
						done();
						resolve("reject");
					},
					{ message: req.detail.slice(0, 500) },
				);
				return { component: selector, focus: selector };
			});
		});
	}

	// lunr: approval dialog for auto-activated agent swarms (one subagent call
	// launching >2 parallel subagents). "Reject with feedback" collects a one-line
	// reason that becomes the block reason the model sees, so it can adjust the plan.
	private async showSwarmApprovalDialog(req: {
		toolName: string;
		action: string;
		detail: string;
	}): Promise<ApprovalResponse> {
		return new Promise((resolve) => {
			this.showSelector((done) => {
				const selector = new ExtensionSelectorComponent(
					"▶ Approve agent swarm?",
					["Approve once", "Approve for this session", "Reject", "Reject with feedback"],
					(option) => {
						done();
						if (option === "Approve once") resolve("once");
						else if (option === "Approve for this session") resolve("session");
						else if (option === "Reject with feedback") {
							void this.showExtensionInput("Reject agent swarm", "feedback for the agent (optional)").then(
								(feedback) => {
									const trimmed = feedback?.trim();
									resolve(trimmed ? { decision: "reject", feedback: trimmed } : "reject");
								},
							);
						} else resolve("reject");
					},
					() => {
						done();
						resolve("reject");
					},
					{ message: req.detail.slice(0, 500) },
				);
				return { component: selector, focus: selector };
			});
		});
	}

	// lunr: approval dialog for present_plan (plan mode). The "with feedback"
	// options collect a one-line note via showExtensionInput that becomes part of
	// the result text the model sees. Esc/close counts as Decline.
	private showPlanInChat(summary: string): void {
		this.chatContainer.addChild(new Spacer(1));
		this.chatContainer.addChild(
			new PlanMessageComponent(
				summary,
				this.getMarkdownThemeWithSettings(),
				this.outputPad,
				this.settingsManager.getGutterRail(),
			),
		);
		this.ui.requestRender();
	}

	private async showPlanApprovalDialog(summary: string): Promise<ApprovalResponse> {
		this.showPlanInChat(summary);
		return new Promise((resolve) => {
			this.showSelector((done) => {
				const selector = new ExtensionSelectorComponent(
					"▶ Approve plan?",
					["Approve", "Approve with feedback", "Decline", "Decline with feedback"],
					(option) => {
						done();
						if (option === "Approve") resolve({ decision: "approve" });
						else if (option === "Approve with feedback") {
							void this.showExtensionInput("Approve plan", "feedback for the agent (optional)").then(
								(feedback) => {
									const trimmed = feedback?.trim();
									resolve(trimmed ? { decision: "approve", feedback: trimmed } : { decision: "approve" });
								},
							);
						} else if (option === "Decline with feedback") {
							void this.showExtensionInput("Decline plan", "feedback for the agent (optional)").then(
								(feedback) => {
									const trimmed = feedback?.trim();
									resolve(trimmed ? { decision: "reject", feedback: trimmed } : "reject");
								},
							);
						} else resolve("reject");
					},
					() => {
						done();
						resolve("reject");
					},
				);
				return { component: selector, focus: selector };
			});
		});
	}

	// lunr: /processes — view and manage background processes.
	private showProcessesSelector(): void {
		this.showSelector((done) => {
			const component = new ProcessesSelectorComponent(() => {
				done();
			});
			return { component, focus: component };
		});
	}

	// lunr: /rollback — restore last turn's file changes + persistently rewind the conversation.
	private async handleRollbackCommand(): Promise<void> {
		if (this.session.isStreaming) {
			this.showWarning("Wait for the current response to finish before running /rollback.");
			return;
		}
		if (!isRollbackEnabled()) {
			this.showStatus("Rollback is disabled — enable it in /settings → Rollback");
			return;
		}

		// lunr: how many turns the rollback will consume — the newest non-empty turn
		// plus any empty (chat-only) turns on top of it. The conversation must rewind
		// past the same number of user messages or files and messages desync.
		// Defaults to 1 so /rollback still rewinds one exchange when there's nothing to restore.
		const turnsToConsume = Math.max(1, peekRollbackTurnsConsumed(this.sessionManager.getSessionId()));

		// Find the rewind target (the turnsToConsume-th user message from the end of
		// the current branch) BEFORE mutating anything — forking to it is what makes
		// the rewind persistent.
		const branch = this.sessionManager.getBranch();
		let targetUserId: string | undefined;
		let userMessagesSeen = 0;
		for (let i = branch.length - 1; i >= 0; i--) {
			const entry = branch[i];
			if (entry.type === "message" && entry.message.role === "user") {
				userMessagesSeen++;
				targetUserId = entry.id;
				if (userMessagesSeen >= turnsToConsume) break;
			}
		}
		if (!targetUserId) {
			this.showStatus("Nothing to roll back");
			return;
		}

		let result: RollbackResult;
		try {
			// lunr: fork FIRST. If the fork throws or is cancelled by an extension veto,
			// no snapshot has been popped yet, so files and conversation stay consistent
			// and /rollback can simply be retried. Fork teardown skips the rollback wipe
			// (reason === "fork"); the snapshots are then migrated to the forked session id.
			const oldSid = this.sessionManager.getSessionId();
			const forkResult = await this.runtimeHost.fork(targetUserId, { position: "before" });
			if (forkResult.cancelled) {
				this.showStatus("Rollback cancelled");
				return;
			}
			migrateRollbackSession(oldSid, this.sessionManager.getSessionId());
			result = rollbackLastTurn(this.sessionManager.getSessionId());
			// /redo cannot redo across a fork — the branched session has new entry ids.
			this.redoStack.length = 0;
			if (forkResult.selectedText && !this.editor.getText().trim()) {
				this.editor.setText(forkResult.selectedText);
			}
		} catch (error) {
			this.showError(error instanceof Error ? error.message : String(error));
			return;
		}

		const names = (paths: string[]): string => {
			const base = paths
				.slice(0, 3)
				.map((p) => path.basename(p))
				.join(", ");
			return paths.length > 3 ? `${base}, …` : base;
		};
		const parts: string[] = [];
		if (result.restored.length > 0)
			parts.push(`${result.restored.length} file(s) restored (${names(result.restored)})`);
		if (result.deleted.length > 0) parts.push(`${result.deleted.length} file(s) deleted (${names(result.deleted)})`);
		if (parts.length === 0) parts.push("no file changes to restore");
		this.showStatus(`Rollback complete — ${parts.join("; ")}.`);
	}

	/**
	 * Get capitalized display string for an app keybinding action.
	 */
	private getAppKeyDisplay(action: AppKeybinding): string {
		return keyDisplayText(action);
	}

	/**
	 * Get capitalized display string for an editor keybinding action.
	 */
	private getEditorKeyDisplay(action: Keybinding): string {
		return keyDisplayText(action);
	}

	private handleHotkeysCommand(): void {
		// Navigation keybindings
		const cursorUp = this.getEditorKeyDisplay("tui.editor.cursorUp");
		const cursorDown = this.getEditorKeyDisplay("tui.editor.cursorDown");
		const cursorLeft = this.getEditorKeyDisplay("tui.editor.cursorLeft");
		const cursorRight = this.getEditorKeyDisplay("tui.editor.cursorRight");
		const cursorWordLeft = this.getEditorKeyDisplay("tui.editor.cursorWordLeft");
		const cursorWordRight = this.getEditorKeyDisplay("tui.editor.cursorWordRight");
		const cursorLineStart = this.getEditorKeyDisplay("tui.editor.cursorLineStart");
		const cursorLineEnd = this.getEditorKeyDisplay("tui.editor.cursorLineEnd");
		const jumpForward = this.getEditorKeyDisplay("tui.editor.jumpForward");
		const jumpBackward = this.getEditorKeyDisplay("tui.editor.jumpBackward");
		const pageUp = this.getEditorKeyDisplay("tui.editor.pageUp");
		const pageDown = this.getEditorKeyDisplay("tui.editor.pageDown");

		// Editing keybindings
		const submit = this.getEditorKeyDisplay("tui.input.submit");
		const newLine = this.getEditorKeyDisplay("tui.input.newLine");
		const deleteWordBackward = this.getEditorKeyDisplay("tui.editor.deleteWordBackward");
		const deleteWordForward = this.getEditorKeyDisplay("tui.editor.deleteWordForward");
		const deleteToLineStart = this.getEditorKeyDisplay("tui.editor.deleteToLineStart");
		const deleteToLineEnd = this.getEditorKeyDisplay("tui.editor.deleteToLineEnd");
		const yank = this.getEditorKeyDisplay("tui.editor.yank");
		const yankPop = this.getEditorKeyDisplay("tui.editor.yankPop");
		const undo = this.getEditorKeyDisplay("tui.editor.undo");
		const tab = this.getEditorKeyDisplay("tui.input.tab");

		// App keybindings
		const interrupt = this.getAppKeyDisplay("app.interrupt");
		const clear = this.getAppKeyDisplay("app.clear");
		const exit = this.getAppKeyDisplay("app.exit");
		const suspend = this.getAppKeyDisplay("app.suspend");
		const cycleThinkingLevel = this.getAppKeyDisplay("app.thinking.cycle");
		const cyclePermissionMode = this.getAppKeyDisplay("app.mode.cycle");
		const cycleModelForward = this.getAppKeyDisplay("app.model.cycleForward");
		const selectModel = this.getAppKeyDisplay("app.model.select");
		const expandTools = "click";
		const toggleThinking = this.getAppKeyDisplay("app.thinking.toggle");
		const externalEditor = this.getAppKeyDisplay("app.editor.external");
		const cycleModelBackward = this.getAppKeyDisplay("app.model.cycleBackward");
		const copyMessage = this.getAppKeyDisplay("app.message.copy");
		const followUp = this.getAppKeyDisplay("app.message.followUp");
		const dequeue = this.getAppKeyDisplay("app.message.dequeue");
		const pasteImage = this.getAppKeyDisplay("app.clipboard.pasteImage");

		let hotkeys = `
**Navigation**
| Key | Action |
|-----|--------|
| \`${cursorUp}\` / \`${cursorDown}\` / \`${cursorLeft}\` / \`${cursorRight}\` | Move cursor / browse history |
| \`${cursorWordLeft}\` / \`${cursorWordRight}\` | Move by word |
| \`${cursorLineStart}\` | Start of line |
| \`${cursorLineEnd}\` | End of line |
| \`${jumpForward}\` | Jump forward to character |
| \`${jumpBackward}\` | Jump backward to character |
| \`${pageUp}\` / \`${pageDown}\` | Scroll by page |
| Mouse wheel | Scroll session (Ctrl+wheel pages) |

**Editing**
| Key | Action |
|-----|--------|
| \`${submit}\` | Send message |
| \`${newLine}\` | New line${process.platform === "win32" ? " (Ctrl+Enter on Windows Terminal)" : ""} |
| \`${deleteWordBackward}\` | Delete word backwards |
| \`${deleteWordForward}\` | Delete word forwards |
| \`${deleteToLineStart}\` | Delete to start of line |
| \`${deleteToLineEnd}\` | Delete to end of line |
| \`${yank}\` | Paste the most-recently-deleted text |
| \`${yankPop}\` | Cycle through the deleted text after pasting |
| \`${undo}\` | Undo |

**Other**
| Key | Action |
|-----|--------|
| \`${tab}\` | Path completion / accept autocomplete |
| \`${interrupt}\` | Cancel autocomplete / abort streaming |
| \`${clear}\` | Clear editor (first) / exit (second) |
| \`${exit}\` | Exit (when editor is empty) |
| \`${suspend}\` | Suspend to background |
| \`${cyclePermissionMode}\` | Cycle permission mode |
${cycleThinkingLevel ? `| \`${cycleThinkingLevel}\` | Cycle thinking level |\n` : ""}| \`${cycleModelForward}\` / \`${cycleModelBackward}\` | Cycle models |
| \`${selectModel}\` | Open model selector |
| \`${expandTools}\` | Expand or collapse a thinking/tool card |
| \`${toggleThinking}\` | Toggle thinking block visibility |
| \`${externalEditor}\` | Edit message in external editor |
| \`${copyMessage}\` | Copy last assistant message |
| \`${followUp}\` | Queue follow-up message |
| \`${dequeue}\` | Restore queued messages |
| \`${pasteImage}\` | Paste image or text from clipboard |
| \`/\` | Slash commands |
| \`!\` | Run bash command |
| \`!!\` | Run bash command (excluded from context) |
`;

		// Add extension-registered shortcuts
		const extensionRunner = this.session.extensionRunner;
		const shortcuts = extensionRunner.getShortcuts(this.keybindings.getEffectiveConfig());
		if (shortcuts.size > 0) {
			hotkeys += `
**Extensions**
| Key | Action |
|-----|--------|
`;
			for (const [key, shortcut] of shortcuts) {
				const description = shortcut.description ?? shortcut.extensionPath;
				const keyDisplay = formatKeyText(key, { capitalize: true });
				hotkeys += `| \`${keyDisplay}\` | ${description} |\n`;
			}
		}

		this.chatContainer.addChild(new Spacer(1));
		this.chatContainer.addChild(new DynamicBorder());
		this.chatContainer.addChild(new Text(theme.bold(theme.fg("accent", "Keyboard Shortcuts")), 1, 0));
		this.chatContainer.addChild(new Spacer(1));
		this.chatContainer.addChild(new Markdown(hotkeys.trim(), 1, 1, this.getMarkdownThemeWithSettings()));
		this.chatContainer.addChild(new DynamicBorder());
		this.ui.requestRender();
	}

	private async handleClearCommand(): Promise<void> {
		this.clearStatusIndicator();
		this.ui.setChatScroll(0);
		// Deferred factories must be on builtinRoster before /new rebuilds the runtime.
		await this.waitForDeferredBuiltins();
		// lunr: preserve the in-flight model across /new so a /model switch survives.
		const previousModel = this.session.model;
		try {
			const result = await this.runtimeHost.newSession();
			if (result.cancelled) {
				return;
			}
			// lunr: restore the model if /new reverted to the default.
			if (previousModel && this.session.model?.id !== previousModel.id) {
				await this.session.setModel(previousModel);
			}
			this.chatContainer.addChild(new Spacer(1));
			this.chatContainer.addChild(new Text(`${theme.fg("accent", "✓ New session started")}`, 1, 1));
			this.ui.requestRender();
		} catch (error: unknown) {
			await this.handleFatalRuntimeError("Failed to create session", error);
		}
	}

	private handleDebugCommand(): void {
		const width = this.ui.terminal.columns;
		const height = this.ui.terminal.rows;
		const allLines = this.ui.render(width);

		const debugLogPath = getDebugLogPath();
		const debugData = [
			`Debug output at ${new Date().toISOString()}`,
			`Terminal: ${width}x${height}`,
			`Total lines: ${allLines.length}`,
			"",
			"=== All rendered lines with visible widths ===",
			...allLines.map((line, idx) => {
				const vw = visibleWidth(line);
				const escaped = JSON.stringify(line);
				return `[${idx}] (w=${vw}) ${escaped}`;
			}),
			"",
			"=== Agent messages (JSONL) ===",
			...this.session.messages.map((msg) => JSON.stringify(msg)),
			"",
		].join("\n");

		fs.mkdirSync(path.dirname(debugLogPath), { recursive: true });
		fs.writeFileSync(debugLogPath, debugData);

		this.chatContainer.addChild(new Spacer(1));
		this.chatContainer.addChild(
			new Text(`${theme.fg("accent", "✓ Debug log written")}\n${theme.fg("muted", debugLogPath)}`, 1, 1),
		);
		this.ui.requestRender();
	}

	private handleArminSaysHi(): void {
		this.chatContainer.addChild(new Spacer(1));
		this.chatContainer.addChild(new ArminComponent(this.ui));
		this.ui.requestRender();
	}

	private handleDaxnuts(): void {
		this.chatContainer.addChild(new Spacer(1));
		this.chatContainer.addChild(new DaxnutsComponent(this.ui));
		this.ui.requestRender();
	}

	private checkDaxnutsEasterEgg(model: { provider: string; id: string }): void {
		if (model.provider === "opencode" && model.id.toLowerCase().includes("kimi-k2.5")) {
			this.handleDaxnuts();
		}
	}

	private async handleBashCommand(command: string, excludeFromContext = false): Promise<void> {
		const extensionRunner = this.session.extensionRunner;

		// Emit user_bash event to let extensions intercept
		const eventResult = await extensionRunner.emitUserBash({
			type: "user_bash",
			command,
			excludeFromContext,
			cwd: this.sessionManager.getCwd(),
		});

		// If extension returned a full result, use it directly
		if (eventResult?.result) {
			const result = eventResult.result;

			// Create UI component for display
			this.bashComponent = new BashExecutionComponent(command, this.ui, excludeFromContext);
			if (this.session.isStreaming) {
				this.pendingMessagesContainer.addChild(this.bashComponent);
				this.pendingBashComponents.push(this.bashComponent);
			} else {
				this.chatContainer.addChild(this.bashComponent);
			}

			// Show output and complete
			if (result.output) {
				this.bashComponent.appendOutput(result.output);
			}
			this.bashComponent.setComplete(
				result.exitCode,
				result.cancelled,
				result.truncated ? ({ truncated: true, content: result.output } as TruncationResult) : undefined,
				result.fullOutputPath,
			);

			// Record the result in session
			this.session.recordBashResult(command, result, { excludeFromContext });
			this.bashComponent = undefined;
			this.ui.requestRender();
			return;
		}

		// Normal execution path (possibly with custom operations)
		const isDeferred = this.session.isStreaming;
		this.bashComponent = new BashExecutionComponent(command, this.ui, excludeFromContext);

		if (isDeferred) {
			// Show in pending area when agent is streaming
			this.pendingMessagesContainer.addChild(this.bashComponent);
			this.pendingBashComponents.push(this.bashComponent);
		} else {
			// Show in chat immediately when agent is idle
			this.chatContainer.addChild(this.bashComponent);
		}
		this.ui.requestRender();

		try {
			const result = await this.session.executeBash(
				command,
				(chunk) => {
					if (this.bashComponent) {
						this.bashComponent.appendOutput(chunk);
						this.ui.requestRender();
					}
				},
				{ excludeFromContext, operations: eventResult?.operations },
			);

			if (this.bashComponent) {
				this.bashComponent.setComplete(
					result.exitCode,
					result.cancelled,
					result.truncated ? ({ truncated: true, content: result.output } as TruncationResult) : undefined,
					result.fullOutputPath,
				);
			}
		} catch (error) {
			if (this.bashComponent) {
				this.bashComponent.setComplete(undefined, false);
			}
			this.showError(`Bash command failed: ${error instanceof Error ? error.message : "Unknown error"}`);
		}

		this.bashComponent = undefined;
		this.ui.requestRender();
	}

	private async handleCompactCommand(customInstructions?: string): Promise<void> {
		this.clearStatusIndicator();

		try {
			await this.session.compact(customInstructions);
		} catch {
			// Ignore, will be emitted as an event
		}
	}

	stop(): void {
		if (this.settingsManager.getShowTerminalProgress()) {
			this.ui.terminal.setProgress(false);
		}
		this.clearStatusIndicator();
		this.themeController.disableAutoSync();
		this.clearExtensionTerminalInputListeners();
		this.footer.dispose();
		this.footerDataProvider.dispose();
		if (this.unsubscribe) {
			this.unsubscribe();
		}
		if (this.isInitialized) {
			this.ui.stop();
			this.isInitialized = false;
		}
		this.unregisterSignalHandlers();
	}
}
